import { Injectable } from '@angular/core';

import {
  SkyActionButtonDemoComponent,
  SkyAlertDemoComponent,
  SkyAutocompleteDemoComponent,
  SkyAvatarDemoComponent,
  SkyCardDemoComponent,
  SkyCheckboxDemoComponent,
  SkyColorpickerDemoComponent,
  SkyConfirmDemoComponent,
  SkyDatepickerDemoComponent,
  SkyDefinitionListDemoComponent,
  SkyDropdownDemoComponent,
  SkyEmailValidationDemoComponent,
  SkyErrorDemoComponent,
  SkyFileAttachmentDemoComponent,
  SkyFilterDemoComponent,
  SkyFilterInlineDemoComponent,
  SkyFluidGridDemoComponent,
  SkyFlyoutDemoComponent,
  SkyGridDemoComponent,
  SkyHelpInlineDemoComponent,
  SkyKeyInfoDemoComponent,
  SkyLabelDemoComponent,
  SkyLinkRecordsDemoComponent,
  SkyListDemoComponent,
  SkyListFiltersDemoComponent,
  SkyListFiltersInlineDemoComponent,
  SkyListPagingDemoComponent,
  SkyListProviderDemoComponent,
  SkyListToolbarDemoComponent,
  SkyListToolbarCustomDemoComponent,
  SkyListViewChecklistDemoComponent,
  SkyListViewGridDemoComponent,
  SkyLookupDemoComponent,
  SkyMediaQueryDemoComponent,
  SkyModalDemoComponent,
  SkyNavbarDemoComponent,
  SkyNumericDemoComponent,
  SkyPageSummaryDemoComponent,
  SkyPagingDemoComponent,
  SkyPopoverDemoComponent,
  SkyRadioDemoComponent,
  SkyRepeaterDemoComponent,
  SkySearchDemoComponent,
  SkySectionedFormDemoComponent,
  SkySelectFieldDemoComponent,
  SkySortDemoComponent,
  SkyTabsDemoComponent,
  SkyTextExpandDemoComponent,
  SkyTextHighlightDemoComponent,
  SkyTileDemoComponent,
  SkyTimepickerDemoComponent,
  SkyTokensDemoComponent,
  SkyToolbarDemoComponent,
  SkyUrlValidationDemoComponent,
  SkyVerticalTabsDemoComponent,
  SkyWaitDemoComponent,
  SkyWizardDemoComponent
} from './index';

/**
 * This service provides consumers with the raw file contents for each component demo.
 * The `componentName` and `bootstrapSelector` properties allow the consumer to dynamically
 * generate the demo when needed. For example:
 *
 * `import { componentName } from 'file location';
 * `template: '<bootstrap-selector></bootstrap-selector>'`
 *
 * Note: If the componentName is provided but the bootstrapSelector is omitted, it can be assumed that
 * the component is an entry component.
 */


export class SkyDemoService {
  public components: any[] = [
    {
      name: 'Action button',
      component: SkyActionButtonDemoComponent,
      files: [
        {
          name: 'action-button-demo.component.html',
          fileContents: `<sky-action-button-container>
  <sky-action-button
    (actionClick)="filterActionClick()">
    <sky-action-button-icon
      iconType="filter">
    </sky-action-button-icon>
    <sky-action-button-header>
      Build a new list
    </sky-action-button-header>
    <sky-action-button-details>
      Start from scratch and fine-tune with filters
    </sky-action-button-details>
  </sky-action-button>

  <sky-action-button
    (actionClick)="openActionClick()">
    <sky-action-button-icon
      iconType="folder-open-o">
    </sky-action-button-icon>
    <sky-action-button-header>
      Open a saved list
    </sky-action-button-header>
    <sky-action-button-details>
      Open a list with filters saved in the web view
    </sky-action-button-details>
  </sky-action-button>
</sky-action-button-container>
`
        },
        {
          name: 'action-button-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

@Component({
  selector: 'sky-action-button-demo',
  templateUrl: './action-button-demo.component.html'
})
export class SkyActionButtonDemoComponent {
  public filterActionClick() {
    alert('Filter action clicked');
  }

  public openActionClick() {
    alert('Open action clicked');
  }
}
`,
          componentName: 'SkyActionButtonDemoComponent',
          bootstrapSelector: 'sky-action-button-demo'
        }
      ]
    },
    {
      name: 'Alert',
      component: SkyAlertDemoComponent,
      files: [
        {
          name: 'alert-demo.component.html',
          fileContents: `<sky-alert
  [alertType]="alertType"
  [closeable]="closeable"
  [closed]="closed">
  This is a sample alert.
</sky-alert>
<select [(ngModel)]="alertType">
  <option value="info">info</option>
  <option value="success">success</option>
  <option value="warning">warning</option>
  <option value="danger">danger</option>
</select>
<sky-checkbox [(ngModel)]="closeable">
  <sky-checkbox-label>Closeable</sky-checkbox-label>
</sky-checkbox>
`
        },
        {
          name: 'alert-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

@Component({
  selector: 'sky-alert-demo',
  templateUrl: './alert-demo.component.html'
})
export class SkyAlertDemoComponent {
  public closeable = true;
  public closed = false;
  public alertType = 'warning';

  public openAlert() {
    this.closed = false;
  }
}
`,
          componentName: 'SkyAlertDemoComponent',
          bootstrapSelector: 'sky-alert-demo'
        }
      ]
    },
    {
      name: 'Autocomplete',
      component: SkyAutocompleteDemoComponent,
      files: [
        {
          name: 'autocomplete-demo.component.html',
          fileContents: `<h2 class="sky-section-heading">
  Reactive form setup
</h2>

<form
  class="autocomplete-demo-form"
  [formGroup]="reactiveForm"
  (ngSubmit)="submitReactiveForm()"
  novalidate>

  <h3 class="sky-subsection-heading">
    Basic
  </h3>

  <p>
    The following field uses the default settings.
  </p>

  <div class="sky-form-group">
    <label
      for="my-autocomplete-reactive-basic"
      class="sky-control-label">
      What is your favorite color?
    </label>

    <sky-autocomplete
      [data]="colors">
      <input
        skyAutocomplete
        id="my-autocomplete-reactive-basic"
        type="text"
        formControlName="favoriteColor">
    </sky-autocomplete>
  </div>

  <h3 class="sky-subsection-heading">
    Advanced
  </h3>

  <div style="margin-bottom:10px;">
    The following field:<br>
    <ul>
      <li>utilizes a custom search result template,</li>
      <li>searches against the name and description properties,</li>
      <li>limits the search results to two (2),</li>
      <li>runs the search if the query is at least three (3) characters long,</li>
      <li>and fires an event when a selection is made.</li>
    </ul>
  </div>

  <div class="sky-form-group">
    <label
      for="my-autocomplete-reactive-advanced"
      class="sky-control-label">
      Which planet is farthest from the sun?
    </label>

    <sky-autocomplete
      [data]="planets"
      [propertiesToSearch]="['name', 'description']"
      [searchResultsLimit]="2"
      [searchResultTemplate]="planetSearchResultTemplate"
      [searchTextMinimumCharacters]="3"
      (selectionChange)="onPlanetSelection($event)">
      <input
        skyAutocomplete
        id="my-autocomplete-reactive-advanced"
        type="text"
        formControlName="farthestPlanet">
    </sky-autocomplete>
  </div>

  <h3 class="sky-subsection-heading">
    Search filters
  </h3>

  <p>
    The following field provides a custom function that filters the data before every search attempt.
  </p>

  <div class="sky-form-group">
    <label
      for="my-autocomplete-reactive-filters"
      class="sky-control-label">
      Pick a color ("Red" is not available).
    </label>

    <sky-autocomplete
      [data]="colors"
      [searchFilters]="searchFilters">
      <input
        skyAutocomplete
        id="my-autocomplete-reactive-filters"
        type="text"
        formControlName="favoriteColor">
    </sky-autocomplete>
  </div>

  <h3 class="sky-subsection-heading">
    Custom search function
  </h3>

  <p>
    The following field has a preselected value and utilizes a custom search function, as well as a custom template for the search results.
  </p>

  <div class="sky-form-group">
    <label
      for="my-autocomplete-reactive-custom"
      class="sky-control-label">
      Which ocean is the largest?
    </label>

    <sky-autocomplete
      [data]="oceans"
      [search]="getOceanSearchFunction()"
      [searchResultTemplate]="oceanSearchResultTemplate"
      [descriptorProperty]="'title'">
      <input
        skyAutocomplete
        id="my-autocomplete-reactive-custom"
        type="text"
        formControlName="largestOcean">
    </sky-autocomplete>
  </div>

  <div class="autocomplete-demo-alert">
    <strong>Form model:</strong>
    <pre>{{ reactiveForm.value | json }}</pre>
  </div>

  <button
    class="sky-btn sky-btn-default"
    type="submit">
    Submit
  </button>
</form>

<h2 class="sky-section-heading">
  Template-driven form setup
</h2>

<form
  class="autocomplete-demo-form"
  #myForm="ngForm"
  (ngSubmit)="submitTemplateForm(myForm.value)"
  novalidate>

  <p>
    This field uses the default settings and has a preselected value.
  </p>

  <div class="sky-form-group">
    <label
      for="my-autocomplete-template-driven"
      class="sky-control-label">
      What is your favorite color?
    </label>

    <sky-autocomplete
      [data]="colors">
      <input
        skyAutocomplete
        id="my-autocomplete-template-driven"
        type="text"
        name="favoriteColor"
        [(ngModel)]="templateDrivenModel.favoriteColor">
    </sky-autocomplete>
  </div>

  <div class="autocomplete-demo-alert">
    <strong>Form model:</strong>
    <pre>{{ myForm.value | json }}</pre>
  </div>

  <button
    class="sky-btn sky-btn-default"
    type="submit">
    Submit
  </button>
</form>

<!-- Custom search result templates -->

<ng-template
  let-item="item"
  #planetSearchResultTemplate>
  <strong>{{ item.name }}</strong><br>
  {{ item.description }}
</ng-template>

<ng-template
  let-item="item"
  #oceanSearchResultTemplate>
  <i class="fa fa-long-arrow-right" aria-hidden="true"></i>
  {{ item.title }} &bull; ID {{ item.id }}
</ng-template>
`
        },
        {
          name: 'autocomplete-demo.component.scss',
          fileContents: `.autocomplete-demo-form ::ng-deep .sky-form-group {
  background-color: #eeeeef;
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.3);
  border-radius: 3px;
  margin: 10px 0;
  padding: 10px;
}

.autocomplete-demo-form ::ng-deep h3 {
  margin-top: 30px;
}

.autocomplete-demo-alert {
  padding: 10px;
  margin-bottom: 10px;
  border: 1px solid #cdcfd2;
  border-radius: 3px;
}

.autocomplete-demo-alert pre {
  margin: 0;
}
`
        },
        {
          name: 'autocomplete-demo.component.ts',
          fileContents: `import {
  Component,
  OnInit
} from '@angular/core';

import {
  FormBuilder,
  FormGroup
} from '@angular/forms';

import {
  SkyAutocompleteSearchFunction,
  SkyAutocompleteSearchFunctionFilter,
  SkyAutocompleteSearchFunctionResponse,
  SkyAutocompleteSelectionChange
} from '../../core';

@Component({
  selector: 'sky-autocomplete-demo',
  templateUrl: './autocomplete-demo.component.html',
  styleUrls: ['./autocomplete-demo.component.scss']
})
export class SkyAutocompleteDemoComponent implements OnInit {
  public reactiveForm: FormGroup;
  public templateDrivenModel: any = {
    favoriteColor: { name: 'Red' }
  };

  public selectedColor: any;
  public colors: any[] = [
    { name: 'Red' },
    { name: 'Blue' },
    { name: 'Green' },
    { name: 'Orange' },
    { name: 'Pink' },
    { name: 'Purple' },
    { name: 'Yellow' },
    { name: 'Brown' },
    { name: 'Turquoise' },
    { name: 'White' },
    { name: 'Black' }
  ];

  public largestOcean: any;
  public oceans: any[] = [
    { title: 'Arctic', id: 1 },
    { title: 'Atlantic', id: 2 },
    { title: 'Indian', id: 3 },
    { title: 'Pacific', id: 4 }
  ];

  public farthestPlanet: any;
  public planets: any[] = [
    { name: 'Mercury', description: 'Mercury is a planet in our solar system.' },
    { name: 'Venus', description: 'Venus is a planet in our solar system.' },
    { name: 'Earth', description: 'Earth is a planet in our solar system.' },
    { name: 'Mars', description: 'Mars is a planet in our solar system.' },
    { name: 'Jupiter', description: 'Jupiter is a planet in our solar system.' },
    { name: 'Saturn', description: 'Saturn is a planet in our solar system.' },
    { name: 'Uranus', description: 'Uranus is a planet in our solar system.' },
    { name: 'Neptune', description: 'Neptune is a planet in our solar system.' }
  ];

  public searchFilters: SkyAutocompleteSearchFunctionFilter[] = [
    (searchText: string, item: any): boolean => {
      return (item.name !== 'Red');
    }
  ];

  constructor(
    private formBuilder: FormBuilder
  ) { }

  public ngOnInit(): void {
    this.createForm();
  }

  public submitTemplateForm(formData: any): void {
    alert('Form submitted with: \\n' + JSON.stringify(formData));
  }

  public submitReactiveForm(): void {
    alert('Form submitted with: \\n' + JSON.stringify(this.reactiveForm.value));
  }

  public onPlanetSelection(args: SkyAutocompleteSelectionChange): void {
    alert(\`You selected \${args.selectedItem.name}\`);
  }

  public getOceanSearchFunction(): SkyAutocompleteSearchFunction {
    const searchFunction = (searchText: string, oceans: any[]): SkyAutocompleteSearchFunctionResponse => {
      return new Promise((resolve: Function) => {
        const searchTextLower = searchText.toLowerCase();

        const results = oceans.filter((ocean: any) => {
          const val = ocean.title;
          const isMatch = (val && val.toString().toLowerCase().indexOf(searchTextLower) > -1);
          return isMatch;
        });

        // Simulate an async request.
        setTimeout(() => {
          resolve(results);
        }, 500);
      });
    };

    return searchFunction;
  }

  private createForm(): void {
    this.reactiveForm = this.formBuilder.group({
      favoriteColor: undefined,
      farthestPlanet: {},
      largestOcean: { title: 'Arctic', id: 1 }
    });
  }
}
`,
          componentName: 'SkyAutocompleteDemoComponent',
          bootstrapSelector: 'sky-autocomplete-demo'
        }
      ]
    },
    {
      name: 'Avatar',
      component: SkyAvatarDemoComponent,
      files: [
        {
          name: 'avatar-demo.component.html',
          fileContents: `<sky-avatar
  [name]="name"
  [src]="src"
  [canChange]="true"
  (avatarChanged)="updateSrc($event)">
</sky-avatar>
<sky-checkbox
  [(ngModel)]="showImage">
  <sky-checkbox-label>Show image</sky-checkbox-label>
</sky-checkbox>
`
        },
        {
          name: 'avatar-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

import { SkyFileItem } from '../../core';

@Component({
  selector: 'sky-avatar-demo',
  templateUrl: './avatar-demo.component.html'
})
export class SkyAvatarDemoComponent {
  public name = 'Robert C. Hernandez';
  public showImage = true;
  public avatarUrl: string | File = 'https://imgur.com/tBiGElW.png';

  public get src(): string | File {
    return this.showImage ?  this.avatarUrl : undefined;
  }

  public updateSrc(fileItem: SkyFileItem) {
    /*
      This is where you might upload the new avatar,
      but for this demo we'll just update it locally.
    */
    if (fileItem) {
      this.avatarUrl = fileItem.file;
    }
  }
}
`,
          componentName: 'SkyAvatarDemoComponent',
          bootstrapSelector: 'sky-avatar-demo'
        }
      ]
    },
    {
      name: 'Card',
      component: SkyCardDemoComponent,
      files: [
        {
          name: 'card-demo.component.html',
          fileContents: `<sky-card [selectable]="showCheckbox">
  <sky-card-title *ngIf="showTitle">Large card</sky-card-title>
  <sky-card-content *ngIf="showContent">
    This card demonstrates the amount of space that is available for a card that uses
    the default large size. If the content does not require this much space, you can
    set the card size to small. The type of content to display in the body of a card
    varies based on what the card represents and whether it prompts users to action.
  </sky-card-content>
  <sky-card-actions *ngIf="showAction">
    <button class="sky-btn sky-btn-default">Click me</button>
  </sky-card-actions>
</sky-card>
<sky-card size="small" [selectable]="showCheckbox">
  <sky-card-title *ngIf="showTitle">Small card</sky-card-title>
  <sky-card-content *ngIf="showContent">
    This card demonstrates the reduced amount of space that is available when you
    set the card size to small.
  </sky-card-content>
  <sky-card-actions *ngIf="showAction">
    <sky-dropdown buttonType="context-menu">
      <sky-dropdown-menu>
        <sky-dropdown-item>
          <button type="button">
            Action
          </button>
        </sky-dropdown-item>
      </sky-dropdown-menu>
    </sky-dropdown>
  </sky-card-actions>
</sky-card>
<div>
  <ul class="sky-list-unstyled">
    <li>
      <sky-checkbox [(ngModel)]="showTitle">
        <sky-checkbox-label>Show title</sky-checkbox-label>
      </sky-checkbox>
    </li>
    <li>
      <sky-checkbox [(ngModel)]="showContent">
        <sky-checkbox-label>Show content</sky-checkbox-label>
      </sky-checkbox>
    </li>
    <li>
      <sky-checkbox [(ngModel)]="showAction">
        <sky-checkbox-label>Show action</sky-checkbox-label>
      </sky-checkbox>
    </li>
    <li>
      <sky-checkbox [(ngModel)]="showCheckbox">
        <sky-checkbox-label>Show checkbox</sky-checkbox-label>
      </sky-checkbox>
    </li>
  </ul>
</div>
`
        },
        {
          name: 'card-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

@Component({
  selector: 'sky-card-demo',
  templateUrl: './card-demo.component.html'
})
export class SkyCardDemoComponent {
  public showTitle = true;
  public showContent = true;
  public showAction = true;
  public showCheckbox = true;
}
`,
          componentName: 'SkyCardDemoComponent',
          bootstrapSelector: 'sky-card-demo'
        }
      ]
    },
    {
      name: 'Checkbox',
      component: SkyCheckboxDemoComponent,
      files: [
        {
          name: 'checkbox-demo.component.html',
          fileContents: `<fieldset>
  <legend>Checkbox options</legend>
  <ul class="sky-list-unstyled">
    <li *ngFor="let checkboxItem of checkboxItems">
      <sky-checkbox [(ngModel)]="checkboxItem.checked" [disabled]="checkboxItem.disabled">
        <sky-checkbox-label>
          {{ checkboxItem.description }}
        </sky-checkbox-label>
      </sky-checkbox>
    </li>
  </ul>
</fieldset>
<b>Checked state:</b>
<ul class="sky-list-unstyled">
  <li *ngFor="let checkboxItem of checkboxItems">
    {{ checkboxItem.description }}: <b>{{ checkboxItem.checked ? 'true': 'false' }}</b>
  </li>
</ul>
`
        },
        {
          name: 'checkbox-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

@Component({
  selector: 'sky-checkbox-demo',
  templateUrl: './checkbox-demo.component.html'
})
export class SkyCheckboxDemoComponent {
  public checkboxItems = [
    {
      description: 'Checkbox 1'
    },
    {
      description: 'Checkbox 2',
      checked: true
    },
    {
      description: 'Disabled',
      disabled: true
    },
    {
      description: 'Disabled and selected',
      checked: true,
      disabled: true
    }
  ];
}
`,
          componentName: 'SkyCheckboxDemoComponent',
          bootstrapSelector: 'sky-checkbox-demo'
        }
      ]
    },
    {
      name: 'Colorpicker',
      component: SkyColorpickerDemoComponent,
      files: [
        {
          name: 'colorpicker-demo.component.html',
          fileContents: `<h3>
  Colorpicker types and formats
</h3>

<div style="margin-bottom: 10px">
  <sky-colorpicker
    (selectedColorChanged)="onSelectedColorChanged($event)"
    #colorPickerExample1>
    <input
      [skyColorpickerInput]="colorPickerExample1"
      [(ngModel)]="color1"
      [presetColors]="presetColors1"
      [initialColor]="selectedColor1"
      [outputFormat]="selectedOutputFormat1">
  </sky-colorpicker>
</div>

<sky-definition-list>
  <sky-definition-list-content>
    <sky-definition-list-label>
      RGBA
    </sky-definition-list-label>
    <sky-definition-list-value
      *ngIf="color1?.rgba">
      {{color1?.rgba?.red}},
      {{color1?.rgba?.green}},
      {{color1?.rgba?.blue}},
      {{color1?.rgba?.alpha}}
    </sky-definition-list-value>
  </sky-definition-list-content>
  <sky-definition-list-content>
    <sky-definition-list-label>
      HSLA
    </sky-definition-list-label>
    <sky-definition-list-value
      *ngIf="color1?.hsla">
      {{color1?.hsla?.hue}},
      {{color1?.hsla?.saturation}},
      {{color1?.hsla?.lightness}},
      {{color1?.hsla?.alpha}}
    </sky-definition-list-value>
  </sky-definition-list-content>
  <sky-definition-list-content>
    <sky-definition-list-label>
      HSVA
    </sky-definition-list-label>
    <sky-definition-list-value
      *ngIf="color1?.hsva">
      {{color1?.hsva?.hue}},
      {{color1?.hsva?.saturation}},
      {{color1?.hsva?.value}},
      {{color1?.hsva?.alpha}}
    </sky-definition-list-value>
  </sky-definition-list-content>
  <sky-definition-list-content>
    <sky-definition-list-label>
      CMYK
    </sky-definition-list-label>
    <sky-definition-list-value
      *ngIf="color1?.cmyk">
      {{color1?.cmyk?.cyan}},
      {{color1?.cmyk?.magenta}},
      {{color1?.cmyk?.yellow}},
      {{color1?.cmyk?.key}}
    </sky-definition-list-value>
  </sky-definition-list-content>
</sky-definition-list>

<div style="margin-bottom: 10px">
  <sky-colorpicker #colorPickerExample2>
    <input
      type="text"
      [skyColorpickerInput]="colorPickerExample2"
      [(ngModel)]="color2">
  </sky-colorpicker>
</div>

<sky-definition-list>
  <sky-definition-list-content>
    <sky-definition-list-label>
      HEX
    </sky-definition-list-label>
    <sky-definition-list-value *ngIf="color2?.hex">
      {{color2?.hex}}
    </sky-definition-list-value>
  </sky-definition-list-content>
  <sky-definition-list-content>
    <sky-definition-list-label>
      RGBA CSS
    </sky-definition-list-label>
    <sky-definition-list-value *ngIf="color2?.rgbaText">
      <div style="margin-bottom: 5px;">
        {{color2?.rgbaText}}
      </div>
      <div style="border-color: #999;width: 28px;height: 28px;border-radius: 50%;border-width: 2px;border-style: solid;">
        <div [style.background-color]="color2?.rgbaText" style="width:24px;height:24px;    border-radius: 50%;">&nbsp;</div>
      </div>
    </sky-definition-list-value>
  </sky-definition-list-content>
</sky-definition-list>

<h3>
  Interacting with a colorpicker programmatically
</h3>

<sky-colorpicker
  [messageStream]="colorpickerController"
  [showResetButton]="showResetButton"
  (selectedColorChanged)="onSelectedColorChanged($event)"
  #colorPickerExample3>
  <input
    [skyColorpickerInput]="colorPickerExample3"
    [(ngModel)]="color3"
    [outputFormat]="selectedOutputFormat3">
</sky-colorpicker>

<sky-definition-list>
  <sky-definition-list-content>
    <sky-definition-list-label>
      HEX
    </sky-definition-list-label>
    <sky-definition-list-value *ngIf="color3?.hex">
      {{color3?.hex}}
    </sky-definition-list-value>
  </sky-definition-list-content>
  <sky-definition-list-content>
    <sky-definition-list-label>
      RGBA CSS
    </sky-definition-list-label>
    <sky-definition-list-value *ngIf="color3?.rgbaText">
      <div style="margin-bottom: 5px;">
        {{color3?.rgbaText}}
      </div>
      <div style="border-color:#999; width:28px; height:28px; border-radius:50%; border-width:2px; border-style:solid;">
        <div
          [style.background-color]="color3?.rgbaText"
          style="width:24px; height:24px; border-radius:50%;">
        </div>
      </div>
    </sky-definition-list-value>
  </sky-definition-list-content>
</sky-definition-list>

<p>
  <button
    type="button"
    class="sky-btn sky-btn-default"
    (click)="openColorpicker()">
    Open colorpicker
  </button>

  <button
    type="button"
    class="sky-btn sky-btn-default"
    (click)="resetColorpicker()">
    Reset colorpicker
  </button>

  <button
    type="button"
    class="sky-btn sky-btn-default"
    (click)="toggleResetButton()">
    Toggle reset button
  </button>
</p>
`
        },
        {
          name: 'colorpicker-demo.component.ts',
          fileContents: `import {
  Component
} from '@angular/core';

import { Subject } from 'rxjs/Subject';

import {
  SkyColorpickerOutput,
  SkyColorpickerMessage,
  SkyColorpickerMessageType
} from '../../core';

@Component({
  selector: 'sky-colorpicker-demo',
  templateUrl: './colorpicker-demo.component.html'
})
export class SkyColorpickerDemoComponent {
  public color1: any;
  public color2: any;
  public color3: any;
  public selectedColor1 = '#2889e5';

  public selectedOutputFormat1 = 'rgba';
  public selectedOutputFormat3 = 'rgba';
  public presetColors1 = [
    '#333333',
    '#888888',
    '#EFEFEF',
    '#FFF',
    '#BD4040',
    '#617FC2',
    '#60AC68',
    '#3486BA',
    '#E87134',
    '#DA9C9C',
    '#A1B1A7',
    '#68AFEF'
  ];
  public colorpickerController = new Subject<SkyColorpickerMessage>();
  public showResetButton = false;

  public onSelectedColorChanged(args: SkyColorpickerOutput) {
    console.log('You selected this color:', args);
  }

  public openColorpicker() {
    this.sendMessage(SkyColorpickerMessageType.Open);
  }

  public resetColorpicker() {
    this.sendMessage(SkyColorpickerMessageType.Reset);
  }

  public toggleResetButton() {
    this.sendMessage(SkyColorpickerMessageType.ToggleResetButton);
  }

  private sendMessage(type: SkyColorpickerMessageType) {
    const message: SkyColorpickerMessage = { type };
    this.colorpickerController.next(message);
  }
}
`,
          componentName: 'SkyColorpickerDemoComponent',
          bootstrapSelector: 'sky-colorpicker-demo'
        }
      ]
    },
    {
      name: 'Confirm',
      component: SkyConfirmDemoComponent,
      files: [
        {
          name: 'confirm-demo.component.html',
          fileContents: `<div style="margin-bottom: 20px;">
  <button
    type="button"
    class="sky-btn sky-btn-default"
    (click)="openOKConfirm()">
    OK confirm
  </button>

  <button
    type="button"
    class="sky-btn sky-btn-default"
    (click)="openYesCancelConfirm()">
    Yes/cancel confirm
  </button>

  <button
    type="button"
    class="sky-btn sky-btn-default"
    (click)="openYesNoCancelConfirm()">
    Yes/no/cancel confirm
  </button>

  <button
    type="button"
    class="sky-btn sky-btn-default"
    (click)="openCustomConfirm()">
    Custom confirm
  </button>
</div>

<sky-alert *ngIf="selectedAction">
  You selected "{{ selectedAction }}".
</sky-alert>
`
        },
        {
          name: 'confirm-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

import {
  SkyConfirmButtonAction,
  SkyConfirmInstance,
  SkyConfirmService,
  SkyConfirmType
} from '../../core';

@Component({
  selector: 'sky-confirm-demo',
  templateUrl: './confirm-demo.component.html'
})
export class SkyConfirmDemoComponent {
  public selectedAction: SkyConfirmButtonAction;

  constructor(
    private confirmService: SkyConfirmService
  ) { }

  public openOKConfirm() {
    const dialog: SkyConfirmInstance = this.confirmService.open({
      message: 'Do you wish to continue?',
      type: SkyConfirmType.OK
    });

    dialog.closed.subscribe((result: any) => {
      this.selectedAction = result.action;
    });
  }

  public openYesCancelConfirm() {
    const dialog: SkyConfirmInstance = this.confirmService.open({
      message: 'Do you wish to continue?',
      type: SkyConfirmType.YesCancel
    });

    dialog.closed.subscribe((result: any) => {
      this.selectedAction = result.action;
    });
  }

  public openYesNoCancelConfirm() {
    const dialog: SkyConfirmInstance = this.confirmService.open({
      message: 'Do you wish to continue?',
      type: SkyConfirmType.YesNoCancel
    });

    dialog.closed.subscribe((result: any) => {
      this.selectedAction = result.action;
    });
  }

  public openCustomConfirm() {
    const dialog: SkyConfirmInstance = this.confirmService.open({
      message: 'What option are you going to select?',
      type: SkyConfirmType.Custom,
      buttons: [
        { text: '1', action: 'foo', styleType: 'primary' },
        { text: '2', action: 'bar' },
        { text: '3', action: 'baz', autofocus: true }
      ]
    });

    dialog.closed.subscribe((result: any) => {
      this.selectedAction = result.action;
    });
  }
}
`,
          componentName: 'SkyConfirmDemoComponent',
          bootstrapSelector: 'sky-confirm-demo'
        }
      ]
    },
    {
      name: 'Datepicker',
      component: SkyDatepickerDemoComponent,
      files: [
        {
          name: 'datepicker-demo.component.html',
          fileContents: `<div style="max-width: 50%">
  <sky-datepicker #picker>
    <input
      [(ngModel)]="selectedDate"
      [skyDatepickerInput]="picker"
      [minDate]="minDate"
      [maxDate]="maxDate"
      #date="ngModel"/>
  </sky-datepicker>
  <div
    class="sky-error-label"
    *ngIf="date.errors && date.errors.skyDate && (date.dirty || date.touched)">

    <span [hidden]="!date.errors.skyDate.invalid">
      Please enter a valid date.
    </span>

    <span [hidden]="!date.errors.skyDate.minDate">
      Date is prior to minimum date range.
    </span>

    <span [hidden]="!date.errors.skyDate.maxDate">
      Date is after maximum date range.
    </span>
  </div>
</div>

<div>
  Selected date is {{selectedDate}}
</div>
`
        },
        {
          name: 'datepicker-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

@Component({
  selector: 'sky-datepicker-demo',
  templateUrl: './datepicker-demo.component.html'
})
export class SkyDatepickerDemoComponent {
  public minDate: Date;
  public maxDate: Date;
  public selectedDate: Date;
}
`,
          componentName: 'SkyDatepickerDemoComponent',
          bootstrapSelector: 'sky-datepicker-demo'
        }
      ]
    },
    {
      name: 'Definition list',
      component: SkyDefinitionListDemoComponent,
      files: [
        {
          name: 'definition-list-demo.component.html',
          fileContents: `<sky-definition-list>
  <sky-definition-list-heading>
    Personal information
  </sky-definition-list-heading>
  <sky-definition-list-content
    *ngFor="let item of personalInfo">
    <sky-definition-list-label>
      {{item.label}}
    </sky-definition-list-label>
    <sky-definition-list-value>
      {{item.value}}
    </sky-definition-list-value>
  </sky-definition-list-content>
</sky-definition-list>
<sky-definition-list
  labelWidth="150px"
  defaultValue="No information found">
  <sky-definition-list-heading>
    System information
  </sky-definition-list-heading>
  <sky-definition-list-content
    *ngFor="let item of systemInfo">
    <sky-definition-list-label>
      {{item.label}}
    </sky-definition-list-label>
    <sky-definition-list-value>
      {{item.value}}
    </sky-definition-list-value>
  </sky-definition-list-content>
</sky-definition-list>
`
        },
        {
          name: 'definition-list-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

@Component({
  selector: 'sky-definition-list-demo',
  templateUrl: './definition-list-demo.component.html'
})
export class SkyDefinitionListDemoComponent {
  public personalInfo: { label: string, value: string }[] = [
    {
      label: 'Job title',
      value: 'Engineer'
    },
    {
      label: 'Hobby',
      value: 'Volleyball'
    },
    {
      label: 'Experience',
      value: '3 years'
    }
  ];

  public systemInfo: { label: string, value?: string }[] = [
    {
      label: 'Username',
      value: 'user1'
    },
    {
      label: 'Role',
      value: 'Admin'
    },
    {
      label: 'Last log-in time'
    }
  ];
}
`,
          componentName: 'SkyDefinitionListDemoComponent',
          bootstrapSelector: 'sky-definition-list-demo'
        }
      ]
    },
    {
      name: 'Dropdown',
      component: SkyDropdownDemoComponent,
      files: [
        {
          name: 'dropdown-demo.component.html',
          fileContents: `
<h3>
  Button types and styles
</h3>

<sky-row>
  <sky-column screenSmall="6">
    <ul class="sky-list-unstyled">
      <li>
        Default (select) style:
        <br>
        <sky-dropdown>
          <sky-dropdown-button>
            Show dropdown
          </sky-dropdown-button>
          <sky-dropdown-menu>
            <sky-dropdown-item *ngFor="let item of items">
              <button type="button" [attr.disabled]="item.disabled ? '' : null">
                {{ item.name }}
              </button>
            </sky-dropdown-item>
          </sky-dropdown-menu>
        </sky-dropdown>
      </li>
      <li>
        Context menu style:
        <br>
        <sky-dropdown
          buttonType="context-menu">
          <sky-dropdown-menu>
            <sky-dropdown-item *ngFor="let item of items">
              <button type="button" [attr.disabled]="item.disabled ? '' : null">
                {{ item.name }}
              </button>
            </sky-dropdown-item>
          </sky-dropdown-menu>
        </sky-dropdown>
      </li>
      <li>
        Icon style:
        <br>
        <sky-dropdown
          buttonType="folder-open-o">
          <sky-dropdown-menu>
            <sky-dropdown-item *ngFor="let item of items">
              <button type="button" [attr.disabled]="item.disabled ? '' : null">
                {{ item.name }}
              </button>
            </sky-dropdown-item>
          </sky-dropdown-menu>
        </sky-dropdown>
      </li>
    </ul>
  </sky-column>
  <sky-column screenSmall="6">
    <ul class="sky-list-unstyled" style="text-align:right">
      <li>
        Select primary style:
        <br>
        <sky-dropdown
          alignment="right"
          buttonStyle="primary">
          <sky-dropdown-button>
            Show dropdown
          </sky-dropdown-button>
          <sky-dropdown-menu>
            <sky-dropdown-item *ngFor="let item of items">
              <button type="button" [attr.disabled]="item.disabled ? '' : null">
                {{ item.name }}
              </button>
            </sky-dropdown-item>
          </sky-dropdown-menu>
        </sky-dropdown>
      </li>
      <li>
        Context menu with primary style:
        <br>
        <sky-dropdown
          alignment="right"
          buttonType="context-menu"
          buttonStyle="primary">
          <sky-dropdown-menu>
            <sky-dropdown-item *ngFor="let item of items">
              <button type="button" [attr.disabled]="item.disabled ? '' : null">
                {{ item.name }}
              </button>
            </sky-dropdown-item>
          </sky-dropdown-menu>
        </sky-dropdown>
      </li>
      <li>
        Icon with primary style:
        <br>
        <sky-dropdown
          alignment="right"
          buttonType="folder-open-o"
          buttonStyle="primary">
          <sky-dropdown-menu>
            <sky-dropdown-item *ngFor="let item of items">
              <button type="button" [attr.disabled]="item.disabled ? '' : null">
                {{ item.name }}
              </button>
            </sky-dropdown-item>
          </sky-dropdown-menu>
        </sky-dropdown>
      </li>
    </ul>
  </sky-column>
</sky-row>

<h3>
  Interacting with a dropdown programmatically
</h3>

<p>
  <button
    type="button"
    class="sky-btn sky-btn-default"
    (click)="openDropdown()">
    Open dropdown
  </button>

  <button
    type="button"
    class="sky-btn sky-btn-default"
    (click)="closeDropdown()">
    Close dropdown
  </button>

  <button
    type="button"
    class="sky-btn sky-btn-default"
    (click)="focusTriggerButton()">
    Focus trigger button
  </button>

  <button
    type="button"
    class="sky-btn sky-btn-default"
    (click)="focusNextItem()">
    Focus next item
  </button>

  <button
    type="button"
    class="sky-btn sky-btn-default"
    (click)="focusPreviousItem()">
    Focus previous item
  </button>

  <button
    type="button"
    class="sky-btn sky-btn-default"
    (click)="changeItems()">
    Change items
  </button>
</p>
<sky-dropdown
  buttonStyle="primary"
  [dismissOnBlur]="false"
  [messageStream]="dropdownController">
  <sky-dropdown-button>
    Open
  </sky-dropdown-button>
  <sky-dropdown-menu
    [useNativeFocus]="false"
    (menuChanges)="onMenuChanges($event)">
    <sky-dropdown-item *ngFor="let item of items">
      <button type="button" [attr.disabled]="item.disabled ? '' : null">
        {{ item.name }}
      </button>
    </sky-dropdown-item>
  </sky-dropdown-menu>
</sky-dropdown>
<p>
  This menu does not bring the active items to focus; this is useful for custom implementations where the focus should remain on a different control.
</p>
`
        },
        {
          name: 'dropdown-demo.component.ts',
          fileContents: `import {
  ChangeDetectorRef,
  Component
} from '@angular/core';

import { Subject } from 'rxjs/Subject';

import {
  SkyDropdownMessage,
  SkyDropdownMessageType,
  SkyDropdownMenuChange
} from '../../core';

@Component({
  selector: 'sky-dropdown-demo',
  templateUrl: './dropdown-demo.component.html'
})
export class SkyDropdownDemoComponent {
  public dropdownController = new Subject<SkyDropdownMessage>();
  public items: any[] = [
    { name: 'Option 1', disabled: false },
    { name: 'Option 2', disabled: true },
    { name: 'Option 3', disabled: false },
    { name: 'Option 4', disabled: false },
    { name: 'Option 5', disabled: false }
  ];

  constructor(
    private changeDetector: ChangeDetectorRef
  ) { }

  public optionClicked(option: number) {
    alert('You selected option ' + option);
  }

  public openDropdown() {
    this.sendMessage(SkyDropdownMessageType.Open);
  }

  public closeDropdown() {
    this.sendMessage(SkyDropdownMessageType.Close);
  }

  public focusTriggerButton() {
    this.sendMessage(SkyDropdownMessageType.FocusTriggerButton);
  }

  public focusNextItem() {
    this.sendMessage(SkyDropdownMessageType.FocusNextItem);
  }

  public focusPreviousItem() {
    this.sendMessage(SkyDropdownMessageType.FocusPreviousItem);
  }

  public changeItems() {
    this.items.pop();
    this.changeDetector.detectChanges();
  }

  public onMenuChanges(change: SkyDropdownMenuChange) {
    if (change.activeIndex !== undefined) {
      console.log('The menu\\\'s active index changed to:', change.activeIndex);
    }
  }

  private sendMessage(type: SkyDropdownMessageType) {
    const message: SkyDropdownMessage = { type };
    this.dropdownController.next(message);
  }
}
`,
          componentName: 'SkyDropdownDemoComponent',
          bootstrapSelector: 'sky-dropdown-demo'
        }
      ]
    },
    {
      name: 'Email validation',
      component: SkyEmailValidationDemoComponent,
      files: [
        {
          name: 'email-validation-demo.component.html',
          fileContents: `<div style="max-width: 50%">
  <input
    [(ngModel)]="emailValidator"
    class="sky-form-control"
    skyEmailValidation
    #email="ngModel">
  <div
    class="sky-error-label"
    *ngIf="email.errors && email.errors.skyEmail && (email.dirty && email.touched)">
    <span [hidden]="!email.errors.skyEmail.invalid">
      Please enter valid email with format joe@abc.com
    </span>
  </div>
</div>
`
        },
        {
          name: 'email-validation.component.ts',
          fileContents: `import { Component } from '@angular/core';

@Component({
  selector: 'sky-email-validation-demo',
  templateUrl: './email-validation-demo.component.html'
})
export class SkyEmailValidationDemoComponent {
  public emailValidator: string;
}
`,
          componentName: 'SkyEmailValidationDemoComponent',
          bootstrapSelector: 'sky-email-validation-demo'
        }
      ]
    },
    {
      name: 'Error',
      component: SkyErrorDemoComponent,
      files: [
        {
          name: 'error-demo.component.html',
          fileContents: `<h3 class="sky-subsection-heading">
  Embed errors within the page markup
</h3>

<sky-error errorType="broken">
  <sky-error-action>
    <button
      type="button"
      class="sky-btn sky-btn-primary"
      (click)="customAction()">
      Refresh
    </button>
  </sky-error-action>
</sky-error>

<sky-error errorType="notfound">
  <sky-error-action>
    <button
      type="button"
      class="sky-btn sky-btn-primary"
      (click)="customAction()">
      Refresh
    </button>
  </sky-error-action>
</sky-error>

<sky-error errorType="construction">
  <sky-error-action>
    <button
      type="button"
      class="sky-btn sky-btn-primary"
      (click)="customAction()">
      Refresh
    </button>
  </sky-error-action>
</sky-error>

<sky-error errorType="security">
  <sky-error-action>
    <button
      type="button"
      class="sky-btn sky-btn-primary"
      (click)="customAction()">
      Refresh
    </button>
  </sky-error-action>
</sky-error>

<sky-error>
  <sky-error-image>
    <img role="presentation" src='data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAALYAAAC9CAYAAAAeN4fHAAAABHNCSVQICAgIfAhkiAAAAAlwSFlzAAAOxAAADsQBlSsOGwAAABl0RVh0U29mdHdhcmUAd3d3Lmlua3NjYXBlLm9yZ5vuPBoAACAASURBVHic7Z13eFRV+sc/585MJoU0ehGk9w6KiiBIR1GBzJDQBN3VXRv2XV1dI7v2te7qqr9VOkkmAcSCjS6gCKETeu8kJIT0ycw9vz9CSZlkSu6UaD7P4yO597SZ+c6Zc9/znveFWmqppZZaaqmlllp+L6zo1ev6zX36GPw9jt86ir8H8HtDSmVmdjH3+nscv3WEvwcQaPzQq1dTg02JGLwzda/WbS/vdkNrodj3ITl9wV7Uzrx7t1XrPmopoXbGLsVXffqE6lSWSMFwrdteNWiQXlHU5wE9ghb1DMaHapck3uN3O2PHg9K/d+8OhUIcG5Oamm8xmXT19h/6ArgTmD9k+5Yp1e1jedfe3YWOeCQdEbQBgsoVsQHHgTRFL6YPTk3NqG6ftZTwu52x40FVVDEz1CbzVvTonVVv/6GDlIgaoK8WfQzdtWUHyI0IOlFR1AB6oBmST2tFrS2/2xkb4PuuNzTX6+xpQJ1ytyTI5ULIDzPatfvanJxsB1jZs29/Fdlg6LbUL9zpZ2XP3s9IyRtUfL+LJMQM3b7la89fhYskTmgP6iigA0IWg2JhgmW91/v1E79rYQMs79n7WVEiOsdI9thU3YgRuzadWNGt1ygUsUzCp5m2okfdefhb0aP3UeD6cpdXDtm+ZYhnI3eRBNNdwN8Q3FjuTiaxyfW82rcf0ft7AH5HcqzSO5CkGsRfRqRuOgGAothBIuCBenpjjxW9ek0YsnVrmfrxoAzo1utWFGGSUm4bumPrZ1/16ROKTTZ30Ec3TV9LaSymzqh8CvR3eF/KRV7rOwD43QtbIJ90+MMluXvIji1flbmkqKpQr5bthyq2L+/e6w/rdmxd3L9Hn1sEqkkgYoCmAKoi2wOEWWUnqaAAe4WUL6iKUkeRvCSRrVZ1uaHx4N2bzmr6ohJjpqLyERBWSYm9BNue1bTPAON3LewV3XoNBHEjYAU2g+wIoi6AtOo3lC8v7EIt9x2IFEJYBvTonQ6yYZkviCB1+LZtBwBUvYwWUjygREXMGrx6tQ1gc58+Cy/auA9DcWNAG2F/8oCByIufgJxeaRnJBYRyJ2O/uKhJnwHK71rYqk6nCFUdqLsUsWnw0dWFy3v16qyofCehyZC9v2ZWKK8oqiLV8pcF0LBi4yLxyj+Hbt26vPztvqmpxcAnHg/eYtJhLnmoBWD+pAgMWSlIhlVRy4qO8aCeJsk8FCkHADcATSh5gD6HkOuwG95lYsI5j8cWAPyuhT1s2+bVpf8eunVr2g+9et2kqOJzUbLGLoNO2KWULj1vS9WARaNhliVxQnuE+goqd5IU8ykTUmaQENsUYV2GpEcVNVWk/BuScUixBGS0gzJtkaI/OlsPYJRXxu8jftfCdsTwrVtPL2vb9u7y15f37HmDVIWrM+y6YampxzUeGiTGmED9HHnZPCmFZGFcI4RtOdCp6spyD0K8VfHr6qioXFbtsfqZWmE7YPTBg0VX/r1q0CC9zMp5Tkr5d1x/vzZrPqgk83ikXFhmDArfgG0VzkQtASG6uNBLHpIZxKV8Vp2hBgK/ezt2Vazq2bOlKpW5wAA3qxapKDcP2755qyYDSYi5BSFWA6V9SzZd/v8NmvQBqaBMJDZpv0bt+ZXf7Za6M5b36D1BlcpO3Bc1gFFBTVjVpUv5HU33sZgiEWIBZUUNkIs2oi5Gin+icMtvRdRQuxSplMz2bVKi9x8+VWKbVsaBvM7NJjqoeuP/AXHVGogq3gDZstzVdGBwtdotYQco04lL2qJBWwFF7VLEBSSIlT363gD2sZRswLR1ua4Q04ZuS53jUccJ4/silI1U/GXdSbV2LaUEZQOCPUgiL1/LAk4hScUe9BOTF1zyvH3/UytsN1nRtU8bdPKgg1s5QCYQUeqaEZCKKvq6fXBBIkg0/VLBx0NgQ5Jfrh+tyUXIOQjxV8zJuV7sx2vUCtsDVvToswtkaSuDTSpy4NCtW3+uVsMWUwOkGIGU/YBhQIdqtVd9VhCbPNTPY/CI2jW2B0ihfinkNfOZQMwcsnWLZ6K2mDpjF/eAvBOVfiAD6YG++g+/fqJW2J4g+ArJcyV/yPUZ7du8yvZU1+vPmhZMSO4YEA+gMgTh2namD9kCch4Ref/190A8JdDe0BpBPCgDevQ+BYQoQu05eNu2oy5VtJgaoPIM8ABceWgLMAQ5SCxI9UPiFmljh/cDtcL2kJU9+vwPwcrbt6UudFp4YVx9FNtTwCPUrJ/3tUj1WeIWbfT3QNylVtgesrZPnyYDU1PPVFnIYtJhFw8j5D/wrhXDm0hgPgqPY06u4PEYqNQK21skjO8FyscOjmTVVE4gRFxNOSdZK2ytWTVIz/n6LyPFXwCdu9UNio6RjXsysEFnukW1IExvBGB/zhm2ZR0l+cTPnC302xkBK8g4YlMW+2sArlIrbC1JiG0O9oUIbnW3ql7omNF+NE90uINmIXUrLWeXKl+c2sSz2+dzONcvZwFsIKcQm5LovKj/qBW2ViSZR6DKBQjcPvl9fVgDEm9+nJvqtXO5TqG9mGe2z+M/B75ztzstKELK24lLqXB8LlBw+6eyFgckmf+IlAsQ7ls8moXUZfXgeLpFtXCrnl7RMbpJL4w6PSvO7XK32+qiR4g7MHdZQHJaQG65127QVAeJINH8ElK+5El1nVBYOuBZWtdp5PEQnus0lv05Z5h9ZLVL5UN1RkY26cmt9TvSLrwxwbogCuxW9uec5pcLB/j6dCqF9mJXmmqMyn+AGI8H70VqlyJucun0qvqqrnh80okjnz944MePEeI+T9ua0X407/WaVuG6lJKDhw+z78AB8vPzqV+vHj27daNuXcdr73x7EZ2/fZJjeemV9hWmN/K3zuN4sM0w6gZV/sOSXZzPp4eW84/di8ixFTh/EZK7iUv+0nlB31IrbDfJOft9Qzuc3ZOXdfru7cuanbe68OE7QC90nLjrvzQOjipz/ZdNm/hs9myOHi85Mtm8WTMeuO8+2rVtS05ODtHR0URGVDSJf3Twex5OdXyiq2/dNiy86THahTdxeXynC7KY9MsHrD6/21nR3ezt0p34+ArH9/1JrbDdRSK2HLWcaR0S2ehsUT6mnd+zM/eC282MbNKTbwc+X+bavIQE5iUkIGXJidvg4GD+9+GHNGp4LbpDcXExGZmZNGlUdvlSYLdSb8l9FNjLRl3rE92aFYP/TqQh1O0xFqnFTPr5AxaddLbxKM3EpiS73YEXCSRPspqBJeblt45uawTQ2BjK1z1H0y/S/TXygPplz99+9+OPzF248KqoAbp06lRG1AAGg4HNqalYrWUFHKILon/9sl6u9Y3hfHvb8x6JGsCoGJh/02P0jGpZdUEpHvaoAy9SK2x3SDBNQIoXUtIPcbKoxBgQqTeypPsoBtdt5lZTbcMbX/13Tk4On3z+eYUyhYWFDutmX7rE98srxOChZ3TLMn+/0WMyDYwVly2qqrJn3z6+/fFHvvzmG35NTaXI6ji+ZrDOQOItj6MXVRjQBLexcHzHygv4nlphu0pCzAAEcwBRrKr89+S1tWeoTk9C1+GMrOe6yS5IuWaQWrF6Nbm5Fa1maXv3kra37MGbgsJCVq5Zw4rVqyuUjzZceyhsFlKXe1veVqHMtz/+yJQ//IHHnn6adz74gJVr1qDT6cjMzOTQ4cMUF1e0iHQIb8qk653sOenEhKoL+JZac58rJMQ2RdgXUXLUC4BZp/fw1PU9qKsPBiBY0bGg6zAe2ruGpHOOTo6VJbs4/+q/U7dtc1hGSslzf/87EydMoHvXrly4cIGE5GROnDyJ0WisUN4mr0U8m9JyIDpxbd5SVZU33nmHlWvWXL3WtEkTXps5k5Dg4KvX0jMyCAsLIzQkpEzbD7cbyZyja6gUVYwGXq68gG+pFbYzLCYdqn0B0KD05Xy7jc9O7eGZ63tdvaYTgo863oYQgsSzB6psdufFa4Gizp0/X2m5/IIC/jd7doXrRUVFSCkR4trz/6FSW+y3Nehcpvwnn39eRtQAA/r3LyNqgAb165PyxRfE3HNPmet9oltT3xhORlGO44EK+rIwrj4TEwIiM0PtUsQZUr4ADHJ0698ndpJlKypzTScE/+04kAebVR146Yez26/+W693f36pW7duGVED/HzhWliQNqU2fQ4ePsySLyuamh0tOwB2paVx4NChMtcUIegWWeVSS0FnDxhPxlphV0WSuT9SvFjZ7Us2K+8f317hukDwerubeLh55RESdmYfZ11Gyfq5ZQv3ttMBunYuOyP/mnmQAznX3MNDdNdS3nz59ddlrC1XWLF6NZdyys7A586fZ+v27axYtapC+egqNnZKULWKSlVtaoVdGbOmBSPl/3DiT/PxyTROF+VVuC4QvNKmH39t2bvSuv/YXZJU4NZbbnF7eKOHl83Y98aepVf/3bQgiMz87Kt/b97q+IRXdnY2jz71FKvWruXosWOsWruWZ194gfz8/AozNpR4FlaJFD3deAlepXaNXRmhuS8ihVMTVqFq4+3j23i7neOMGH9t2ZtgRU/84V8r3Pvh7HY+PPA9D904nA7t2rHvQNXr8iv0v/lm+vS6trb/6nQqi0ttogw/W5/tr85lf0Q4kY3q00qEEB3ZgIvWArIK8ykuJdDTZ87w6ltvVeijoKDijuqhXKfx6Vu79AJ8QK13nyMSJ/QAZuPi+7MrLxNzwzZEGSpaKgBuimxEpD6IlZmnKtxbcW4Xfeu2xtR/BD+tX+9QUKXp0K4d8c8/j8FQEspvS9YRxq57i0L12np5R3QOuQY7rTP0FGZkUz84jOvCImkbUZ9udZvQPrIBzcIiqBccRh2DEZ2iYLXbUEvFGG7Xti2333bNXJhlzeO5HQudzdphpKS9VuUL8BG1wnZETGcLbsw+qpRk2YoY06BlpWVuiGhIq5AIvs88jlpqvWuXKpYTP9O+QXNm3DmZY8ePc+ZsxZlRr9dz1x138NzTT1+1ZPx4dgdj179FprWsDVwCh+oUsK7BRcKLdTQvMCJKeU8YFB3hBiMNSgm+a93GtI2oT5PQCKKNIXRr156mjZsQFGJE0emYd3QtS09twglBmLu8R3JakbOC3qbWV6Q8JXGoU9ytpgj4qc84utSp/PQLwLKMY9yXtpJC1V7h3uCGXXin172Ephfza2oqZ86exWg00ur667npxhtpUL8+AMfzM3gtbQmfHFqOdCGSe8dLoUw51oTm+Y5/UapEQFh0FDuUDPYGXeSHxplkGWyVl1dojTn5iPsdaUutsEtjMQWhshs3gk6WZmB0U77sMdppufXZZ4jd+QM5Nsfmtl7RrYi57ia6RjanbXhjCuxW8m1FbM48zOr03Sw7vbXMZowr6KRgyPloxp1oSKjdM5tBtsHOEz33Y1Oq+DJJ9QbiFmkf+N5Nah8eS2MXf0JIj0QNsDbrNMsvnGRovaojDvePbMJXPe8gZvt3ZBRX9AfZmnWErVnaTnp2IfmhUSYb615iwolG9M+IcDsA1fyWZ6oWNQAixEkBn1Br7rvCslFGhKx27sO/HfoFmzOzGNCzTn2W9bqT64y+jZ+TbbDxaetTxHc5yqE6rvuSf9M0g411XYgsXHof348ExCACgkth9wPuueg5YF/+RRY42U6/QvvQKL7rfScdQqOcF9aYI2EFzOxyhE/anCLHUPmyRgrJ0mYZJDWvfNu/DHYZEKuA3/ca22IKQor+oN5weYex2tNnkE1y23nBvPFTCTYGOa8AXLRZid35A79ka5ug11XC7Ar3nGzIsHN1Kb3SuGiwMbvVGbZEV+If4ggpBxKX8pP2o3SP35+w4+MVOu4aASIOGANoMl2GFanE/pLF2M2XCLGqNJl0O02nVpVLtCxF0s4DaatZmu58bd0qrCEtQusTHRRGrq2QfLuVw7nnqh1I5/r8YKYebcJ1+UZWNMziq2bpFOjcPfGl3EhsklO7oLf5/QjbYopEldNBPIyHVo/K6L8/j6eWpRNZcO0nXQQZ6Pr5kwQ1cP17o6qSZ/ev539nq05+YFQMDG/cHVPzm7mrWd+rJ2RybAWsO7+HpCWLWR51mlN6zyIj6KXAJlxJCOkAVe3KxEVOD0p6m9/+Bs3Su8IZ2+1ZpEgCcQ9QtaHZTUwbs3n623SCi8vNbHYV+6UCovq7kl6xBCEEw+u3oOc5G8vyzlKsdzzv2KXK/pwzLDn1K+/u+4aNmQexSTsdwpuinrxEyicLeH3YH+nfqRffn91eZkfRFdTqTHfSMJNFuyo6z/iY3+6MPWtaMCF5jwLPAvW90cWYLdk8/n3l7sdCEXT84GFC27n/THpmRSp/37aWlN51kMK1j8moGBiia0PzNCsvPvIUzaIasPZ8GoNWvezSRo4GFLO3S3AgnFj/bc7YiTHjMBR/AZgBz06yOqHLqUJeXHqeKs26EopOplNveB+32w9v3ZSBRNJ+wRb2NDKQFeb8o7JLlQNqBqn1svjwyI9szDxIs9ASv+09lyr6qWiO4AyPfPSO9ztyzm9rxraYuqHyPtrkQKwURcKnn52gVbrjA7DlaTPzXqL6eXbWNW/Pcfa8PJeP+oSwpG8kniT1CFL0WNUqtsG14xdik2/2RUfO+G3YsS2mEBLMr6KSipdFDTB62yWXRQ1w6tNvkDb3tsCvENapBV3f+CNPbC9m5qKzhFrd/5XXWtRK5UM4rWlH1aDmCzshZgAqWxDyOSqmZdYcIWHCRvfMaoUnM8j41nP3ieDrG9Hx/YcYYgvng7mnaJDjk9m3UtTLqoksqKDwgBF2zV1jz58UgbnjhwjxAeUO2nqTPscKGLcp23nBcuTtO0GD0TeiBHm2MacLNVJ3UA8Mvx7ixvWn2dg2lJwQzz4+nQp9j+QzeX0WT36XTsfTheSEKJyNNLi1OC0yCITkWh0pklm0OyBCC9dMYSeZB6PYv6dk2eHT54RpP2XR+rzry5ArqEXFIAQRvTw3oStBBuoO6oE+7RS91hxnTec6FAS5/qPbIMfGpA0Xee6rc9y57RJtz1sJLpa0zChm+K5chuzOxWhTORUdRKGr7ZZ+94XYRsruipF8/EDNenicNS2Y0Nx4pHgGPy2jLP8+Rr1c95YCuuBgom+6iQaDBxLWLwIpXArTWylSlRz/YAmbUrfz+KRm5AZX/Va0O1vEhI0XGbA3D73q3OynCth2fShf94rgpw5h7tq1V6Gqj/p7k6bmCDthfF+EMg/wWyitFheszPr0hMvlwzt1pJkphkYjR6ALLbE6FheeIT97Z/UHIyWnPv+eRds28/fxjR0WaXjJxh9WX+D2tFw83UhMD9ezoks4S/tEcD7C5WVUMUL+l2Lji0xe4IJLoPYEvrAlgiTTY8CbgGteRV5i6K4cnvvKuZdbeMcOtH74IeoNcBwWLDdzI/Zi99fpjjj/xQYeP/4Ly7pfi9FnsEmmrM8i5teLGG1OFC0EwY0aEdysKYregDUrk/xjx1GLyp7ukkKw9foQvu4Vwbr2Ybh0VkFwBin/yoSUeQjf7BBd6zqQKclkOxtwfizFB0xbm8mU9VmV3teHhdH60YdpZjYhlMo/eZs1i7ws7fyEzq3axtCcjZyI0HF9hpXnvzxH23OVPweENGtGw2FDib7xBiJ79URXLpyZarVyYf0Gjn7yKTl791Wonx6hZ1n3CL7tEU66a7P4WhRmYE52HMvNCwSusJPMI5ByDuB5HguNeWHpOQZXknIlvFNHuv7rTUKaubZ9nn9xG8VFLvo4u8Ch3Qd4ffm33PfDWYIczNKKwUDDEcNpZo4hsls3cGGbXqoq+19/g1MWx0dADzc08sf7r54WslO1MUIFFmC1Pc3UJdq98EoICKfwMlhMOqSciZTPEWBfvMh8x5ssjUaOoNPL8Sgu+l8DBIe3p9iaAS6ctnGFNl3a8Z5i5OBPc7DlXAt4aYiMoGnMeK6LnYCxgXtWUaEodHjurxSdzyDDQXTX1Fal4/4pNyPl7Qj5AI5P+CvAFIL0Y0gwv45Ovos52X3zkosE1gbN4rH1UPkGKZ4nwEQNEFZYUYTNzDF0fvWfbokaQNGFEhRS9dlIdwnr1IIObz+AoX4khsgI2j39JLd8/y1tHn3EbVFfRQg6PP8XFAfRXTe1Drvyz2PEJm0izvIGe7u0Q4hhIJIBR8KNQsjXUdlJgukOzwblwrC91bDbJIzvhVAWAa38PZTKmPXpCVpcuPZZNb5jNJ3/OdOln3VHSLWYnAvrkGr1zH/lsV8qok6DfgRFNXRe2EX2vPQyZ5ZeC2xZZBDc83grrHoBUs4hLmVahUoLJkajKzYBjwGV+e8uv7z+TtNssATKjJ0YMwqh/EQAixrK+ilH9elNp5df8ljUAEIxYAzTPiqYLsJIYXEadpsbR7qc0HDY0DJ/b28RUiJqAIHjwNmTFmYRm/wpscldUegLfAqUP0E8FJVtJMW8z/xJFdMveIj/hZ1kngxiKRDmtKyfselKPsigunXp8vqrCA/C/5bHGNoCRa/9S5eqlbzMX7FZ3U/85IjIHt3LWHo2tC3lDayI1U4bMCenEpv8IMbipsCDwI5Sdw1I8Rh66yESTTOwmKq9I+5fYSeankHKufjAeUkLCoNKhN3+ub94vmatgCCkTnuN2iqLlHbyLm6luLD6Odf14eHoL6fhk0Kwvv3Vc8/H3Yr8NPaLi5dn8R6lZvErJ27qA++h8isWk9v56EvjP2EnmJ+gZNMlcNb5TrgQpqPerf0r/CxXF72xAfogrxzyAamSn70Da8HJajdliIoEYEfzYDLrXJ1UV3rc4JVZXKEZJbP4FTt3b1TWkmi2sGDc9Z407R9hJ8Y8jpABcdLCHS5GBNH2yce90nZIeAe89x2XFFxKozC3YsxrdzBElhxMXtux1NJJiKWVFHcdc3L25Vm8V9lZXJrQ6dJIMMcza1qws2ZK43thJ5n/CKLGiRqg9e3DCWvtnRDQij6MoFBtzX/lKco7REHOXvBwd1sXHIwUgnXtrwq7gCLjj1qND7g2i9uCrsziexHyJULy9pMYM9XVZny7DLCYbkPlB/zs8+Epewe/ToeG3ott7i3zX3mCgpsSEtkFdz/+zZOmsib3ME9PbHrl0lfEJt+l9fgqYDH1QcqpSDEF2IaqPs7ERTuqquK7Gdti6oDKEmqoqG+u196rooYr5r82Xu0DwFp4mvyL29ze9Sy+lM133cOvXRDiC42H5hhzcioTUmZQENYU5H9RlLdINM/BYqr0Cd43W+oWUx1UvgSifdKfFxjfvJ9P+jGGNqe44CR2m2fBblyluCidvItbCI3qhagq624pLlnz+anD1QBAKkIu89oAHTF9diGQDCSTOKETdu4jyXyKhucTGby6jJO8b2Zsu/wP4B2blo+4u5mvEmIJgr1k/iuPzZpJflYqUjo/OGEvLOSHpnaKDFeXL0cwJVffjugpsUl7iLO8QWjBYtIb9GDulDKbAd5fYyeYJiJY4PV+vEjXyObsHPm2T/vMy9qCzeqbXKA6QwRhUb0RSuWrxJy0NPp9/xx7mpb2GRHJWI3TmTrP75GfyuPdGXvx2HoI3vdqHz5gdJPKU9p5C++a/8piL75EbtYmVLXy1DF7T+4rJ2oAaSKocDUWk6Zh47TAu2tsq+F1kF7aefAdvaJb+rzPEvNfc6z5x50X1gDVlkde5q+ERfdB0YXyc8Z+Vp7fxdG8dDZk7KsqklRfVFawYOLtTFpY+SkMH+O9KaHEW28z/t6214CdI9+ma2Rzn/frK/NfaS4UWxm741t25KS7W3UtCsO86WPtDt4TnaK86NX2fYRRMdAhvKnzgl5AKAaCfWD+K009QxCLug2nW5167lYdiCpe9caYPME7wrOYOiO5xytt+5j24U0wKP4LvxIU2hyd3rd5ahoEhfB1z9H0i3T3VJ58AovpNuflvI93hC3ldGqQc1NVNAyO9PMIBMHhHXzea6TeyJLuo7gt2q0QyAoq/9bC7bS6aP/wKBEkiTjN2/UTV7IF+IqMohy2XzzK7uyT7L50gt3ZJziUe45/t+/HsLq+XeeH6vQkdhvGpF3LWZnpsndgN6SIA+Z7cWhO0V7YCeM7XHZD/E3gK2GvTd/D8zsW8vOF/WVSUl/hbwc3MqhvMwxVhHXwBiGKnsRuw5m2ewXLMo65VknKJ/CzsLV/l4Tiqy06n+ALYa/L2Mvtq15mfcY+h6IG2J9/kc9O7/H6WBwRJBRmdxnCnVXkii9Hbyymzl4cklO88PUXNd5u7Wv+tfcr7C44JL1+NJV0q+tJR7UkSCjM6TyECY1cDKppF34NcuSFGVtWzKFcg8mxeV9IJ/Jd2zq/aLMy84j/0pTrhOCjjrcxsbELviyKvMX7I6qie81bVNX1VOXJLvkV2K95v14ip9j7wm4Z5nqYhAVn97HpktcDKVWKTgg+7DiAPzZzstKQdPLNiByjvbAnLtqBkO+Vu2oDvkaIkQg5hxrk6Zdj8/4P0NjrbnS5rCrhmf0bsFeyFvcFAsGb7W7mT9d1rbyQ9F0wfkd45xF7QsqToPQBeR9C3IHV1ozY5DEEF24A8bJX+vQSF4q0i81RGTHX3UQrN2btbbkZzDtTMVikLxEIXmvbj4ebd6usgFtnFLXGe05QsUlbgC1lruUHP46oWU5R+3POeL2PYJ2B93pN4+51b7pc5+XDmxjToCX1DP7Tj0DwSpt+1NHpeePo1vI3/RIX+wq+2yFack8UdiURCHFaNoAoVIv5c9vh1NF7V0AdIppyyZbPLxcOuDguO3lqMSPqtfDquFxhQFRTghU9q7PKeAAeJCXtE3+NyXfW/sKgR/H/0TCPctLtzfFB8k/gzR6TuceNkzqzT+9le642kZ6qy+MtuvNS61Jjl9J/5ht8JexPHjCgyD/7pK+q8Sjaelp29YPNuIJe6Ei65QmXxW2Xksf3rfPrg2RpnmjR45q4Bev8ORbfCDsiy4SkiU/6qpxD4NkRtfUZvntQC1L0pPR/ir90P1rfHQAAGIJJREFUutul8ltz0vnfaU0DlVaLJ1r04E/XdSlG0SCQTjXw1VLkAR/1UzmSJxFiq/OCFVl+bgfShylUdELh9e6TSLz5ceoFhTst/4/DmzldFDjHDl9te5MuZ8B0v/oLeV/YFlNjBNUKMKgBK4hL/pJiw05KUka4xbnC7KqORnmNCS1uYdeot53auXPtxfzl4M8+GpVzFIRiF7oZ/h2Dt1GJwb+JUlUU/gJwOTWbR/kHV5zTIIWdBzQOjmJR/6e4t1XV/vtfpR/luwu+OR/pIj392bkvliKjfNBHFYj5mJNTS13w6KFm8clfNRqPe1hVG9M2fsScI45jq5fmmQMbyLf7N8/6FST4b98fbws7Pl4B/OkMU4AiXyxzRXom7DXn0zic69v4MBeL8xi55hXmHnUuaoAThbl+dZIqg+Azf3bvXWF33t0FiHJazlsI+S7m5LK/z7pK0ko4QSKZ46LAtOBk/gX6L3+RVefdWzl9emoXG7LPemlULvNjdKMRvonrVwneFbYq23m1/arJoNj4RoWr5uRTeLjOnnN0TaUHAbTkYnEeA1e+RNol9+3nqoQ/71lDnt13IRvKcVAvdFP81fkVvCtsqXgUjV4j/llpHm8hv/WkwWN56Sw55f219su7UjiS5/kS9VhhDi8f1i7zrxucUFVlSJ1GQ/0X0+8y3hW2ovorYdIxInI/rvy28r2nDb+8K9nrs/bXp1OdF3LC/53aw6pMn5oot6uqcmvdpsMCwjTj5RlbuKYA4dlWd+X98iKjv608EJ2QawGP4vTuzD5OyslfPB2ZS4hqpNi7gkQyY/9PXLT5IjCTsOSr1lsCRdTgdXOfPOq0iJAfoIrPNex0J/u6VL11XhKGa5WnHby8K9mlM4qeclfTvpq0c7wwl4f3rvHmrqkKvBDZaFhs06Zj8p2WLs+yURXT/WqEl5ciYgOV7/QVgbyXCSkzEFI7PxLJc8THu6A6+Z2nXaRdOsm/9n7laXWn/L1LDO3CtXlLvsk4xscnPXpWdkamEMroqMYjXhHCxV/m0iSYJnKpzkUSYv7rhbF5Wdjm5CMgLA7ubAOlH7EpcwEQUhuToGQdccnfuFRWER49QF7hpV0WdmefqE4TlRJhCOGn22fSv742EaBeOvQrm7U9J7kT6BvZaJhnzypJ5sEI5gLBCHGfN8IQe3/nUZH3Ae9TksPvByRTyY6+kdik7VfLSKGNF78i/upy2ZKkmx67xRWpxdy/6WOvLUkaBUey9vaZzLrxIbpFVu8wgVWq3Lt7BWe0cZRaVqwr7B/VeITrSUtLM3dsQ6SczzU3iyDsquZmYe/noDEnFwBOkiOKYE9TtJViKRMs692qIcVihPQ4sMvGCwd4NW0JL3YZ72kTVaIIwbRWg5jWahDbLh5l+dmdrE7fzfJzOyly0059qiiP8Tu+59tedxCp92xpK+GDqEbZTwph9ujABvHxCkG7FwJlw9cqOucujG4SGGF+pVrdlNN2VPVvHlRbXM1+eXl3Mj+erTIzmyb0jGrJn9oO42heutuivkJaXiZxO3+kUHVblzYh5aPRjUfM8FjUAB3S/goMqXDdbtfcryQwhC2E+0/UZRuYxcRF7j8hxS3aSskBBI+xS5XYn9/jaJ7bgdLd5ult86q9rt+QfZbYnd+7bAaUcEZKdVhkk5H/qVbHCeN7IeRLjjsxav6wEhjChuxq1M2l2PZ3j2sLuaQafQOQac1lwoZ3KfJi5oFMay6zjnhsoSzD6qzTDEtdysF8J2+75Ds99IxuMmp1tTpcNsp4Z9MbFumFLggof3hijzdSfASGsIWszlH9t5iy2PMYCUJUezkC8GvmQZ7aNleLphyyK/sEVlU7l9QDBdkMSv2Cd45vI1e1lf9GbhZS3BHVZMSo8MYjqr9MuFTn3WC9odVnN/6JEF0QfeqWSQT7Q7Xbd0BgCBvF05+iU1iDq5enLq3LRkCTvecPD3zvNQ/AekHaZzXItRcfmHlo87hmeSJcSNkPGCJVW8uoxiNuiGwyXJvkpEnm8cCfvzi5iTub9uGpDmPYlHnw2n0hvtakn3IEiLDx7LSsEH+rdo7B+HgVIROq1UYpHk79H7u8YN/uEtmcfvU0s4qdA/kECl2JS14i2o0uimwy8teoxiNWRje9w8Ug2C6QENscKT8FsEk7ey+donNkM7KsVz+yA5gtKzTrrxSBIWy7zhNh/8KezvO06V/O1qQdIM9WxF0/vUGGF0KjJd78OC1CqxVIaw9CPEBBWEtiU97zaoYviykIYbcAVzdfghQ97+4rs3/2AcI7+/2BIeyJCRnAXjdqqCjMcG3r3JX+F+0GNDt6ciTvPOPW/0vTNTFAy7AGbB/5Fo+0G0mY67boS8A7KPQjNrkzEyz/dzknuXdR+Qi46cqfo5r04kzhRTZlXjVCpRNSNMdb3Xt/g8ZVhFyBFB1dLPwxZovWjtHzAW28j4Cf0vfw+NbZfNTnD1o1CUCUIYx/976PV7rFsSY9jV8vHOR4fgYF9pLJN0xnZPX5NI7mnwc4i7G4E2O/uKjpIJyRaJoB3H/lz0hDKP/pfR8zts5GEeKy26/8B3d/6bWIn4GT2SshZghCLHehZDoKHTEnZ1a7T4upBVJORYoJQGdKtj81PVH/YZ/7eajtCC2brJI/bf4/Pjn0Y8kfKqOYmOyxs5cn3Pj9c/eE6o0pq9N3X30fU/o/SZPgaML0wRTarZg2vHP8hP5Y+ypdi6tJ4MzYsSkrSTLtx1nsbMlz1RL13ClhGArHIbgXlcEgvLocm7FlNh3CmzKkUSXhdjXk/f3LrolayjlMTPGpqLGYWv2adfD/5t/0qK6usQ6LT27k4XYjaB5an1uWv4BdqvSv34E3u0/5Pq5lf6+JGgJljQ2UPETI152U+oF9XWa53XZ8vILFdBuJ5s8JKjxz2bNsCD54/TZpx7zhXQ54ORzxwmPreHLb5SWr4CRq0BNe7bA8FlMdVL4C6s8/9hMPtBnK3c1u4G+dxhH783tXncXWZ+xbG3d9/we9PZzAWYrA5RyRppXAIAd3T2G19WbqEtc3DBaO74LQmRByCtDaaXkv0rpOI9bdPpMmIdoHnF12Ziv3rHuT4hIfEBUphxOX4hUzWqUkxowDsQiggTGCHwa9gJCCO9e8xsmiqz+wRahqTyYucsdQ4BGBsxSBkll7gWEcuuJvgX6l7uxC6sYwNdm5qC2mbkhpQopYoJ0GXoOacDj3HCPWvMKa218mOki7o6C/XDiAecM7V0QNiH8Rl+xbUQMoYgUqOUB496jr2Z55lEc2fUauLLo2fQr5mi9EDYE2Y1/BYtIhxXik7AhyD4pYWqnNtSS98c1IeSdS3A24aFnxDwMadOLbgc+7Y66rlJ3Zx7lt5UulNzy2onCTV+3TVdD4iz/8p0dEy4c3ph/AKm3kU2oYknXoGOKrsQWmsJ0xd0oYxqLbkdIEjMGfQXk8oH/9Dnx72/OE6z1P7rDn0iluX/UyZwsvW/JsUiI5i+QEQpwDuQ3ERvILV/HgV9X0nqyEuaYWCHv9zlHNb7Xa7ebzRZe6BOn0QRnFOaHllHUcq+0Gt5aR1aRmCFsiSBjfDaEbjiJHIRlIoC2j3GRAg058OeBZogzuL0u2Zh1hxJpXSC8q5Ttm51HsfIlib4QUzUD0BvpTYpv/CeRcsvKX8piGJrb4eIU2uwaWZAiTx2l+IZUzDYai8AVw5ScpDam7g7jEo5r16wKBK2yLqTF2hoEcjhBDgcb+HpLWdI64jm8GPkfLMNczx60+v5ux6/7FxeJSLjJSfk5cyv0OK3x2VzhG4xiknAZ0R4h/E8R/MCdXx1W4aiymG1GZBhzBFvRJpYGLvEjgCDs+XqHz7n6o4h6QI4FuBNL4vETj4Cjm3fQoQ12wc886soo/bf6/8lv1q8iOHsGDnzp3Bp8zoT0623NIcQeIV7gu/UMGrw6M8Kwa41/hrBqkJ73BTZetGDGUPwv3O0EgmNF+NPFdTUQaQivcV6Xk2e3zeXtfhZAPR1D1N172tXGdheM7YhfvA01QxHQmJVc/9FSA4Xthz5oWTGj+WCRjL8/Mmh/krKlEB4XxaLtRTG81mJZhDZDAnksnmbbxw9LOQ1fIBOVWYpP2eNzhfNMEpHwfeJdDXd/SzKksAPCtsBNNDwD/BP+mI/YbErBL0Amn73wDYwRZ1jxs0uHZ2QwUhmFO3lbtMSXENsdmn4+UWRAyqdr+7d4iPl6h3f5IZGEYIjiPA+2zq/oi+k7YJaL2W0LLgEGlRNwKJQJ3nyOo6hiPDi9XxicPGAjN/Ajog15/J3GJpzVr21PmmlqBOhohBgE9KNk5Lu2gZgXWYbXew/0VvQR9KeyVwGCf9Rfo2GXJDK4I1z1WBN8hmIo52TtH4ufGPIdgOqoYzL3Jvs8mZTHpKJQmBA8Bt1K1Pk+jk0Mq28n0pROUT7ZSaww6UTL/qIDN6bb/KZD3YU4e7TVRA0xNeQ3BRyhyFXNMvktnJxHMG2+mSN2HIAEYQFWilvIEiNuq2p733YxtMTVGsgnJdT7rs6ZgB2SFtbcdyc8IOQdFLLgcUcs3zI15FsEkrNZbHf3Ma9uXqQUwGyFd+zUX8iiqcjtTk6sMsebbh8cF41ujU1KAXj7tNxCRXECQAZf/r6JglwOAjRjkOxQH/+yPjY2rzIv5EGiNUdyJOdnz6E9VMTfGBHyMwLWglC6KGvxh7rOYgrCLxxDyL0C1TqbWILYgxBMImUGhLYPDPTIcPtEvjKuPvXgpcIT8utNd2nTxFqsG6TlZ/xuQ25my6FlN2/5glJHosE+BqW7U2ocqhri69vffBs1XY0LJDzEh5b2UrKlqtO9HFRSA0o3YJNdCqVlMIRSRhFRzONxtil9tywsmRqNatyHlA0xd5HF6kzJYTHUokouA4S7XkezFoB/ijrUmMLasLaZIpBiMyo0IOQxED5DVDVQZKDxLbPJbbtWYNS0Yfe63ILcwZdFTXhqXa8yLGQAkYRO9mZ5cvTx788Y1AWUZbmXtFdspZhj3uffQHBhHw8zJ2dh16xC0APr+hkS9nezo99yuNX12IUZxD4JxzB3vu5PAjpiS8hNSfo6e6mUeSIhtCso63EtFnYqR290VNQSKsBNjxqHYdoGc5O+haIgdhfs9Xiebk7ORYjrwf3wypqIDiS8JVmaC2oH5MXd6VN9iisRm+wb3juelYhTDPT247V9hLx5bj4SYhZfPyjXy61i0piQrcPWci6akrEaIjYQETddoVJ5hTrYilT+D/Ddzp7jnQP7JmFCK5Ne4t/xYjzDeXp1oBP4TdpJ5NFb9doSI89sYvMdBQoscx4J2F6G+jRBOMkL4gKnJa5DiZ0TBMy7XiY9XCDUmULKL6CqrMTKyuqZO3wvbYqpLQsw8pPwG8N3ulu+wgjKRMRodx5q8+BfAwDxTW03aqw5S/BV4hLljG7pUvs2uF4G73OhhNUYxBnOyRzk4S+NbYVtMnVHZjBCTfdqvLxHyeWKTNE7IKtcj3Zr1vMPU5OMgExDK007LzjMNA150o/XvMIrRWogafCnsBROjUfkOaOXlnvwYb0EkY055R/tmxV5Qvf2+uYh8FSnuL7FyVMKCcdeDTMLVcHGSpRjF3Vq6DfhO2IptItDciz3kAEfR2jYvXE6cvp6C0KleCYsrRDFCBMYG1pTFZxDMwVbseN0fH6+gKrMBVyMDpVBQ16R1WAbfCVuo3ppJ05DiGSTrgZaatizYh3Qpsfl27IYxXgvPK6VEysAwzQIo6vsgpju0kLTZ/RSOI3lVRMokrsuI84brgA9nbDEX2O60nCsIziDlxwhxKwrdQb0BwUhN2r7G+0iXHm43EmQb4o0EQVcRsiMKB7zWvrtMWnwM+AlRUPZZab6pG8h/uNjKHIKVSd46TOw7YZuTcwkpGoAUrwHu2icLkKwBZiJlf/Z0uY64lD9jtmzALv+LEGYNR1qEZDpSHAOcJX75GoWhjFtyQcP+KyJFD4SizaSgHR8AjyEvL/3i4xWQn3AtnkhV/I9DXe/zmtcg/vIVsZiCsHErOtkXKdoDDYErYZEKQJxDqGdQxV50pAFpDtdgieZ/gdTSl+IcUo4jLmUDiaa1lDhnOcKGFDPZ1/kVrzspzRlbD0V3EKNo6lOfbFeYZ9qJYn+YSYvXMs/0wGVhO+MTDnV9yNvvm38eSEpEuvLyf56RaPqzxqLegdTdXSpiUWUHjlNReBCzxTchC4QSC3wdcKIGkHIeqjKROWN3g3zFhRofMTnlEUSK1y1XgfGk7S4JpruAf2vWnuAbioMmltntEnIWUrxOya+aHViLEB9itiz2VkIgx2MTUxB4nqDVmxj087HZtiN0Bpz71r/BlJS/MsUXAwsUt1V3SDL3R8ofubZ0qQ4SeJUJyS86FOv8cdcRpKuPNeiwX06zzB/XH6nMwyjaeXM9Wi3mmX4B2ZeqbdYlovYhNWvGtpjaosolaCPqDJDTiU35mthKSkxefBI4qUFfniF1byB4IWBFDSAwIqsQtRAvMTl5pg9HBPjbu88dLKbGqPyANsF2llNs705sileywmrC3JhxQB0Odkn091AqZV7MIKSsymvvGX+IGmrKjB0fr2DfnYio9nZ8JkI8g9kyy6frZHexmOpQpL6JKv8U4GHH4iu5LhE8yeQU9w9ZaETNmLE7pU1BcFs1WrCDmIWq78wEy+cBLWqAIvU9pFjJvYtcSQ/oH+bF3AIOPxMJ4jF/ihpqyoyNNHlaEfgSVf2bpiHBvMm88fcAgyi2BnqICkf+5ipC/pHJKZ/7fDTlqBnClrjrg1EILEDhHczJaS7VmDslzO8BGeeYmoH8L1I6jEcXMMw3dUbKYeWu2hHyfiYv8loaaXeoGUsRVX0JgbNEiQXADyDvQ6Exscl/cFnUAKLgBmaZ/Jc1Ye6UMBS5FMm7TF200W/jcAWpPkJZU7EdIacFiqihJtmxr2TUVWQ/VBoghETIDKQ4gJTbKazzi8vedXOnhKEv7EdO9E88+GmxlLK+boH5P2oQU/2ScSs+XqHN7kUgs5iScl+l5WZNC0aX1wQddZF2A3ZRB0XRI7GiIxuVbIrJ9uRUt8t8dlc4QUEngYjLV6xI4piasthrfXpAzRG2BjRZ+uDAMwVZU0BOoCTg/CoU7jI3uvkNy/H1gimLHvLLwObFvAOiD0aGkdbFRpvdrRH0RFW7IZQuIK+nxJfd1QPPWcAuBLuRbEORK5m0SBvvwHmmR0Be2fW1gpzAlEVfaNK2hvz2hT13bEOMuilIcd8/uk3o/O6+b8i0Xjt91Duq1YFD2WejsvMKunh1pqt0fDFPochHQUkA+iLlTTj3KvSEA8ACpJhVcsTLQ+aZdoLsChQhpYmpiyrkDwkEfpvCXnJPFEX6u0DEACMBA8CdTfvwfq9pzNg6m69Pp3Jnk978dCaNXFG0zD7BcofPxrdgfDtUZTSoDyJEx6uun76hGCETEcQzadFht2rOH3cTUvkZyENwN5N9nNbaDX47wl4Y16h5nYjxJ/Iz7wCGAkGOig1u2IUXOo8nRBfEmJWvky3zsaGCELczwbLKe+Mb3xG7mFCyDBKdvNaP6xQi5ZsU1PunyydY5sW8AsxAiruYmuy5Z6YPqBnmvsqwmDoj5ThUMQZh6/tKt4nKHzZ9XD5dHAChOiPmFjczo/1oNl84xFOpc7mkFlz7aks5CtBW2J/dFU6QcTLIP2K/Ejo5YOaSYIT4OyGZI5lrMrm4PBmAKoZxb/LPXh9dNal5wk6c0AZhj0OKCah0hWvB0g/knmHjsFd5fMts1qSn0SQkmuGNujOl5UB6Rrfk54z9/HnTp2zMOIhUKKcx+YtmY5xv6oZU/wxiMsjAzoomuBHkBuabRjE5eWel5eZPioCCp7h3sbahJbxEwEwfLpFoeg14GgdfyGCdge6R1zO91SCGNOoGQpBemM2RvHTqBoUxfv3bFNgdWvKswExik11xlK+a+TF3lgSVkf2r3ZbvSUfV3cq9Sfv9PRAtqDnClgiSTBe5Zj+9ikDQLaoFhXYrNqmSXniJHFvJgZPH2o/iupB6PLt9fvlqecBiUP5JbDU/zLnj+yHEGzj2nahJHMZm68P0Ly76eyDVpeYsRQSSBCYimA9Elb4lkey4eMxhta1ZR+kTfTXI53EQq5FyOaFFX3C3BtvWn4wJRRF1sMt/IngNRNkvniIUJJFlK0lR/jVcxkEsDungmohClJuUJJEIUXYnWZURiPK+0jIcKsQoqUOJ5Sganf5N4AEHY6tR1JwZ+woWU13s4l6QdyPojePMvhI4imAnqtgx/+ZHoyf/+sHbmJ3nLqnlt0HNE3Zp4uMV2u1ojkEXRsmpGiuQDqQH9KmTWmqppZZaaqmlllpqqaWWWmqppZZaaqmlllpc5f8BMDC0ZEVbYc0AAAAASUVORK5CYII='>
  </sky-error-image>
  <sky-error-title>
    Custom title
  </sky-error-title>
  <sky-error-description>
    Custom description
  </sky-error-description>
  <sky-error-action>
    <button
      type="button"
      class="sky-btn sky-btn-primary"
      (click)="customAction()">
      Custom action
    </button>
  </sky-error-action>
</sky-error>

<h3 class="sky-subsection-heading">
  Display an error message in a modal
</h3>

<button
  class="sky-btn sky-btn-default"
  type="open"
  (click)="openErrorModal()">
  Click to open error modal
</button>
`
        },
        {
          name: 'error-demo.component.scss',
          fileContents: `::ng-deep .sky-error {
  margin: 40px 0;
}
`
        },
        {
          name: 'error-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

import {
  ErrorModalConfig,
  SkyErrorModalService
} from '../../core';

@Component({
  selector: 'sky-error-demo',
  templateUrl: './error-demo.component.html',
  styleUrls: ['./error-demo.component.scss'],
  providers: [SkyErrorModalService]
})
export class SkyErrorDemoComponent {
  constructor(
    private errorService: SkyErrorModalService
  ) { }

  public customAction() {
    alert('action clicked!');
  }

  public openErrorModal() {
    const config: ErrorModalConfig = {
      errorTitle: 'Something bad happened!',
      errorDescription: 'Try to refresh this page, or come back later.',
      errorCloseText: 'OK'
    };

    this.errorService.open(config);
  }
}
`,
          componentName: 'SkyErrorDemoComponent',
          bootstrapSelector: 'sky-error-demo'
        }
      ]
    },
    {
      name: 'File attachment',
      component: SkyFileAttachmentDemoComponent,
      files: [
        {
          name: 'file-attachment-demo.component.html',
          fileContents: `<div style="width: 60%; margin-left: auto; margin-right: auto;">
  <sky-file-drop
    (filesChanged)="filesUpdated($event)"
    (linkChanged)="linkAdded($event)"
    [maxFileSize]="maxFileSize"
    [validateFn]="validateFile"
    [allowLinks]="true"
    [acceptedTypes]="acceptedTypes">
  </sky-file-drop>

  <sky-alert
    [alertType]="'danger'"
    [closeable]="true"
    *ngIf="rejectedFiles.length > 0">
    The file <strong>{{rejectedFiles[rejectedFiles.length - 1].file.name}}</strong> is invalid.
    Reason: {{rejectedFiles[rejectedFiles.length - 1].errorType}} violation.
    Error param: {{rejectedFiles[rejectedFiles.length - 1].errorParam}}
  </sky-alert>

  <div *ngFor="let file of allItems">
    <sky-file-item
      [fileItem]="file"
      (deleteFile)="deleteFile($event)">
    </sky-file-item>
  </div>
</div>
`
        },
        {
          name: 'file-attachment-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

import {
  SkyFileItem,
  SkyFileDropChange,
  SkyFileLink
} from '../../core';

@Component({
  selector: 'sky-file-attachment-demo',
  templateUrl: './file-attachment-demo.component.html'
})
export class SkyFileAttachmentDemoComponent {
  public filesToUpload: SkyFileItem[];
  public allItems: (SkyFileItem | SkyFileLink)[];
  public linksToUpload: SkyFileLink[];
  public rejectedFiles: SkyFileItem[];
  public maxFileSize: number = 4000000;
  public acceptedTypes: string = 'image/png,image/jpeg';

  constructor() {
    this.filesToUpload = [];
    this.rejectedFiles = [];
    this.allItems = [];
    this.linksToUpload = [];
  }

  public filesUpdated(result: SkyFileDropChange) {
    this.filesToUpload = this.filesToUpload.concat(result.files);
    this.rejectedFiles = this.rejectedFiles.concat(result.rejectedFiles);
    this.allItems = this.allItems.concat(result.files);
  }

  public linkAdded(result: SkyFileLink) {
    this.linksToUpload = this.linksToUpload.concat(result);
    this.allItems = this.allItems.concat(result);
  }

  public validateFile(file: SkyFileItem) {
    if (file.file.name.indexOf('a') === 0) {
      return 'You may not upload a file that begins with the letter "a."';
    }
  }

  public deleteFile(file: SkyFileItem | SkyFileLink) {
    this.removeFromArray(this.allItems, file);
    this.removeFromArray(this.filesToUpload, file);
    this.removeFromArray(this.linksToUpload, file);
  }

  private removeFromArray(items: any[], obj: SkyFileItem | SkyFileLink) {
    if (items) {
      const index = items.indexOf(obj);

      if (index !== -1) {
        items.splice(index, 1);
      }
    }
  }
}
`,
          componentName: 'SkyFileAttachmentDemoComponent',
          bootstrapSelector: 'sky-file-attachment-demo'
        }
      ]
    },
    {
      name: 'Filter',
      component: SkyFilterDemoComponent,
      files: [
        {
          name: 'filter-demo.component.html',
          fileContents: `<div>
  <sky-toolbar>
    <sky-toolbar-section>
      <sky-toolbar-item>
        <sky-filter-button
          (filterButtonClick)="filterButtonClicked()">
        </sky-filter-button>
      </sky-toolbar-item>
    </sky-toolbar-section>
    <sky-toolbar-section *ngIf="appliedFilters && appliedFilters.length > 0">
      <sky-filter-summary>
        <sky-filter-summary-item
          *ngFor="let item of appliedFilters; let i = index"
          (dismiss)="onDismiss(i)"
          (itemClick)="filterButtonClicked()">
          {{item.label}}
        </sky-filter-summary-item>
      </sky-filter-summary>
    </sky-toolbar-section>
  </sky-toolbar>
  <sky-repeater expandMode="none">
    <sky-repeater-item *ngFor="let item of filteredItems">
      <sky-repeater-item-title>
        {{item.name}}
      </sky-repeater-item-title>
      <sky-repeater-item-content>
        <div>
          {{item.description}}
        </div>
      </sky-repeater-item-content>
    </sky-repeater-item>
  </sky-repeater>

</div>
`
        },
        {
          name: 'filter-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

import {
  SkyModalService,
  SkyModalCloseArgs
} from '../../core';

import { SkyFilterDemoModalComponent } from './filter-demo-modal.component';
import { SkyFilterDemoModalContext } from './filter-demo-modal-context';

@Component({
  selector: 'sky-filter-demo',
  templateUrl: './filter-demo.component.html'
})
export class SkyFilterDemoComponent {
  public appliedFilters: any[] = [];
  public items: any[] = [
    {
      name: 'Orange',
      description: 'A round, orange fruit.',
      type: 'citrus',
      color: 'orange'
    },
    {
      name: 'Mango',
      description: 'Delicious in smoothies, but don\\\'t eat the skin.',
      type: 'other',
      color: 'orange'
    },
    {
      name: 'Lime',
      description: 'A sour, green fruit used in many drinks.',
      type: 'citrus',
      color: 'green'
    },
    {
      name: 'Strawberry',
      description: 'A red fruit that goes well with shortcake.',
      type: 'berry',
      color: 'red'
    },
    {
      name: 'Blueberry',
      description: 'A small, blue fruit often found in muffins.',
      type: 'berry',
      color: 'blue'
    }

  ];

  public filteredItems: any[];

  constructor(private modal: SkyModalService) {
    this.filteredItems = this.items.slice();
  }

  public filterButtonClicked() {
    let modalInstance = this.modal.open(
      SkyFilterDemoModalComponent,
      [{
        provide: SkyFilterDemoModalContext,
        useValue: {
          appliedFilters: this.appliedFilters
        }
      }]
    );

    modalInstance.closed.subscribe((result: SkyModalCloseArgs) => {
      if (result.reason === 'save') {
        this.appliedFilters = result.data.slice();
        this.filteredItems = this.filterItems(this.items, this.appliedFilters);
      }
    });
  }

  public onDismiss(index: number) {
    this.appliedFilters.splice(index, 1);
    this.filteredItems = this.filterItems(this.items, this.appliedFilters);
  }

  private orangeFilterFailed(filter: any, item: any) {
    return filter.name === 'hideOrange' && filter.value && item.color === 'orange';
  }

  private fruitTypeFilterFailed(filter: any, item: any) {
    return filter.name === 'fruitType' && filter.value !== 'any' && filter.value !== item.type;
  }

  private itemIsShown(filters: any[], item: any[]) {
    let passesFilter = true,
        j: number;

    for (j = 0; j < filters.length; j++) {
      if (this.orangeFilterFailed(filters[j], item)) {
        passesFilter = false;
      } else if (this.fruitTypeFilterFailed(filters[j], item)) {
        passesFilter = false;
      }
    }

    return passesFilter;
  }

  private filterItems(items: any[], filters: any[]) {
    let i: number,
      passesFilter: boolean,
      result: any[] = [];

    for (i = 0; i < items.length; i++) {
      passesFilter = this.itemIsShown(filters, items[i]);
      if (passesFilter) {
        result.push(items[i]);
      }
    }

    return result;
  }
}
`,
          componentName: 'SkyFilterDemoComponent',
          bootstrapSelector: 'sky-filter-demo'
        },
        {
          name: 'filter-demo-modal.component.html',
          fileContents: `<sky-modal>
  <sky-modal-header>
    Food preferences
  </sky-modal-header>
  <sky-modal-content>
    <label for="sky-demo-select-type">Fruit type</label>
    <select id="sky-demo-select-type" [(ngModel)]="fruitType" class="sky-form-control">
      <option value="any">Any fruit</option>
      <option value="citrus">Citrus</option>
      <option value="berry">Berry</option>
    </select>
    <div style="margin-top: 15px;">
      <sky-checkbox [(ngModel)]="hideOrange">
        <sky-checkbox-label>
          Hide orange fruits
        </sky-checkbox-label>
      </sky-checkbox>
    </div>
  </sky-modal-content>
  <sky-modal-footer>
    <button
      type="button"
      class="sky-btn sky-btn-primary"
      (click)="applyFilters()">
      Apply filters
    </button>
    <button
      type="button"
      class="sky-btn sky-btn-link"
      (click)="clearAllFilters()">
      Clear all filters
    </button>
    <button
      type="button"
      class="sky-btn sky-btn-link"
      (click)="cancel()">
      Cancel
    </button>
  </sky-modal-footer>
</sky-modal>
`
        },
        {
          name: 'filter-demo-modal.component.ts',
          fileContents: `import { Component } from '@angular/core';

import { SkyModalInstance } from '../../core';

import { SkyFilterDemoModalContext } from './filter-demo-modal-context';

@Component({
  selector: 'sky-demo-filter-modal-form',
  templateUrl: './filter-demo-modal.component.html'
})
export class SkyFilterDemoModalComponent {
  public fruitType = 'any';
  public hideOrange: boolean;

  constructor(
    public context: SkyFilterDemoModalContext,
    public instance: SkyModalInstance
  ) {
    if (
      this.context &&
      this.context.appliedFilters &&
      this.context.appliedFilters.length > 0
    ) {
      this.setFormFilters(this.context.appliedFilters);
    } else {
      this.clearAllFilters();
    }
  }

  public applyFilters() {
    let result = this.getAppliedFiltersArray();
    this.instance.save(result);
  }

  public clearAllFilters() {
    this.hideOrange = false;
    this.fruitType = 'any';
  }

  public cancel() {
    this.instance.cancel();
  }

  private getAppliedFiltersArray() {
    let appliedFilters: any[] = [];
    if (this.fruitType !== 'any') {
      appliedFilters.push({
        name: 'fruitType',
        value: this.fruitType,
        label: this.fruitType
      });
    }

    if (this.hideOrange) {
      appliedFilters.push({
        name: 'hideOrange',
        value: true,
        label: 'hide orange fruits'
      });
    }

    return appliedFilters;
  }

  private setFormFilters(appliedFilters: any[]) {
    for (let i = 0; i < appliedFilters.length; i++) {
      if (appliedFilters[i].name === 'fruitType') {
        this.fruitType = appliedFilters[i].value;
      }

      if (appliedFilters[i].name === 'hideOrange') {
        this.hideOrange = appliedFilters[i].value;
      }
    }
  }
}
`,
          componentName: 'SkyFilterDemoModalComponent'
        },
        {
          name: 'filter-demo-modal-context.ts',
          fileContents: `export class SkyFilterDemoModalContext {
  public appliedFilters: any[];
}
`
        }
      ]
    },
    {
      name: 'Filter inline',
      component: SkyFilterInlineDemoComponent,
      files: [
        {
          name: 'filter-inline-demo.component.html',
          fileContents: `<div>
  <sky-toolbar>
    <sky-toolbar-section>
      <sky-toolbar-item>
        <sky-filter-button
          [active]="filtersActive"
          (filterButtonClick)="filterButtonClicked()">
        </sky-filter-button>
      </sky-toolbar-item>
    </sky-toolbar-section>
  </sky-toolbar>
  <sky-filter-inline [hidden]="!showInlineFilters">
    <sky-filter-inline-item>
      <label for="sky-demo-select-type">Fruit type</label>
      <select
        id="sky-demo-select-type"
        [ngModel]="fruitType"
        (ngModelChange)="fruitTypeChange($event)">
        <option value="any">Any fruit</option>
        <option value="citrus">Citrus</option>
        <option value="berry">Berry</option>
      </select>
    </sky-filter-inline-item>
    <sky-filter-inline-item>
      <sky-checkbox
        [ngModel]="hideOrange"
        (ngModelChange)="hideOrangeChange($event)"
        >
        <sky-checkbox-label>
          Hide orange fruits
        </sky-checkbox-label>
      </sky-checkbox>
    </sky-filter-inline-item>
  </sky-filter-inline>
  <sky-repeater expandMode="none">
    <sky-repeater-item *ngFor="let item of filteredItems">
      <sky-repeater-item-title>
        {{item.name}}
      </sky-repeater-item-title>
      <sky-repeater-item-content>
        <div>
          {{item.description}}
        </div>
      </sky-repeater-item-content>
    </sky-repeater-item>
  </sky-repeater>

</div>
`
        },
        {
          name: 'filter-inline-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

@Component({
  selector: 'sky-filter-inline-demo',
  templateUrl: './filter-inline-demo.component.html'
})
export class SkyFilterInlineDemoComponent {

  public filtersActive: boolean = false;

  public showInlineFilters: boolean = false;

  public fruitType: string = 'any';

  public hideOrange: boolean = false;

  public items: any[] = [
      {
          name: 'Orange',
          description: 'A round, orange fruit.',
          type: 'citrus',
          color: 'orange'
      },
      {
          name: 'Mango',
          description: 'Delicious in smoothies, but don\\\'t eat the skin.',
          type: 'other',
          color: 'orange'
      },
      {
          name: 'Lime',
          description: 'A sour, green fruit used in many drinks.',
          type: 'citrus',
          color: 'green'
      },
      {
          name: 'Strawberry',
          description: 'A red fruit that goes well with shortcake.',
          type: 'berry',
          color: 'red'
      },
      {
          name: 'Blueberry',
          description: 'A small, blue fruit often found in muffins.',
          type: 'berry',
          color: 'blue'
      }

  ];

  public filteredItems: any[];

  public appliedFilters: any[] = [];

  constructor() {
    this.filteredItems = this.items.slice();
  }

  public filterButtonClicked() {
    this.showInlineFilters = !this.showInlineFilters;
  }

  public fruitTypeChange(newValue: string) {
    this.fruitType = newValue;
    this.setFilterActiveState();
  }

  public hideOrangeChange(newValue: boolean) {
    this.hideOrange = newValue;
    this.setFilterActiveState();
  }

  private setFilterActiveState() {
    this.appliedFilters = [];
    if (this.fruitType !== 'any') {
      this.appliedFilters.push({
        name: 'fruitType',
        value: this.fruitType
      });
    }
    if (this.hideOrange) {
      this.appliedFilters.push({
        name: 'hideOrange',
        value: true
      });
    }
    this.filtersActive = this.appliedFilters.length > 0;

    this.filteredItems = this.filterItems(this.items, this.appliedFilters);
  }

  private orangeFilterFailed(filter: any, item: any) {
    return filter.name === 'hideOrange' && filter.value && item.color === 'orange';
  }

  private fruitTypeFilterFailed(filter: any, item: any) {
    return filter.name === 'fruitType' && filter.value !== 'any' && filter.value !== item.type;
  }

  private itemIsShown(filters: any[], item: any[]) {
    let passesFilter = true,
        j: number;

    for (j = 0; j < filters.length; j++) {
      if (this.orangeFilterFailed(filters[j], item)) {
        passesFilter = false;
      } else if (this.fruitTypeFilterFailed(filters[j], item)) {
        passesFilter = false;
      }
    }

    return passesFilter;
  }

  private filterItems(items: any[], filters: any[]) {
      let i: number,
          passesFilter: boolean,
          result: any[] = [];

      for (i = 0; i < items.length; i++) {
        passesFilter = this.itemIsShown(filters, items[i]);
        if (passesFilter) {
          result.push(items[i]);
        }
      }

      return result;
  }

}
`,
          componentName: 'SkyFilterInlineDemoComponent',
          bootstrapSelector: 'sky-filter-inline-demo'
        }
      ]
    },
    {
      name: 'Fluid grid',
      component: SkyFluidGridDemoComponent,
      files: [
        {
          name: 'fluid-grid-demo.component.html',
          fileContents: `<div class="highlight-columns">
  <sky-row>
    <sky-column screenSmall="1">
      small="1"
    </sky-column>
    <sky-column screenSmall="1">
      small="1"
    </sky-column>
    <sky-column screenSmall="1">
      small="1"
    </sky-column>
    <sky-column screenSmall="1">
      small="1"
    </sky-column>
    <sky-column screenSmall="1">
      small="1"
    </sky-column>
    <sky-column screenSmall="1">
      small="1"
    </sky-column>
    <sky-column screenSmall="1">
      small="1"
    </sky-column>
    <sky-column screenSmall="1">
      small="1"
    </sky-column>
    <sky-column screenSmall="1">
      small="1"
    </sky-column>
    <sky-column screenSmall="1">
      small="1"
    </sky-column>
    <sky-column screenSmall="1">
      small="1"
    </sky-column>
    <sky-column screenSmall="1">
      small="1"
    </sky-column>
  </sky-row>

  <sky-row>
    <sky-column screenSmall="2">
      small="2"
    </sky-column>
    <sky-column screenSmall="2">
      small="2"
    </sky-column>
    <sky-column screenSmall="2">
      small="2"
    </sky-column>
    <sky-column screenSmall="2">
      small="2"
    </sky-column>
    <sky-column screenSmall="2">
      small="2"
    </sky-column>
    <sky-column screenSmall="2">
      small="2"
    </sky-column>
  </sky-row>

  <sky-row>
    <sky-column screenSmall="3">
      small="3"
    </sky-column>
    <sky-column screenSmall="3">
      small="3"
    </sky-column>
    <sky-column screenSmall="3">
      small="3"
    </sky-column>
    <sky-column screenSmall="3">
      small="3"
    </sky-column>
  </sky-row>

  <sky-row>
    <sky-column screenSmall="4">
      small="4"
    </sky-column>
    <sky-column screenSmall="4">
      small="4"
    </sky-column>
    <sky-column screenSmall="4">
      small="4"
    </sky-column>
  </sky-row>

  <sky-row>
    <sky-column screenSmall="5">
      small="5"
    </sky-column>
    <sky-column screenSmall="7">
      small="7"
    </sky-column>
  </sky-row>

  <sky-row>
    <sky-column screenSmall="6">
      small="6"
    </sky-column>
    <sky-column screenSmall="6">
      small="6"
    </sky-column>
  </sky-row>

  <sky-row>
    <sky-column screenSmall="8">
      small="8"
    </sky-column>
    <sky-column screenSmall="4">
      small="4"
    </sky-column>
  </sky-row>

  <sky-row>
    <sky-column screenSmall="9">
      small="9"
    </sky-column>
    <sky-column screenSmall="3">
      small="3"
    </sky-column>
  </sky-row>

  <sky-row>
    <sky-column screenSmall="10">
      small="10"
    </sky-column>
    <sky-column screenSmall="2">
      small="2"
    </sky-column>
  </sky-row>

  <sky-row>
    <sky-column screenSmall="11">
      small="11"
    </sky-column>
    <sky-column screenSmall="1">
      small="1"
    </sky-column>
  </sky-row>

  <sky-row>
    <sky-column screenXSmall="6" screenSmall="8" screenMedium="9" screenLarge="10">
        screenXSmall="6" small="8" medium="9" large="10"
    </sky-column>
    <sky-column screenXSmall="6" screenSmall="4" screenMedium="3" screenLarge="2">
        screenXSmall="6" small="4" medium="3" large="2"
    </sky-column>
  </sky-row>

  <sky-row [reverseColumnOrder]="true">
    <sky-column screenSmall="4">
      First column
    </sky-column>
    <sky-column screenSmall="4">
      Second column
    </sky-column>
    <sky-column screenSmall="4">
      Third column
    </sky-column>
  </sky-row>
</div>
`
        },
        {
          name: 'fluid-grid-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

@Component({
  selector: 'sky-fluid-grid-demo',
  templateUrl: './fluid-grid-demo.component.html',
  styles: [\`
  .highlight-columns .sky-column {
    background-color: #97eced;
    border: 1px solid #56e0e1;
    overflow-wrap: break-word;
  }
  \`]
})
export class SkyFluidGridDemoComponent { }
`,
          componentName: 'SkyFluidGridDemoComponent',
          bootstrapSelector: 'sky-fluid-grid-demo'
        }
      ]
    },
    {
      name: 'Flyout',
      component: SkyFlyoutDemoComponent,
      files: [
        {
          name: 'flyout-demo.component.html',
          fileContents: `<sky-list
  [data]="users"
  [defaultView]="grid">
  <sky-list-view-grid #grid>
    <sky-grid-column
      [locked]="true"
      [template]="customTemplate">
    </sky-grid-column>
    <sky-grid-column
      field="name"
      heading="Name"
      [locked]="true">
    </sky-grid-column>
  </sky-list-view-grid>
</sky-list>

<ng-template
  let-row="row"
  #customTemplate>
  <button
    type="button"
    class="sky-btn sky-btn-link"
    (click)="openRecord(row)">
    View record
  </button>
  <button
    *ngIf="isRecordOpen(row)"
    type="button"
    class="sky-btn sky-btn-default"
    title="Close record"
    (click)="closeFlyout()">
    <i class="fa fa-close" aria-hidden="true"></i>
  </button>
</ng-template>

<div *ngIf="flyout">
  <p>
    This button will close the flyout and then delete the host element from the document.
  </p>

  <button
    type="button"
    class="sky-btn sky-btn-default"
    (click)="removeFlyout()">
    <i class="fa fa-trash" aria-hidden="true"></i>
    Delete flyout
  </button>
</div>
`
        },
        {
          name: 'flyout-demo.component.ts',
          fileContents: `
import {
  Component
} from '@angular/core';

import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/of';

import {
  SkyFlyoutInstance,
  SkyFlyoutService
} from '../../core';

import { FlyoutDemoContext } from './flyout-demo-context';
import { SkyFlyoutDemoInternalComponent } from './flyout-demo-internal.component';

@Component({
  selector: 'sky-flyout-demo',
  templateUrl: './flyout-demo.component.html'
})
export class SkyFlyoutDemoComponent {
  public users = Observable.of([
    { id: '1', name: 'Sally' },
    { id: '2', name: 'John' },
    { id: '3', name: 'David' },
    { id: '4', name: 'Janet' }
  ]);

  public flyout: SkyFlyoutInstance<SkyFlyoutDemoInternalComponent>;

  constructor(
    private flyoutService: SkyFlyoutService
  ) { }

  public openRecord(record: FlyoutDemoContext) {
    this.flyout = this.flyoutService.open(SkyFlyoutDemoInternalComponent, {
      providers: [{
        provide: FlyoutDemoContext,
        useValue: record
      }],
      ariaDescribedBy: 'my-describedby-id',
      ariaLabelledBy: 'my-labelledby-id',
      ariaRole: 'modal',
      defaultWidth: 500,
      maxWidth: 1000,
      minWidth: 200
    });

    this.flyout.closed.subscribe(() => {
      this.flyout = undefined;
    });
  }

  public closeFlyout() {
    this.flyout.close();
  }

  public removeFlyout() {
    this.flyoutService.close();
    this.flyout = undefined;
  }

  public isRecordOpen(record: FlyoutDemoContext): boolean {
    return (
      this.flyout &&
      this.flyout.componentInstance.context.id === record.id
    );
  }
}
`,
          componentName: 'SkyFlyoutDemoComponent',
          bootstrapSelector: 'sky-flyout-demo'
        },
        {
          name: 'flyout-demo-internal.component.html',
          fileContents: `<sky-page-summary>
  <sky-page-summary-alert>
    <sky-alert
      alertType="info">
      The internal flyout content is dynamic!
    </sky-alert>
  </sky-page-summary-alert>
  <sky-page-summary-image>
    <sky-avatar
      [name]="context.name"
      [canChange]="false">
    </sky-avatar>
  </sky-page-summary-image>
  <sky-page-summary-title>
    {{context.name}}
  </sky-page-summary-title>
  <sky-page-summary-subtitle>
    Board member
  </sky-page-summary-subtitle>
  <sky-page-summary-status>
    <sky-label
      labelType="success">
      Fundraiser
    </sky-label>
    <sky-label>Inactive</sky-label>
  </sky-page-summary-status>
  <sky-page-summary-content>
    This is the arbitrary content section.
  </sky-page-summary-content>
</sky-page-summary>
`
        },
        {
          name: 'flyout-demo-internal.component.ts',
          fileContents: `import {
  Component
} from '@angular/core';

import { FlyoutDemoContext } from './flyout-demo-context';

@Component({
  selector: 'sky-flyout-demo-internal',
  templateUrl: './flyout-demo-internal.component.html'
})
export class SkyFlyoutDemoInternalComponent {
  constructor(
    public context: FlyoutDemoContext
  ) { }
}
`,
          componentName: 'SkyFlyoutDemoInternalComponent'
        },
        {
          name: 'flyout-demo-context.ts',
          fileContents: `export class FlyoutDemoContext {
  public id: number;
  public name: string;
}
`
        }
      ]
    },
    {
      name: 'Grid',
      component: SkyGridDemoComponent,
      files: [
        {
          name: 'grid-demo.component.html',
          fileContents: `<sky-grid
  fit="scroll"
  [data]="items"
  (sortFieldChange)="sortChanged($event)">
  <sky-grid-column
    id="column1"
    field="column1"
    [heading]="asyncHeading | async">
  </sky-grid-column>
  <sky-grid-column
    id="column2"
    field="column2"
    heading="Column2">
  </sky-grid-column>
  <sky-grid-column
    id="column3"
    field="column3"
    heading="Column3">
  </sky-grid-column>
  <sky-grid-column
    id="composite"
    field="composite"
    heading="Composite column"
    [template]="customColumnTemplate">
  </sky-grid-column>
</sky-grid>

<ng-template
  let-row="row"
  let-value="value"
  #customColumnTemplate>
  <div>
    Value: {{value}}
  </div>
  <div>
    Column 2: {{row.column2}}
  </div>
</ng-template>
`
        },
        {
          name: 'grid-demo.component.ts',
          fileContents: `import {
  Component,
  OnInit
} from '@angular/core';

import { BehaviorSubject } from 'rxjs/BehaviorSubject';

import {
  ListSortFieldSelectorModel
} from '../../core';

@Component({
  selector: 'sky-grid-demo',
  templateUrl: './grid-demo.component.html'
})
export class SkyGridDemoComponent implements OnInit {
  public items: any[] = [
    { id: '1', column1: 101, column2: 'Apple', column3: 'Anne eats apples', composite: 'Comp A' },
    { id: '2', column1: 202, column2: 'Banana', column3: 'Ben eats bananas', composite: 'Comp B' },
    { id: '3', column1: 303, column2: 'Pear', column3: 'Patty eats pears', composite: 'Comp C' },
    { id: '4', column1: 404, column2: 'Grape', column3: 'George eats grapes', composite: 'Comp D' },
    { id: '5', column1: 505, column2: 'Banana', column3: 'Becky eats bananas',
      composite: 'Comp E' },
    { id: '6', column1: 606, column2: 'Lemon', column3: 'Larry eats lemons', composite: 'Comp F' },
    { id: '7', column1: 707, column2: 'Strawberry', column3: 'Sally eats strawberries',
      composite: 'Comp G' }
  ];

  public asyncHeading = new BehaviorSubject<string>('');

  public ngOnInit() {
    // Simulate async request:
    setTimeout(() => {
      this.asyncHeading.next('Column1');
    }, 1000);
  }

  public sortChanged(activeSort: ListSortFieldSelectorModel) {
    const sortField = activeSort.fieldSelector;
    const descending = activeSort.descending;

    this.items = this.items.sort((a: any, b: any) => {
      let value1 = a[sortField];
      let value2 = b[sortField];

      if (value1 && typeof value1 === 'string') {
        value1 = value1.toLowerCase();
      }

      if (value2 && typeof value2 === 'string') {
        value2 = value2.toLowerCase();
      }

      if (value1 === value2) {
        return 0;
      }

      let result = value1 > value2 ? 1 : -1;

      if (descending) {
        result *= -1;
      }

      return result;
    }).slice();
  }
}
`,
          componentName: 'SkyGridDemoComponent',
          bootstrapSelector: 'sky-grid-demo'
        }
      ]
    },
    {
      name: 'Help inline',
      component: SkyHelpInlineDemoComponent,
      files: [
        {
          name: 'help-inline-demo.component.html',
          fileContents: `<div>
  <h2>
    Giving
    <sky-help-inline
      (actionClick)="givingActionClick()">
    </sky-help-inline>
  </h2>

  <h4>
    Email entry
    <sky-help-inline
      (actionClick)="emailActionClick()">
    </sky-help-inline>
  </h4>

  <input placeholder="Enter email">
</div>
`
        },
        {
          name: 'help-inline-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

@Component({
  selector: 'sky-help-inline-demo',
  templateUrl: './help-inline-demo.component.html'
})
export class SkyHelpInlineDemoComponent {
  public givingActionClick() {
    alert('Giving help inline clicked');
  }

  public emailActionClick() {
    alert('enter email in format joe@abc.com');
  }
}
`,
          componentName: 'SkyHelpInlineDemoComponent',
          bootstrapSelector: 'sky-help-inline-demo'
        }
      ]
    },
    {
      name: 'Key info',
      component: SkyKeyInfoDemoComponent,
      files: [
        {
          name: 'key-info-demo.component.html',
          fileContents: `<h3>
  Vertical example
</h3>

<sky-key-info>
  <sky-key-info-value>
    This is a default sample value.
  </sky-key-info-value>
  <sky-key-info-label>
    This is a default sample label.
  </sky-key-info-label>
</sky-key-info>
<sky-key-info>
  <sky-key-info-value>
    This is another default sample value.
  </sky-key-info-value>
  <sky-key-info-label>
    This is another default sample label.
  </sky-key-info-label>
</sky-key-info>

<h3>
  Horizontal example
</h3>

<sky-key-info
  layout="horizontal">
  <sky-key-info-value>
    This is a horizontal sample value.
  </sky-key-info-value>
  <sky-key-info-label>
    This is a horizontal sample label.
  </sky-key-info-label>
</sky-key-info>
`
        },
        {
          name: 'key-info-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

@Component({
  selector: 'sky-key-info-demo',
  templateUrl: './key-info-demo.component.html'
})
export class SkyKeyInfoDemoComponent { }
`,
          componentName: 'SkyKeyInfoDemoComponent',
          bootstrapSelector: 'sky-key-info-demo'
        }
      ]
    },
    {
      name: 'Label',
      component: SkyLabelDemoComponent,
      files: [
        {
          name: 'label-demo.component.html',
          fileContents: `<sky-label labelType="info">
  Info label
</sky-label>
<sky-label labelType="success">
  Success label
</sky-label>
<sky-label labelType="warning">
  Warning label
</sky-label>
<sky-label labelType="danger">
  Danger label
</sky-label>
`
        },
        {
          name: 'label-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

@Component({
  selector: 'sky-label-demo',
  templateUrl: './label-demo.component.html'
})
export class SkyLabelDemoComponent { }
`,
          componentName: 'SkyLabelDemoComponent',
          bootstrapSelector: 'sky-label-demo'
        }
      ]
    },
    {
      name: 'Link records',
      component: SkyLinkRecordsDemoComponent,
      files: [
        {
          name: 'link-records-demo.component.html',
          fileContents: `<h3>
  Link records child templates
</h3>

<sky-link-records
  [items]="items"
  [matches]="matches"
  [matchFields]="matchFields"
  [showNewFieldValues]="false">

  <sky-link-records-item-title>
    <ng-template
      let-item="item">
      Applicant
    </ng-template>
  </sky-link-records-item-title>

  <sky-link-records-item-content>
    <ng-template
      let-item="item">
      <div>Original Item</div>
      <div>{{ item?.id }}</div>
      <div>{{ item?.name }}</div>
      <div>{{ item?.description }}</div>
    </ng-template>
  </sky-link-records-item-content>

  <sky-link-records-match-content>
    <ng-template let-match="match">
      <div>Match Item</div>
      <div>{{ match?.item?.id }}</div>
      <div>{{ match?.item?.name }}</div>
      <div>{{ match?.item?.description }}</div>
    </ng-template>
  </sky-link-records-match-content>

  <sky-link-records-nomatch-content>
    <ng-template
      let-item="item"
      let-match="match"
      let-api="api">
      <div *ngIf="match?.item">
        <div>Match Item</div>
        <div>{{ match?.item?.id }}</div>
        <div>{{ match?.item?.name }}</div>
        <div>{{ match?.item?.description }}</div>
      </div>
      <div *ngIf="!match?.item">
        <div>Match Search</div>
        <button
          class="sky-btn sky-btn-default"
          (click)="api.addSelectedItem(item?.id, newItem)">
          Test Select
        </button>
      </div>
    </ng-template>
  </sky-link-records-nomatch-content>
</sky-link-records>

<button
  class="sky-btn sky-btn-default"
  (click)="showResults()">
  Show Results
</button>

<h3>
  Default link with templates
</h3>

<sky-link-records
  [items]="items"
  [matches]="matches"
  [itemTemplate]="item"
  [matchTemplate]="match"
  [matchFields]="matchFields">
</sky-link-records>

<ng-template #itemTitle let-item="item">Custom Title</ng-template>

<ng-template #item let-item="item">
  <div>{{ item?.name }}</div>
  <div>{{ item?.description }}</div>
</ng-template>

<ng-template #match let-match="match">
  <div>Custom Label:</div>
  <div>{{ match?.item?.name }}</div>
  <div>{{ match?.item?.description }}</div>
</ng-template>

<button
  class="sky-btn sky-btn-default"
  (click)="showResults()">
  Show Results
</button>
`
        },
        {
          name: 'link-records-demo.component.ts',
          fileContents: `import { Component, ViewChild } from '@angular/core';

import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/take';

import {
  SKY_LINK_RECORDS_STATUSES,
  SkyLinkRecordsComponent,
  SkyLinkRecordsMatchModel
} from '../../core';

const getWindow = () => {
  return window;
};

@Component({
  selector: 'sky-link-records-demo',
  templateUrl: './link-records-demo.component.html'
})
export class SkyLinkRecordsDemoComponent {
  @ViewChild(SkyLinkRecordsComponent)
  public item: SkyLinkRecordsComponent;

  public window: any;
  public matchFields: any[] = [{ key: 'description' }, { key: 'name' }];
  public newItem: any = { id: '99', address: 999, name: 'Lime', description: 'Laura eats limes.' };

  public items: Observable<any> = Observable.of([
    { id: '1', address: 101, name: 'Apple', description: 'Anne eats apples' },
    { id: '2', address: 202, name: 'Banana', description: 'Ben eats bananas' },
    { id: '3', address: 303, name: 'Pear', description: 'Patty eats pears' },
    { id: '4', address: 404, name: 'Grape', description: 'George eats grapes' },
    { id: '5', address: 505, name: 'Banana', description: 'Becky eats bananas' },
    { id: '6', address: 606, name: 'Lemon', description: 'Larry eats lemons' },
    { id: '7', address: 707, name: 'Kiwi', description: 'Kim eats kiwis.' },
    { id: '8', address: 808, name: 'Strawberry', description: 'Sally eats strawberries' }
  ]);

  public matches: Observable<SkyLinkRecordsMatchModel[]> = Observable.of([
    new SkyLinkRecordsMatchModel({
      key: '1',
      status: SKY_LINK_RECORDS_STATUSES.Edit,
      item: { id: '11', address: 111, name: 'Big Apple', description: 'George and his apples' }
    }),
    new SkyLinkRecordsMatchModel({
      key: '2',
      status: undefined,
      item: undefined
    }),
    new SkyLinkRecordsMatchModel({
      key: '3',
      status: SKY_LINK_RECORDS_STATUSES.NoMatch,
      item: undefined
    }),
    new SkyLinkRecordsMatchModel({
      key: '5',
      status: SKY_LINK_RECORDS_STATUSES.Suggested,
      item: { id: '55', address: 555, name: 'Huge Banana', description: 'Barry loves bananas.' }
    }),
    new SkyLinkRecordsMatchModel({
      key: '6',
      status: SKY_LINK_RECORDS_STATUSES.Selected,
      item: { id: '66', address: 666, name: 'Lovely Lemons', description: 'Lisa loves lemons.' }
    }),
    new SkyLinkRecordsMatchModel({
      key: '7',
      status: SKY_LINK_RECORDS_STATUSES.Created,
      item: undefined
    }),
    new SkyLinkRecordsMatchModel({
      key: '8',
      status: SKY_LINK_RECORDS_STATUSES.Linked,
      item: {
        id: '88',
        address: 888,
        name: 'Strawberry Shortcake',
        description: 'Steve loves strawberries'
      }
    })
  ]);

  constructor() {
    this.window = getWindow();
  }

  public showResults() {
    this.item.results.take(1).subscribe((r: any) => this.window.alert(JSON.stringify(r)));
  }
}
`,
          componentName: 'SkyLinkRecordsDemoComponent',
          bootstrapSelector: 'sky-link-records-demo'
        }
      ]
    },
    {
      name: 'List',
      component: SkyListDemoComponent,
      files: [
        {
          name: 'list-demo.component.html',
          fileContents: `<button
  style="margin-bottom: 5px;"
  class="sky-btn sky-btn-default"
  (click)="changeData()">
  Change data
</button>

<sky-list [data]="items">
  <sky-list-toolbar></sky-list-toolbar>

  <sky-list-view-grid fit="scroll">
    <sky-grid-column field="column1" heading="Column1"></sky-grid-column>
    <sky-grid-column field="column2" heading="Column2"></sky-grid-column>
    <sky-grid-column field="column3" heading="Column3"></sky-grid-column>
  </sky-list-view-grid>

  <sky-list-paging pageSize="5"></sky-list-paging>
</sky-list>

`
        },
        {
          name: 'list-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';

@Component({
  selector: 'sky-list-demo',
  templateUrl: './list-demo.component.html'
})
export class SkyListDemoComponent {
  public items: BehaviorSubject<any> = new BehaviorSubject([
    { id: '1', column1: 101, column2: 'Apple', column3: 'Anne eats apples' },
    { id: '2', column1: 202, column2: 'Banana', column3: 'Ben eats bananas' },
    { id: '3', column1: 303, column2: 'Pear', column3: 'Patty eats pears' },
    { id: '4', column1: 404, column2: 'Grape', column3: 'George eats grapes' },
    { id: '5', column1: 505, column2: 'Banana', column3: 'Becky eats bananas' },
    { id: '6', column1: 606, column2: 'Lemon', column3: 'Larry eats lemons' },
    { id: '7', column1: 707, column2: 'Strawberry', column3: 'Sally eats strawberries' }
  ]);

  public changeData() {
    this.items.next([
      { id: '1', column1: 101, column2: 'blah', column3: 'Anne eats apples' },
      { id: '2', column1: 202, column2: 'me', column3: 'Ben eats bananas' },
      { id: '3', column1: 303, column2: 'q', column3: 'Patty eats pears' },
      { id: '4', column1: 404, column2: 'j', column3: 'George eats grapes' },
      { id: '5', column1: 505, column2: 'l', column3: 'Becky eats bananas' },
      { id: '6', column1: 606, column2: 'i', column3: 'Larry eats lemons' },
      { id: '7', column1: 707, column2: 't', column3: 'Sally eats strawberries' }
    ]);
  }
}
`,
          componentName: 'SkyListDemoComponent',
          bootstrapSelector: 'sky-list-demo'
        }
      ]
    },
    {
      name: 'List filters',
      component: SkyListFiltersDemoComponent,
      files: [
        {
          name: 'list-filters-demo.component.html',
          fileContents: `<sky-list
  [data]="items"
  [defaultView]="grid"
  [appliedFilters]="listFilters"
  (appliedFiltersChange)="modalFilters = $event">
  <sky-list-toolbar>
    <sky-list-secondary-actions>
      <sky-list-secondary-action>
        <button type="button">
          Option
        </button>
      </sky-list-secondary-action>
    </sky-list-secondary-actions>
    <sky-list-toolbar-sort
      label="Fruit name (A - Z)"
      field="name"
      [descending]="false"
      type="string"
    >
    </sky-list-toolbar-sort>
    <sky-list-toolbar-sort
      label="Fruit name (Z - A)"
      field="name"
      [descending]="true"
      type="string"
    >
    </sky-list-toolbar-sort>
    <sky-list-filter-button>
      <sky-filter-button
        (filterButtonClick)="openFilterModal()">
      </sky-filter-button>
    </sky-list-filter-button>
    <sky-list-filter-summary
      (summaryItemClick)="openFilterModal()">
    </sky-list-filter-summary>
  </sky-list-toolbar>

  <sky-list-view-grid fit="scroll" #grid>
    <sky-grid-column
      field="name"
      heading="Name">
    </sky-grid-column>
    <sky-grid-column
      field="description"
      heading="Description">
    </sky-grid-column>
  </sky-list-view-grid>
 </sky-list>
`
        },
        {
          name: 'list-filters-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';
import { Observable } from 'rxjs/Observable';

import {
  SkyModalService,
  SkyModalCloseArgs,
  ListFilterModel
} from '../../core';

import { SkyListFiltersModalDemoComponent } from './list-filters-demo-modal.component';
import { SkyListFiltersModalDemoContext } from './list-filters-demo-modal-context';

@Component({
  selector: 'sky-list-filters-demo',
  templateUrl: './list-filters-demo.component.html'
})
export class SkyListFiltersDemoComponent {
  public listFilters: ListFilterModel[] = [];
  public modalFilters: ListFilterModel[] = [];

  public items: Observable<any> = Observable.of([
    {
      id: 0,
      name: 'Orange',
      description: 'A round, orange fruit.',
      type: 'citrus',
      color: 'orange'
    },
    {
      id: 1,
      name: 'Mango',
      description: 'Delicious in smoothies, but don\\\'t eat the skin.',
      type: 'other',
      color: 'orange'
    },
    {
      id: 2,
      name: 'Lime',
      description: 'A sour, green fruit used in many drinks.',
      type: 'citrus',
      color: 'green'
    },
    {
      id: 3,
      name: 'Strawberry',
      description: 'A red fruit that goes well with shortcake.',
      type: 'berry',
      color: 'red'
    },
    {
      id: 4,
      name: 'Blueberry',
      description: 'A small, blue fruit often found in muffins.',
      type: 'berry',
      color: 'blue'
    }
  ]);

  constructor(private modalService: SkyModalService) {}

  public openFilterModal() {
    let instance = this.modalService.open(SkyListFiltersModalDemoComponent, [
      {
        provide: SkyListFiltersModalDemoContext,
          useValue: {
            appliedFilters: this.modalFilters
          }
      }
    ]);

    instance.closed.subscribe((result: SkyModalCloseArgs) => {
      if (result.reason === 'save') {
        this.listFilters = result.data.slice();
      }
    });
  }
}
`,
          componentName: 'SkyListFiltersDemoComponent',
          bootstrapSelector: 'sky-list-filters-demo'
        },
        {
          name: 'list-filters-demo-modal.component.html',
          fileContents: `<sky-modal>
  <sky-modal-header>
    {{headerText}}
  </sky-modal-header>
  <sky-modal-content>
    <label for="sky-demo-select-type">Fruit type</label>
    <select id="sky-demo-select-type" [(ngModel)]="fruitType" class="sky-form-control">
      <option value="any">Any fruit</option>
      <option value="citrus">Citrus</option>
      <option value="berry">Berry</option>
    </select>
    <div style="margin-top: 15px;">
      <sky-checkbox [(ngModel)]="hideOrange">
        <sky-checkbox-label>
          Hide orange fruits
        </sky-checkbox-label>
      </sky-checkbox>
    </div>
  </sky-modal-content>
  <sky-modal-footer>
    <button
      type="button"
      class="sky-btn sky-btn-primary"
      (click)="applyFilters()">
      Apply filters
    </button>
    <button
      type="button"
      class="sky-btn sky-btn-link"
      (click)="clearAllFilters()">
      Clear all filters
    </button>
    <button
      type="button"
      class="sky-btn sky-btn-link"
      (click)="cancel()">
      Cancel
    </button>
  </sky-modal-footer>
</sky-modal>`
        },
        {
          name: 'list-filters-demo-modal.component.ts',
          fileContents: `import { Component } from '@angular/core';

import {
  SkyModalInstance,
  ListFilterModel,
  ListItemModel
} from '../../core';

import {
  SkyListFiltersModalDemoContext
} from './list-filters-demo-modal-context';

@Component({
  selector: 'sky-demo-filter-modal-form',
  templateUrl: './list-filters-demo-modal.component.html'
})
export class SkyListFiltersModalDemoComponent {

  public fruitType: string = 'any';

  public hideOrange: boolean;

  public headerText: string = 'Filters';

  constructor(public context: SkyListFiltersModalDemoContext, public instance: SkyModalInstance) {
    if (this.context && this.context.appliedFilters && this.context.appliedFilters.length > 0) {
      this.setFormFilters(this.context.appliedFilters);
    } else {
      this.clearAllFilters();
    }
  }

  public applyFilters() {
    let result = this.getAppliedFiltersArray();
    this.instance.save(result);
  }

  public clearAllFilters() {
    this.hideOrange = false;
    this.fruitType = 'any';
  }

  public cancel() {
    this.instance.cancel();
  }

  private fruitTypeFilterFunction(item: ListItemModel, filterValue: any): boolean {
    return filterValue === item.data.type;
  }

  private hideOrangeFilterFunction(item: ListItemModel, filterValue: any): boolean {
    return !filterValue || (filterValue && item.data.color !== 'orange');
  }

  private getAppliedFiltersArray() {
    let appliedFilters: ListFilterModel[] = [];
    if (this.fruitType !== 'any') {

      appliedFilters.push(new ListFilterModel({
        name: 'fruitType',
        value: this.fruitType,
        label: this.fruitType,
        filterFunction: this.fruitTypeFilterFunction
      }));
    }

    if (this.hideOrange) {
      appliedFilters.push(new ListFilterModel({
        name: 'hideOrange',
        value: true,
        label: 'hide orange fruits',
        filterFunction: this.hideOrangeFilterFunction
      }));
    }

    return appliedFilters;
  }

  private setFormFilters(appliedFilters: any[]) {
    for (let i = 0; i < appliedFilters.length; i++) {
      if (appliedFilters[i].name === 'fruitType') {
        this.fruitType = appliedFilters[i].value;
      }

      if (appliedFilters[i].name === 'hideOrange') {
        this.hideOrange = appliedFilters[i].value;
      }
    }
  }
}
`,
          componentName: 'SkyListFiltersModalDemoComponent'
        },
        {
          name: 'list-filters-demo-modal-context.ts',
          fileContents: `export class SkyListFiltersModalDemoContext {
  public appliedFilters: any[];
}
`
        }
      ]
    },
    {
      name: 'List filters inline',
      component: SkyListFiltersInlineDemoComponent,
      files: [
        {
          name: 'list-filters-inline-demo.component.html',
          fileContents: `<sky-list
  [data]="items"
  [defaultView]="grid">
  <sky-list-toolbar>
    <sky-list-secondary-actions>
      <sky-list-secondary-action>
        <button type="button">
          Option
        </button>
      </sky-list-secondary-action>
    </sky-list-secondary-actions>
    <sky-list-toolbar-sort
      label="Fruit name (A - Z)"
      field="name"
      [descending]="false"
      type="string"
    >
    </sky-list-toolbar-sort>
    <sky-list-toolbar-sort
      label="Fruit name (Z - A)"
      field="name"
      [descending]="true"
      type="string"
    >
    </sky-list-toolbar-sort>
    <sky-list-filter-inline>
      <sky-list-filter-inline-item
        name="fruitType"
        [filter]="fruitTypeFilterFunction"
        value="any"
        defaultValue="any">
        <ng-template let-filter="filter">
          <label for="sky-demo-select-type">Fruit type</label>
          <select
            id="sky-demo-select-type"
            [ngModel]="filter.value"
            (ngModelChange)="filter.changed($event)">
            <option value="any">Any fruit</option>
            <option value="citrus">Citrus</option>
            <option value="berry">Berry</option>
          </select>
        </ng-template>
      </sky-list-filter-inline-item>
      <sky-list-filter-inline-item
        name="hideOrange"
        [filter]="hideOrangeFilterFunction">
        <ng-template let-filter="filter">
          <sky-checkbox
            [ngModel]="filter.value"
            (ngModelChange)="filter.changed($event)">
            <sky-checkbox-label>
              Hide orange fruits
            </sky-checkbox-label>
          </sky-checkbox>
        </ng-template>
      </sky-list-filter-inline-item>
    </sky-list-filter-inline>
  </sky-list-toolbar>
  <sky-list-view-grid fit="scroll" #grid>
    <sky-grid-column
      field="name"
      heading="Name">
    </sky-grid-column>
    <sky-grid-column
      field="description"
      heading="Description">
    </sky-grid-column>
  </sky-list-view-grid>
 </sky-list>
`
        },
        {
          name: 'list-filters-inline-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/of';

import {
  ListItemModel
} from '../../core';

@Component({
  selector: 'sky-list-filters-inline-demo',
  templateUrl: './list-filters-inline-demo.component.html'
})
export class SkyListFiltersInlineDemoComponent {

  public items: Observable<any> = Observable.of([
    {
      id: 0,
      name: 'Orange',
      description: 'A round, orange fruit.',
      type: 'citrus',
      color: 'orange'
    },
    {
      id: 1,
      name: 'Mango',
      description: 'Delicious in smoothies, but don\\\'t eat the skin.',
      type: 'other',
      color: 'orange'
    },
    {
      id: 2,
      name: 'Lime',
      description: 'A sour, green fruit used in many drinks.',
      type: 'citrus',
      color: 'green'
    },
    {
      id: 3,
      name: 'Strawberry',
      description: 'A red fruit that goes well with shortcake.',
      type: 'berry',
      color: 'red'
    },
    {
      id: 4,
      name: 'Blueberry',
      description: 'A small, blue fruit often found in muffins.',
      type: 'berry',
      color: 'blue'
    }
  ]);

  public fruitTypeFilterFunction(item: ListItemModel, filterValue: any): boolean {
    return filterValue === 'any' || filterValue === item.data.type;
  }

  public hideOrangeFilterFunction(item: ListItemModel, filterValue: any): boolean {
    return !filterValue || (filterValue && item.data.color !== 'orange');
  }

}
`,
          componentName: 'SkyListFiltersInlineDemoComponent',
          bootstrapSelector: 'sky-list-filters-inline-demo'
        }
      ]
    },
    {
      name: 'List paging',
      component: SkyListPagingDemoComponent,
      files: [
        {
          name: 'list-paging-demo.component.html',
          fileContents: `<sky-list-paging
  pageSize="2"
  maxPages="3"
  pageNumber="1">
</sky-list-paging>
`
        },
        {
          name: 'list-paging-demo.component.ts',
          fileContents: `import {
  Component,
  OnInit
} from '@angular/core';

import {
  ListState,
  ListStateDispatcher,
  ListItemsLoadAction,
  ListItemModel
} from '../../core';

@Component({
  selector: 'sky-list-paging-demo',
  templateUrl: './list-paging-demo.component.html',
  providers: [
    ListState,
    ListStateDispatcher
  ]
})
export class SkyListPagingDemoComponent implements OnInit {
  constructor(
    private dispatcher: ListStateDispatcher
  ) { }

  public ngOnInit() {
    this.dispatcher.next(new ListItemsLoadAction([
      new ListItemModel('1'),
      new ListItemModel('2'),
      new ListItemModel('3'),
      new ListItemModel('4'),
      new ListItemModel('5'),
      new ListItemModel('6'),
      new ListItemModel('7')
    ], true));
  }
}
`,
          componentName: 'SkyListPagingDemoComponent',
          bootstrapSelector: 'sky-list-paging-demo'
        }
      ]
    },
    {
      name: 'List provider',
      component: SkyListProviderDemoComponent,
      files: [
        {
          name: 'list-provider-demo.component.html',
          fileContents: `<sky-list [dataProvider]="listDataProvider">
  <sky-list-toolbar></sky-list-toolbar>

  <sky-list-view-grid fit="scroll">
    <sky-grid-column field="column1" heading="Column1"></sky-grid-column>
    <sky-grid-column field="column2" heading="Column2"></sky-grid-column>
    <sky-grid-column field="column3" heading="Column3"></sky-grid-column>
  </sky-list-view-grid>

  <sky-list-paging pageSize="5"></sky-list-paging>
</sky-list>
`
        },
        {
          name: 'list-provider-demo.component.ts',
          fileContents: `import {
  Component,
  Injectable
} from '@angular/core';

import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/of';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import 'rxjs/add/operator/map';

import {
  ListDataProvider,
  ListDataRequestModel,
  ListDataResponseModel,
  ListItemModel
} from '../../core';

@Injectable()
export class DemoListProvider extends ListDataProvider {
  public items: Observable<ListItemModel[]>;
  public remoteCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  constructor() {
    super();

    this.items = Observable.of([
      {
        id: '1',
        data: {
          column1: 101, column2: 'Apple', column3: 'Anne eats apples'
        }
      },
      {
        id: '2',
        data: {
          column1: 202, column2: 'Banana', column3: 'Ben eats bananas'
        }
      },
      {
        id: '3',
        data: {
          column1: 303, column2: 'Pear', column3: 'Patty eats pears'
        }
      },
      {
        id: '4',
        data: {
          column1: 404, column2: 'Grape', column3: 'George eats grapes'
        }
      },
      {
        id: '5',
        data: {
          column1: 505, column2: 'Banana', column3: 'Becky eats bananas'
        }
      },
      {
        id: '6',
        data: {
          column1: 606, column2: 'Lemon', column3: 'Larry eats lemons'
        }
      },
      {
        id: '7',
        data: {
          column1: 707, column2: 'Strawberry', column3: 'Sally eats strawberries'
        }
      }
    ]);
  }

  public get(request: ListDataRequestModel): Observable<ListDataResponseModel> {
    /*
      In get() you get data based on a given ListDataRequestModel.
      You can fetch data remotely here and return an Observable<ListDataResponseModel>.
    */
    return this.fakeHttpRequest(request);
  }

  public count(): Observable<number> {
    return this.remoteCount;
  }

  private fakeHttpRequest(request: ListDataRequestModel): Observable<ListDataResponseModel> {
    return this.items.map((items: ListItemModel[]) => {
      let searchedList = items;

      if (request.search.searchText) {
        let searchText = request.search.searchText.toLowerCase();

        searchedList = items.filter((item) => {
          return (
            item.data.column2.toLowerCase().indexOf(searchText) > -1 ||
            item.data.column3.toLowerCase().indexOf(searchText) > -1
          );
        });
      }

      let itemStart = (request.pageNumber - 1) * request.pageSize;
      let pagedResult = searchedList.slice(itemStart, itemStart + request.pageSize);

      this.remoteCount.next(searchedList.length);

      return new ListDataResponseModel({
        count: searchedList.length,
        items: pagedResult
      });
    });
  }
}

@Component({
  selector: 'sky-list-provider-demo',
  templateUrl: './list-provider-demo.component.html',
  providers: [DemoListProvider]
})
export class SkyListProviderDemoComponent {
  constructor(
    public listDataProvider: DemoListProvider
  ) { }
}
`,
          componentName: 'SkyListProviderDemoComponent',
          bootstrapSelector: 'sky-list-provider-demo'
        }
      ]
    },
    {
      name: 'List toolbar',
      component: SkyListToolbarDemoComponent,
      files: [
        {
          name: 'list-toolbar-demo.component.html',
          fileContents: `
<sky-list [data]="items" [defaultView]="grid">
  <sky-list-toolbar>
    <sky-list-secondary-actions>
      <sky-list-secondary-action>
        <button type="button">
          Option
        </button>
      </sky-list-secondary-action>
    </sky-list-secondary-actions>
    <sky-list-toolbar-sort
      label="Fruit name (A - Z)"
      field="column2"
      [descending]="false"
      type="string"
    >
    </sky-list-toolbar-sort>
    <sky-list-toolbar-sort
      label="Fruit name (Z - A)"
      field="column2"
      [descending]="true"
      type="string"
    >
    </sky-list-toolbar-sort>
  </sky-list-toolbar>

  <sky-list-view-grid fit="scroll" #grid>
    <sky-grid-column
      field="column1"
      heading="Column1"
      [isSortable]="false">
    </sky-grid-column>
    <sky-grid-column field="column2" heading="Column2">
    </sky-grid-column>
  </sky-list-view-grid>
 </sky-list>
`
        },
        {
          name: 'list-toolbar-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/of';

@Component({
  selector: 'sky-list-toolbar-demo',
  templateUrl: './list-toolbar-demo.component.html'
})
export class SkyListToolbarDemoComponent {
  public items = Observable.of([
    { id: '1', column1: 101, column2: 'Apple', column3: 'Anne eats apples' },
    { id: '2', column1: 202, column2: 'Banana', column3: 'Ben eats bananas' },
    { id: '3', column1: 303, column2: 'Pear', column3: 'Patty eats pears' },
    { id: '4', column1: 404, column2: 'Grape', column3: 'George eats grapes' },
    { id: '5', column1: 505, column2: 'Banana', column3: 'Becky eats bananas' },
    { id: '6', column1: 606, column2: 'Lemon', column3: 'Larry eats lemons' },
    { id: '7', column1: 707, column2: 'Strawberry', column3: 'Sally eats strawberries' }
  ]);
}
`,
          componentName: 'SkyListToolbarDemoComponent',
          bootstrapSelector: 'sky-list-toolbar-demo'
        }
      ]
    },
    {
      name: 'List toolbar custom',
      component: SkyListToolbarCustomDemoComponent,
      files: [
        {
          name: 'list-toolbar-custom-demo.component.html',
          fileContents: `<sky-list [data]="items" [defaultView]="grid">
  <sky-list-toolbar>
    <sky-list-toolbar-item id="custom-item" location="left">
      <ng-template><button class="sky-btn sky-btn-default">Left</button></ng-template>
    </sky-list-toolbar-item>
    <sky-list-toolbar-item id="custom-item2" location="center" index="1">
      <ng-template><button class="sky-btn sky-btn-default">Center</button></ng-template>
    </sky-list-toolbar-item>
    <sky-list-toolbar-item
      id="custom-item3"
      location="right"
      index="1">
      <ng-template>
        <button class="sky-btn sky-btn-default">Right</button>
      </ng-template>
    </sky-list-toolbar-item>
  </sky-list-toolbar>

  <sky-list-view-grid fit="scroll" #grid>
    <sky-grid-column field="column1" heading="Column1"></sky-grid-column>
    <sky-grid-column field="column2" heading="Column2"></sky-grid-column>
  </sky-list-view-grid>
</sky-list>
`
        },
        {
          name: 'list-toolbar-custom-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';
import { Observable } from 'rxjs/Observable';

@Component({
  selector: 'sky-list-toolbar-custom-demo',
  templateUrl: './list-toolbar-custom-demo.component.html'
})
export class SkyListToolbarCustomDemoComponent {
  public items: Observable<any[]> = Observable.of([
    { id: '1', column1: 101, column2: 'Apple', column3: 'Anne eats apples' },
    { id: '2', column1: 202, column2: 'Banana', column3: 'Ben eats bananas' },
    { id: '3', column1: 303, column2: 'Pear', column3: 'Patty eats pears' },
    { id: '4', column1: 404, column2: 'Grape', column3: 'George eats grapes' },
    { id: '5', column1: 505, column2: 'Banana', column3: 'Becky eats bananas' },
    { id: '6', column1: 606, column2: 'Lemon', column3: 'Larry eats lemons' },
    { id: '7', column1: 707, column2: 'Strawberry', column3: 'Sally eats strawberries' }
  ]);
}
`,
          componentName: 'SkyListToolbarCustomDemoComponent',
          bootstrapSelector: 'sky-list-toolbar-custom-demo'
        }
      ]
    },
    {
      name: 'List view checklist',
      component: SkyListViewChecklistDemoComponent,
      files: [
        {
          name: 'list-view-checklist-demo.component.html',
          fileContents: `<div style="margin-bottom: 10px;">
  <h3>
    Selection mode
  </h3>
  <div>
    <sky-radio
      name="selectMode"
      [(ngModel)]="selectMode"
      value="multiple">
      <sky-radio-label>
        Default select mode
      </sky-radio-label>
    </sky-radio>
  </div>
  <div>
    <sky-radio
      name="selectMode"
      [(ngModel)]="selectMode"
      value="single">
      <sky-radio-label>
        Single select mode
      </sky-radio-label>
    </sky-radio>
  </div>
</div>

<sky-list
  [data]="items"
  (selectedIdsChange)="selectedItemsChange($event)">
  <sky-list-toolbar>
  </sky-list-toolbar>
  <sky-list-view-checklist
    label="column2"
    description="column3"
    [selectMode]="selectMode">
  </sky-list-view-checklist>
</sky-list>
<div>
  <label>Selected items:</label>
  <ul>
    <li *ngFor="let item of selectedItems">
      {{item.column2}}
    </li>
  </ul>
</div>
`
        },
        {
          name: 'list-view-checklist-demo.component.ts',
          fileContents: `import {
  Component
} from '@angular/core';

import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/take';

@Component({
  selector: 'sky-list-view-checklist-demo',
  templateUrl: './list-view-checklist-demo.component.html'
})
export class SkyListViewChecklistDemoComponent {
  public items = Observable.of([
    { id: '1', column1: 101, column2: 'Apple', column3: 'Anne eats apples'},
    { id: '2', column1: 202, column2: 'Banana', column3: 'Ben eats bananas' },
    { id: '3', column1: 303, column2: 'Pear', column3: 'Patty eats pears' },
    { id: '4', column1: 404, column2: 'Grape', column3: 'George eats grapes' },
    { id: '5', column1: 505, column2: 'Banana', column3: 'Becky eats bananas' },
    { id: '6', column1: 606, column2: 'Lemon', column3: 'Larry eats lemons' },
    { id: '7', column1: 707, column2: 'Strawberry', column3: 'Sally eats strawberries' }
  ]);

  public selectedItems: any[] = [];
  public selectMode = 'multiple';

  public selectedItemsChange(selectedMap: Map<string, boolean>) {
    this.items.take(1).subscribe((items) => {
      this.selectedItems = items.filter((item) => {
        return selectedMap.get(item.id);
      });
    });
  }
}
`,
          componentName: 'SkyListViewChecklistDemoComponent',
          bootstrapSelector: 'sky-list-view-checklist-demo'
        }
      ]
    },
    {
      name: 'List view grid',
      component: SkyListViewGridDemoComponent,
      files: [
        {
          name: 'list-view-grid-demo.component.html',
          fileContents: `<sky-list [data]="items" [defaultView]="grid">
  <sky-list-toolbar>
    <sky-list-secondary-actions>
      <sky-list-column-selector-action [gridView]="grid">
      </sky-list-column-selector-action>
    </sky-list-secondary-actions>
  </sky-list-toolbar>

  <sky-list-view-grid fit="scroll" #grid>
    <sky-grid-column
      field="control"
      [locked]="true"
      [template]="customCellTemplate">
    </sky-grid-column>
    <sky-grid-column
      field="column1"
      [locked]="true"
      [heading]="asyncHeading | async"
      description="A numbered column">
    </sky-grid-column>
    <sky-grid-column
      field="column2"
      heading="Column2"
      description="A fruit column">
    </sky-grid-column>
    <sky-grid-column
      field="column3"
      heading="Column3"
      description="A sentence column">
    </sky-grid-column>
  </sky-list-view-grid>
</sky-list>

<ng-template
  #customCellTemplate
  let-row="row">
  <sky-dropdown buttonType="context-menu">
    <sky-dropdown-menu>
      <sky-dropdown-item>
        <button type="button">
          Option 1
        </button>
      </sky-dropdown-item>
      <sky-dropdown-item>
        <button type="button">
          Option 2
        </button>
      </sky-dropdown-item>
      <sky-dropdown-item>
        <button type="button">
          Option 3
        </button>
      </sky-dropdown-item>
    </sky-dropdown-menu>
  </sky-dropdown>
</ng-template>
`
        },
        {
          name: 'list-view-grid-demo.component.ts',
          fileContents: `import {
  Component,
  OnInit
} from '@angular/core';

import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/of';

@Component({
  selector: 'sky-list-view-grid-demo',
  templateUrl: './list-view-grid-demo.component.html'
})
export class SkyListViewGridDemoComponent implements OnInit {
  public items = Observable.of([
    { id: '1', column1: 101, column2: 'Apple', column3: 'Anne eats apples' },
    { id: '2', column1: 202, column2: 'Banana', column3: 'Ben eats bananas' },
    { id: '3', column1: 303, column2: 'Pear', column3: 'Patty eats pears' },
    { id: '4', column1: 404, column2: 'Grape', column3: 'George eats grapes' },
    { id: '5', column1: 505, column2: 'Banana', column3: 'Becky eats bananas' },
    { id: '6', column1: 606, column2: 'Lemon', column3: 'Larry eats lemons' },
    { id: '7', column1: 707, column2: 'Strawberry', column3: 'Sally eats strawberries' }
  ]);

  public asyncHeading = new BehaviorSubject<string>('');

  public ngOnInit() {
    // Simulate async request:
    setTimeout(() => {
      this.asyncHeading.next('Column1');
    }, 1000);
  }
}
`,
          componentName: 'SkyListViewGridDemoComponent',
          bootstrapSelector: 'sky-list-view-grid-demo'
        }
      ]
    },
    {
      name: 'Lookup',
      component: SkyLookupDemoComponent,
      files: [
        {
          name: 'lookup-demo.component.html',
          fileContents: `<form
  class="lookup-demo-form"
  [formGroup]="reactiveForm"
  (ngSubmit)="submitReactiveForm()"
  novalidate>

  <div class="sky-form-group">
    <label
      id="my-friends-label"
      class="sky-control-label">
      Who are your friends?
    </label>
    <sky-lookup
      ariaLabelledBy="my-friends-label"
      formControlName="friends"
      placeholderText="Type a person's name..."
      [data]="people"
      [searchFilters]="getSearchFilters()">
    </sky-lookup>
  </div>

  <p>
    <button
      type="button"
      class="sky-btn sky-btn-default"
      (click)="disableLookup()">
      Disable lookup field
    </button>

    <button
      type="button"
      class="sky-btn sky-btn-default"
      (click)="enableLookup()">
      Enable lookup field
    </button>
  </p>

  <div class="lookup-demo-alert">
    <strong>Form model:</strong>
    <pre>{{ reactiveForm.value | json }}</pre>
  </div>

  <button
    class="sky-btn sky-btn-default"
    type="submit">
    Submit
  </button>

</form>
`
        },
        {
          name: 'lookup-demo.component.ts',
          fileContents: `import {
  Component,
  OnInit
} from '@angular/core';

import {
  FormBuilder,
  FormControl,
  FormGroup
} from '@angular/forms';

import {
  SkyAutocompleteSearchFunctionFilter
} from '../../core';

@Component({
  selector: 'sky-lookup-demo',
  templateUrl: './lookup-demo.component.html',
  styleUrls: ['./lookup-demo.component.scss']
})
export class SkyLookupDemoComponent implements OnInit {
  public reactiveForm: FormGroup;

  public people: any[] = [
    { name: 'Andy' },
    { name: 'Beth' },
    { name: 'David' },
    { name: 'Frank' },
    { name: 'Grace' },
    { name: 'Isaac' },
    { name: 'John' },
    { name: 'Joyce' },
    { name: 'Lindsey' },
    { name: 'Mitch' },
    { name: 'Patty' },
    { name: 'Paul' },
    { name: 'Quincy' },
    { name: 'Sally' },
    { name: 'Susan' },
    { name: 'Vanessa' },
    { name: 'Winston' },
    { name: 'Xavier' },
    { name: 'Yolanda' },
    { name: 'Zack' }
  ];

  public friends: any[] = [
    { name: 'Susan' }
  ];

  constructor(
    private formBuilder: FormBuilder
  ) { }

  public ngOnInit(): void {
    this.createForm();

    // If you need to execute some logic after the lookup values change,
    // subscribe to Angular's built-in value changes observable.
    this.reactiveForm.valueChanges.subscribe(changes => {
      console.log('Lookup value changes:', changes);
    });
  }

  // Only show people in the search results that have not been chosen already.
  public getSearchFilters(): SkyAutocompleteSearchFunctionFilter[] {
    const friends: any[] = this.reactiveForm.controls.friends.value;
    return [
      (searchText: string, item: any): boolean => {
        const found = friends.find(friend => friend.name === item.name);
        return !found;
      }
    ];
  }

  public enableLookup() {
    this.reactiveForm.controls.friends.enable();
  }

  public disableLookup() {
    this.reactiveForm.controls.friends.disable();
  }

  public submitReactiveForm() {
    alert('Form submitted with: ' + JSON.stringify(this.reactiveForm.value));
  }

  private createForm(): void {
    this.reactiveForm = this.formBuilder.group({
      friends: new FormControl(this.friends)
    });
  }
}
`,
          componentName: 'SkyLookupDemoComponent',
          bootstrapSelector: 'sky-lookup-demo'
        }
      ]
    },
    {
      name: 'Media query',
      component: SkyMediaQueryDemoComponent,
      files: [
        {
          name: 'media-query-demo.component.html',
          fileContents: `<sky-alert alertType="info">
  Current media breakpoint: <strong>{{currentBreakpoint}}</strong>
</sky-alert>
`
        },
        {
          name: 'media-query-demo.component.ts',
          fileContents: `import {
  Component,
  OnDestroy
} from '@angular/core';

import {
  Subscription
} from 'rxjs/Subscription';

import {
  SkyMediaQueryService,
  SkyMediaBreakpoints
} from '../../core';

@Component({
  selector: 'sky-media-query-demo',
  templateUrl: './media-query-demo.component.html'
})
export class SkyMediaQueryDemoComponent implements OnDestroy {
  public currentBreakpoint: string;

  private querySubscription: Subscription;

  constructor(private mediaQueries: SkyMediaQueryService) {
    this.querySubscription = this.mediaQueries.subscribe((newBreakpoint: SkyMediaBreakpoints) => {
      switch (newBreakpoint) {
        case SkyMediaBreakpoints.xs:
          this.currentBreakpoint = 'xs';
          break;
        case SkyMediaBreakpoints.sm:
          this.currentBreakpoint = 'sm';
          break;
        case SkyMediaBreakpoints.md:
          this.currentBreakpoint = 'md';
          break;
        case SkyMediaBreakpoints.lg:
          this.currentBreakpoint = 'lg';
          break;
        default:
          this.currentBreakpoint = 'unknown';
      }
    });
  }

  public ngOnDestroy() {
    /* istanbul ignore else */
    /* sanity check */
    if (this.querySubscription) {
      this.querySubscription.unsubscribe();
    }
  }
}
`,
          componentName: 'SkyMediaQueryDemoComponent',
          bootstrapSelector: 'sky-media-query-demo'
        }
      ]
    },
    {
      name: 'Modal',
      component: SkyModalDemoComponent,
      files: [
        {
          name: 'modal-demo.component.html',
          fileContents: `<button type="button" class="sky-btn sky-btn-default" (click)="openModal('defaultModal')">
  Open modal
</button>

<button type="button" class="sky-btn sky-btn-default" (click)="openModal('smallModal')">
  Open small modal
</button>

<button type="button" class="sky-btn sky-btn-default" (click)="openModal('largeModal')">
  Open large modal
</button>

<button type="button" class="sky-btn sky-btn-default" (click)="openModal('fullScreenModal')">
 Open full-screen modal
</button>

<button type="button" class="sky-btn sky-btn-default" (click)="openModal('tiledModal')">
  Open tiled modal
</button>

<button type="button" class="sky-btn sky-btn-default" (click)="openModal('withHelpHeader')">
  Open modal with help header
</button>
`
        },
        {
          name: 'modal-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

import {
  SkyModalService,
  SkyModalCloseArgs
} from '../../core';

import { SkyModalDemoContext } from './modal-demo-context';
import { SkyModalDemoFormComponent } from './modal-demo-form.component';
import { SkyModalDemoTiledFormComponent } from './modal-demo-tiled-form.component';

@Component({
  selector: 'sky-modal-demo',
  templateUrl: './modal-demo.component.html'
})
export class SkyModalDemoComponent {
  constructor(private modal: SkyModalService) { }

  public openModal(type: string) {
    const context = new SkyModalDemoContext();
    context.valueA = 'Hello';

    const options: any = {
      providers: [{ provide: SkyModalDemoContext, useValue: context }],
      ariaDescribedBy: 'docs-modal-content'
    };

    let modalInstanceType = SkyModalDemoFormComponent;

    switch (type) {
      case 'fullScreenModal':
      options.fullPage = true;
      break;
      case 'smallModal':
      options.size = 'small';
      break;
      case 'largeModal':
      options.size = 'large';
      break;
      case 'tiledModal':
      modalInstanceType = SkyModalDemoTiledFormComponent;
      break;
      case 'withHelpHeader':
      options.helpKey = 'demo-key.html';
      break;
      default:
      break;
    }

    const modalInstance = this.modal.open(modalInstanceType, options);

    modalInstance.closed.subscribe((result: SkyModalCloseArgs) => {
      console.log(\`Modal closed with reason: \${result.reason} and data: \${result.data}\`);
    });

    modalInstance.helpOpened.subscribe((helpKey: string) => {
      context.eventMessage =  \`
        Modal header help was invoked with the following help key: \${helpKey}
      \`;
    });
  }
}
`,
          componentName: 'SkyModalDemoComponent',
          bootstrapSelector: 'sky-modal-demo'
        },
        {
          name: 'modal-demo-form.component.html',
          fileContents: `<sky-modal>
  <sky-modal-header>
    {{title}}
  </sky-modal-header>
  <sky-modal-content>
    <div id="docs-modal-content">
      This modal can have content!
    </div>
    <label>
      Value A
      <input type="text" [(ngModel)]="context.valueA">
    </label>
    <p>
      <sky-alert *ngIf="context.eventMessage">
        {{context.eventMessage}}
      </sky-alert>
    </p>
  </sky-modal-content>
  <sky-modal-footer>
    <button
      type="button"
      class="sky-btn sky-btn-primary"
      (click)="instance.save('Something cool')">
      Close
    </button>
  </sky-modal-footer>
</sky-modal>
`
        },
        {
          name: 'modal-demo-form.component.ts',
          fileContents: `import { Component } from '@angular/core';

import { SkyModalInstance } from '../../core';

import { SkyModalDemoContext } from './modal-demo-context';

@Component({
  selector: 'sky-demo-modal-form',
  templateUrl: './modal-demo-form.component.html'
})
export class SkyModalDemoFormComponent {
  public title = 'Hello world';

  constructor(
    public context: SkyModalDemoContext,
    public instance: SkyModalInstance
  ) { }
}
`,
          componentName: 'SkyModalDemoFormComponent'
        },
        {
          name: 'modal-demo-tiled-form.component.html',
          fileContents: `<sky-modal [tiledBody]="true">
  <sky-modal-header>
    {{title}}
  </sky-modal-header>
  <sky-modal-content>
    <sky-tile>
      <sky-tile-title>Section 1</sky-tile-title>
      <sky-tile-content>
        <sky-tile-content-section>
          Content 1
        </sky-tile-content-section>
      </sky-tile-content>
    </sky-tile>
    <sky-tile>
      <sky-tile-title>Section 2</sky-tile-title>
      <sky-tile-content>
        <sky-tile-content-section>
          Content 2
        </sky-tile-content-section>
      </sky-tile-content>
    </sky-tile>
  </sky-modal-content>
  <sky-modal-footer>
    <button
      type="button"
      class="sky-btn sky-btn-primary"
      (click)="instance.save('Something cool')"
    >
      Close
    </button>
  </sky-modal-footer>
</sky-modal>
`
        },
        {
          name: 'modal-demo-tiled-form.component.ts',
          fileContents: `import { Component } from '@angular/core';

import { SkyModalInstance } from '../../core';

import { SkyModalDemoContext } from './modal-demo-context';

@Component({
  selector: 'sky-demo-modal-tiled-form',
  templateUrl: './modal-demo-tiled-form.component.html'
})
export class SkyModalDemoTiledFormComponent {
  public title = 'Hello world';

  constructor(
    public context: SkyModalDemoContext,
    public instance: SkyModalInstance
  ) { }
}
`,
          componentName: 'SkyModalDemoTiledFormComponent'
        },
        {
          name: 'modal-demo-context.ts',
          fileContents: `export class SkyModalDemoContext {
  public valueA: string;
  public eventMessage?: string;
}
`
        }
      ]
    },
    {
      name: 'Navbar',
      component: SkyNavbarDemoComponent,
      files: [
        {
          name: 'navbar-demo.component.html',
          fileContents: `<sky-navbar>
  <sky-navbar-item>
    <a routerLink="/" routerLinkActive="sky-navbar-item-active">
      Selected item
    </a>
  </sky-navbar-item>
  <sky-navbar-item>
    <sky-dropdown>
      <sky-dropdown-button>
        Show dropdown
      </sky-dropdown-button>
      <sky-dropdown-menu>
        <sky-dropdown-item>
          <a routerLink="/">Item 1</a>
        </sky-dropdown-item>
        <sky-dropdown-item>
          <a routerLink="/">Item 2</a>
        </sky-dropdown-item>
        <sky-dropdown-item>
          <a routerLink="/">Item 3</a>
        </sky-dropdown-item>
      </sky-dropdown-menu>
    </sky-dropdown>
  </sky-navbar-item>
</sky-navbar>
`
        },
        {
          name: 'navbar-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

@Component({
  selector: 'sky-navbar-demo',
  templateUrl: './navbar-demo.component.html'
})
export class SkyNavbarDemoComponent { }
`,
          componentName: 'SkyNavbarDemoComponent',
          bootstrapSelector: 'sky-navbar-demo'
        }
      ]
    },
    {
      name: 'Numeric',
      component: SkyNumericDemoComponent,
      files: [
        {
          name: 'numeric-demo.component.html',
          fileContents: `<sky-definition-list labelWidth="200px">
  <sky-definition-list-heading>
    This is a sample of numeric formatting.
  </sky-definition-list-heading>
  <sky-definition-list-content>
    <p>
      <sky-definition-list-label>
        1 with zero digits:
      </sky-definition-list-label>
      <sky-definition-list-value>
        {{1 | skyNumeric:{digits: 0} }}
      </sky-definition-list-value>
    </p>
    <p>
      <sky-definition-list-label>
        1000 with zero digits:
      </sky-definition-list-label>
      <sky-definition-list-value>
        {{1000 | skyNumeric:{digits: 0} }}
      </sky-definition-list-value>
    </p>
    <p>
      <sky-definition-list-label>
        1000 with one digit:
      </sky-definition-list-label>
      <sky-definition-list-value>
        {{1000 | skyNumeric}}
      </sky-definition-list-value>
    </p>
    <p>
      <sky-definition-list-label>
        1100 with zero digits:
      </sky-definition-list-label>
      <sky-definition-list-value>
        {{1100 | skyNumeric:{digits: 0} }}
      </sky-definition-list-value>
    </p>
    <p>
      <sky-definition-list-label>
        1100 with one digit:
      </sky-definition-list-label>
      <sky-definition-list-value>
        {{1100 | skyNumeric}}
      </sky-definition-list-value>
    </p>
    <p>
      <sky-definition-list-label>
        1500 with zero digits:
      </sky-definition-list-label>
      <sky-definition-list-value>
        {{1500 | skyNumeric:{digits: 0} }}
      </sky-definition-list-value>
    </p>
    <p>
      <sky-definition-list-label>
        1500 with one digit:
      </sky-definition-list-label>
      <sky-definition-list-value>
        {{1500 | skyNumeric}}
      </sky-definition-list-value>
    </p>
    <p>
      <sky-definition-list-label>
        1000000 with any digits:
      </sky-definition-list-label>
      <sky-definition-list-value>
        {{1000000 | skyNumeric:{digits: 0} }}
      </sky-definition-list-value>
    </p>
    <p>
      <sky-definition-list-label>
        1000000000 with any digits:
      </sky-definition-list-label>
      <sky-definition-list-value>
        {{1000000000 | skyNumeric:{digits: 0} }}
      </sky-definition-list-value>
    </p>
    <p>
      <sky-definition-list-label>
        1000000000000 with any digits:
      </sky-definition-list-label>
      <sky-definition-list-value>
        {{1000000000000 | skyNumeric:{digits: 0} }}
      </sky-definition-list-value>
    </p>
    <p>
      <sky-definition-list-label>
        1234000 with 2 digits:
      </sky-definition-list-label>
      <sky-definition-list-value>
        {{1234000 | skyNumeric:{digits: 2} }}
      </sky-definition-list-value>
    </p>
    <p>
      <sky-definition-list-label>
        1235000000 with 2 digits:
      </sky-definition-list-label>
      <sky-definition-list-value>
        {{1235000000 | skyNumeric:{digits: 2} }}
      </sky-definition-list-value>
    </p>
    <p>
      <sky-definition-list-label>
        1.45 with 1 digit:
      </sky-definition-list-label>
      <sky-definition-list-value>
        {{1.45 | skyNumeric}}
      </sky-definition-list-value>
    </p>
    <p>
      <sky-definition-list-label>
        1234567 with 1 digit:
      </sky-definition-list-label>
      <sky-definition-list-value>
        {{1234567 | skyNumeric}}
      </sky-definition-list-value>
    </p>
    <p>
      <sky-definition-list-label>
        15.50 as US dollar currency with 2 digits:
      </sky-definition-list-label>
      <sky-definition-list-value>
        {{15.50 | skyNumeric:{digits: 2, format: 'currency', iso: 'USD'} }}
      </sky-definition-list-value>
    </p>
    <p>
      <sky-definition-list-label>
        -15.50 as US dollar currency with 2 digits:
      </sky-definition-list-label>
      <sky-definition-list-value>
        {{-15.50 | skyNumeric:{digits: 2, format: 'currency', iso: 'USD'} }}
      </sky-definition-list-value>
    </p>
    <p>
      <sky-definition-list-label>
        1234567 as Euro currency with 2 digits:
      </sky-definition-list-label>
      <sky-definition-list-value>
        {{1234567 | skyNumeric:{digits: 2, format: 'currency', iso: 'EUR'} }}
      </sky-definition-list-value>
    </p>
    <p>
      <sky-definition-list-label>
        1450 as UK Pound currency with 2 digits:
      </sky-definition-list-label>
      <sky-definition-list-value>
        {{1450 | skyNumeric:{digits: 2, format: 'currency', iso: 'GBP'} }}
      </sky-definition-list-value>
    </p>
    <p>
      <sky-definition-list-label>
        1750 as Hong Kong Dollar currency with 1 digit:
      </sky-definition-list-label>
      <sky-definition-list-value>
        {{1750 | skyNumeric:{digits: 1, format: 'currency', iso: 'HKD'} }}
      </sky-definition-list-value>
    </p>
    <p>
      <sky-definition-list-label>
        1234567 as Japanese Yen currency with 3 digit:
      </sky-definition-list-label>
      <sky-definition-list-value>
        {{1234567 | skyNumeric:{digits: 3, format: 'currency', iso: 'JPY'} }}
      </sky-definition-list-value>
    </p>
  </sky-definition-list-content>
</sky-definition-list>
`
        },
        {
          name: 'numeric-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';
import { CurrencyPipe } from '@angular/common';

@Component({
  selector: 'sky-numeric-demo',
  templateUrl: './numeric-demo.component.html',
  providers: [CurrencyPipe]
})
export class SkyNumericDemoComponent { }
`,
          componentName: 'SkyNumericDemoComponent',
          bootstrapSelector: 'sky-numeric-demo'
        }
      ]
    },
    {
      name: 'Page summary',
      component: SkyPageSummaryDemoComponent,
      files: [
        {
          name: 'page-summary-demo.component.html',
          fileContents: `<div style="border: solid 1px #ccc">
  <sky-page-summary>
    <sky-page-summary-alert *ngIf="showAlert">
      <sky-alert alertType="info">This is an alert.</sky-alert>
    </sky-page-summary-alert>
    <sky-page-summary-image *ngIf="showImage">
      <sky-avatar [name]="name" [canChange]="true"></sky-avatar>
    </sky-page-summary-image>
    <sky-page-summary-title *ngIf="showTitle">
      {{name}}
    </sky-page-summary-title>
    <sky-page-summary-subtitle *ngIf="showSubtitle">
      Board member
    </sky-page-summary-subtitle>
    <sky-page-summary-status *ngIf="showStatus">
      <sky-label labelType="success">Fundraiser</sky-label>
      <sky-label>Inactive</sky-label>
    </sky-page-summary-status>
    <sky-page-summary-content *ngIf="showContent">
      This is the arbitrary content section. You can display any kind of content to complement the
      content on a page. We recommend that you display content to support the key tasks of users of
      users who visit the page. We also recommend that you keep in mind the context of how users
      will use the content and limit the amount of content to avoid overloading the summary.
    </sky-page-summary-content>
    <sky-page-summary-key-info *ngIf="showKeyInfo">
      <sky-key-info>
        <sky-key-info-value>The key information</sky-key-info-value>
      </sky-key-info>
      <sky-key-info>
        <sky-key-info-value>section highlights</sky-key-info-value>
      </sky-key-info>
      <sky-key-info>
        <sky-key-info-value>important details</sky-key-info-value>
      </sky-key-info>
    </sky-page-summary-key-info>
  </sky-page-summary>
</div>

<ul class="sky-list-unstyled">
  <li>
    <sky-checkbox [(ngModel)]="showTitle">
      <sky-checkbox-label>Show title</sky-checkbox-label>
    </sky-checkbox>
  </li>
  <li>
    <sky-checkbox [(ngModel)]="showSubtitle">
      <sky-checkbox-label>Show subtitle</sky-checkbox-label>
    </sky-checkbox>
  </li>
  <li>
    <sky-checkbox [(ngModel)]="showImage">
      <sky-checkbox-label>Show image</sky-checkbox-label>
    </sky-checkbox>
  </li>
  <li>
    <sky-checkbox [(ngModel)]="showStatus">
      <sky-checkbox-label>Show record status</sky-checkbox-label>
    </sky-checkbox>
  </li>
  <li>
    <sky-checkbox [(ngModel)]="showKeyInfo">
      <sky-checkbox-label>Show key information</sky-checkbox-label>
    </sky-checkbox>
  </li>
  <li>
    <sky-checkbox [(ngModel)]="showContent">
      <sky-checkbox-label>Show arbitrary content</sky-checkbox-label>
    </sky-checkbox>
  </li>
  <li>
    <sky-checkbox [(ngModel)]="showAlert">
      <sky-checkbox-label>Show alert</sky-checkbox-label>
    </sky-checkbox>
  </li>
</ul>
`
        },
        {
          name: 'page-summary-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

@Component({
  selector: 'sky-page-summary-demo',
  templateUrl: './page-summary-demo.component.html'
})
export class SkyPageSummaryDemoComponent {
  public name = 'Robert C. Hernandez';
  public showAlert = true;
  public showImage = true;
  public showTitle = true;
  public showSubtitle = true;
  public showStatus = true;
  public showContent = true;
  public showKeyInfo = true;
}
`,
          componentName: 'SkyPageSummaryDemoComponent',
          bootstrapSelector: 'sky-page-summary-demo'
        }
      ]
    },
    {
      name: 'Paging',
      component: SkyPagingDemoComponent,
      files: [
        {
          name: 'paging-demo.component.html',
          fileContents: `<div>
  <sky-paging
    pageSize="2"
    maxPages="3"
    [(currentPage)]="currentPage"
    itemCount="8">
  </sky-paging>
  <span>Current page is {{currentPage}}</span>
</div>
`
        },
        {
          name: 'paging-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

@Component({
  selector: 'sky-paging-demo',
  templateUrl: './paging-demo.component.html'
})
export class SkyPagingDemoComponent {
  public currentPage = 1;
}
`,
          componentName: 'SkyPagingDemoComponent',
          bootstrapSelector: 'sky-paging-demo'
        }
      ]
    },
    {
      name: 'Popover',
      component: SkyPopoverDemoComponent,
      files: [
        {
          name: 'popover-demo.component.html',
          fileContents: `<div class="popover-demo-container">
  <div class="popover-demo-item">
    <sky-popover
      placement="right">
      Right
    </sky-popover>
  </div>
  <div class="popover-demo-item">
    <sky-popover
      placement="left">
      Left
    </sky-popover>
  </div>
  <div class="popover-demo-item">
    <sky-popover
      placement="above">
      Above (default)
    </sky-popover>
  </div>
  <div class="popover-demo-item">
    <sky-popover
      placement="above"
      alignment="left">
      Above, align left
    </sky-popover>
  </div>
  <div class="popover-demo-item">
    <sky-popover
      placement="above"
      alignment="right">
      Above, align right
    </sky-popover>
  </div>
  <div class="popover-demo-item">
    <sky-popover
      placement="below">
      Below
    </sky-popover>
  </div>
  <div class="popover-demo-item">
    <sky-popover
      placement="below"
      alignment="left">
      Below, align left
    </sky-popover>
  </div>
  <div class="popover-demo-item">
    <sky-popover
      placement="below"
      alignment="right">
      Below, align right
    </sky-popover>
  </div>
</div>

<h3>
  With titles:
</h3>

<div class="popover-demo-container">
  <div class="popover-demo-item">
    <sky-popover placement="above" popoverTitle="Did you know?">
      The content of a popover can be text, HTML, or Angular components.
    </sky-popover>
  </div>
  <div class="popover-demo-item">
    <sky-popover placement="below" popoverTitle="Did you know?">
      The content of a popover can be text, HTML, or Angular components.
    </sky-popover>
  </div>
  <div class="popover-demo-item">
    <sky-popover placement="right" popoverTitle="Did you know?">
      The content of a popover can be text, HTML, or Angular components.
    </sky-popover>
  </div>
  <div class="popover-demo-item">
    <sky-popover placement="left" popoverTitle="Did you know?">
      The content of a popover can be text, HTML, or Angular components.
    </sky-popover>
  </div>
</div>

<h3>
  Popovers can be opened via buttons:
</h3>

<button
  class="sky-btn"
  type="button"
  [skyPopover]="myPopover0">
  Default
</button>

<sky-popover #myPopover0>
  The content of a popover can be text, HTML, or Angular components.<br>
  <sky-dropdown buttonType="context-menu">
    <sky-dropdown-menu>
      <sky-dropdown-item>
        <button type="button">
          Option 1
        </button>
      </sky-dropdown-item>
      <sky-dropdown-item>
        <button type="button">
          Option 2
        </button>
      </sky-dropdown-item>
      <sky-dropdown-item>
        <button type="button">
          Option 3
        </button>
      </sky-dropdown-item>
    </sky-dropdown-menu>
  </sky-dropdown>
</sky-popover>

<button
  class="sky-btn"
  type="button"
  [skyPopover]="myPopover1"
  skyPopoverPlacement="right">
  Right
</button>

<sky-popover #myPopover1>
  The content of a popover can be text, HTML, or Angular components.
  <a href="">Some link</a>
</sky-popover>

<button
  class="sky-btn"
  type="button"
  [skyPopover]="myPopover2"
  skyPopoverPlacement="above">
  Above
</button>

<sky-popover #myPopover2>
  The content of a popover can be text, HTML, or Angular components.
  <a href="">Some link</a>
</sky-popover>

<button
  class="sky-btn"
  type="button"
  [skyPopover]="myPopover2a"
  skyPopoverPlacement="above"
  skyPopoverAlignment="left">
  Above left
</button>

<sky-popover #myPopover2a>
  The content of a popover can be text, HTML, or Angular components.
</sky-popover>

<button
  class="sky-btn"
  type="button"
  [skyPopover]="myPopover2b"
  skyPopoverPlacement="above"
  skyPopoverAlignment="right">
  Above right
</button>

<sky-popover #myPopover2b>
  The content of a popover can be text, HTML, or Angular components.
</sky-popover>

<button
  class="sky-btn"
  type="button"
  [skyPopover]="myPopover3"
  skyPopoverPlacement="below">
  Below
</button>

<sky-popover #myPopover3>
  The content of a popover can be text, HTML, or Angular components.
</sky-popover>

<button
  class="sky-btn"
  type="button"
  [skyPopover]="myPopover3a"
  skyPopoverPlacement="below"
  skyPopoverAlignment="left">
  Below left
</button>

<sky-popover #myPopover3a>
  The content of a popover can be text, HTML, or Angular components.
</sky-popover>

<button
  class="sky-btn"
  type="button"
  [skyPopover]="myPopover3b"
  skyPopoverPlacement="below"
  skyPopoverAlignment="right">
  Below right
</button>

<sky-popover #myPopover3b>
  The content of a popover can be text, HTML, or Angular components.
</sky-popover>

<button
  class="sky-btn"
  type="button"
  [skyPopover]="myPopover4"
  skyPopoverPlacement="left">
  Left
</button>

<sky-popover #myPopover4>
  The content of a popover can be text, HTML, or Angular components.
  <a href="">Some link</a>
</sky-popover>

<h3>
  Or, via links:
</h3>

<p>
  <a
    href="javascript:void"
    style="cursor:help;"
    [skyPopover]="myPopover5"
    skyPopoverPlacement="right">
    Did you know?
  </a>
</p>

<sky-popover #myPopover5>
  The content of a popover can be text, HTML, or Angular components.
</sky-popover>

<h3>
  Popovers can also be opened when the mouse hovers over an element:
</h3>

<button
  class="sky-btn"
  [skyPopover]="popoverMouseEnter"
  skyPopoverPlacement="right"
  skyPopoverTrigger="mouseenter">
  Hover
</button>

<sky-popover
  popoverTitle="Did you know?"
  #popoverMouseEnter>
  You can move your mouse off the button to close this popover.
</sky-popover>

<h3>
  Events
</h3>

<button class="sky-btn" [skyPopover]="myPopoverOnOpened">
  Say something when opened
</button>

<sky-popover
  popoverTitle="Did you know?"
  (popoverOpened)="onPopoverOpened($event)"
  #myPopoverOnOpened>
  The content of a popover can be text, HTML, or Angular components.
</sky-popover>

<button class="sky-btn" [skyPopover]="myPopoverOnClosed">
  Say something when closed
</button>

<sky-popover
  popoverTitle="Did you know?"
  (popoverClosed)="onPopoverClosed($event)"
  #myPopoverOnClosed>
  The content of a popover can be text, HTML, or Angular components.<br><br>

  <sky-dropdown buttonType="context-menu">
    <sky-dropdown-menu>
      <sky-dropdown-item>
        <button type="button">
          Option 1
        </button>
      </sky-dropdown-item>
      <sky-dropdown-item>
        <button type="button">
          Option 2
        </button>
      </sky-dropdown-item>
      <sky-dropdown-item>
        <button type="button">
          Option 3
        </button>
      </sky-dropdown-item>
    </sky-dropdown-menu>
  </sky-dropdown>
</sky-popover>

<h3>
  Popovers with large amounts of content are displayed fullscreen
</h3>

<button
  class="sky-btn"
  [skyPopover]="popoverFullscreen"
  skyPopoverPlacement="fullscreen">
  Show fullscreen popover
</button>

<sky-popover
  popoverTitle="This is a fullscreen popover"
  #popoverFullscreen>
  <p>
    Lorem ipsum dolor sit amet, consectetur adipiscing elit. Curabitur posuere placerat mollis. Orci varius natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Praesent accumsan lacus sed volutpat pharetra. Etiam convallis elit ac odio rhoncus molestie. Pellentesque consequat porttitor magna sit amet consectetur. Donec sed accumsan urna, in dapibus erat. Orci varius natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Etiam auctor justo a mattis euismod. Etiam porttitor turpis quis tellus laoreet, quis eleifend diam mollis. In eget risus nulla. Nulla a elit eu lectus laoreet lobortis. Proin rhoncus libero dictum augue consequat lobortis. Vestibulum suscipit dapibus dolor sit amet facilisis. Pellentesque ullamcorper, libero id fringilla tempus, mi orci vestibulum eros, ac pulvinar nibh turpis ac nulla.
  </p>
  <p>
    Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas. Nullam cursus arcu magna, at aliquet arcu pellentesque ac. Donec quis elit quis sapien tristique tincidunt. Mauris vel suscipit felis. Curabitur vestibulum vitae ipsum non tincidunt. Integer mollis congue ante. Nulla non vulputate nunc. Aenean eget est et libero venenatis faucibus. In hac habitasse platea dictumst. Suspendisse potenti. Donec interdum, tellus eu finibus auctor, felis turpis pulvinar nibh, quis gravida quam diam at risus. Integer porttitor ipsum eget vehicula vulputate.
  </p>
  <p>
    Etiam eget scelerisque tellus. Suspendisse vehicula sapien et felis dapibus varius. Orci varius natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Proin tincidunt suscipit nulla. Fusce ullamcorper malesuada lacus vel venenatis. Morbi venenatis mi id diam lacinia facilisis. In nec ipsum nec tortor fringilla sodales at in sapien. Nam accumsan turpis augue, in finibus ligula congue a. Nunc lobortis sem eget libero eleifend consectetur. In euismod maximus aliquam. Maecenas placerat scelerisque cursus. Fusce elementum malesuada sem in elementum. Phasellus congue mi et leo gravida tincidunt.
  </p>
  <p>
    Proin suscipit nulla tellus, ut lobortis ipsum porttitor eu. Sed pulvinar quam porttitor ligula ornare viverra. Nulla vitae metus lobortis, sagittis mauris ac, gravida diam. Fusce tincidunt massa a odio euismod maximus. Mauris eget dignissim justo. Proin quis nunc est. Duis non blandit ligula.
  </p>
  <sky-dropdown buttonType="context-menu">
    <sky-dropdown-menu>
      <sky-dropdown-item>
        <button type="button">
          Option 1
        </button>
      </sky-dropdown-item>
      <sky-dropdown-item>
        <button type="button">
          Option 2
        </button>
      </sky-dropdown-item>
      <sky-dropdown-item>
        <button type="button">
          Option 3
        </button>
      </sky-dropdown-item>
    </sky-dropdown-menu>
  </sky-dropdown>
  <p>
    Lorem ipsum dolor sit amet, consectetur adipiscing elit. Curabitur posuere placerat mollis. Orci varius natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Praesent accumsan lacus sed volutpat pharetra. Etiam convallis elit ac odio rhoncus molestie. Pellentesque consequat porttitor magna sit amet consectetur. Donec sed accumsan urna, in dapibus erat. Orci varius natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Etiam auctor justo a mattis euismod. Etiam porttitor turpis quis tellus laoreet, quis eleifend diam mollis. In eget risus nulla. Nulla a elit eu lectus laoreet lobortis. Proin rhoncus libero dictum augue consequat lobortis. Vestibulum suscipit dapibus dolor sit amet facilisis. Pellentesque ullamcorper, libero id fringilla tempus, mi orci vestibulum eros, ac pulvinar nibh turpis ac nulla.
  </p>
  <p>
    Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas. Nullam cursus arcu magna, at aliquet arcu pellentesque ac. Donec quis elit quis sapien tristique tincidunt. Mauris vel suscipit felis. Curabitur vestibulum vitae ipsum non tincidunt. Integer mollis congue ante. Nulla non vulputate nunc. Aenean eget est et libero venenatis faucibus. In hac habitasse platea dictumst. Suspendisse potenti. Donec interdum, tellus eu finibus auctor, felis turpis pulvinar nibh, quis gravida quam diam at risus. Integer porttitor ipsum eget vehicula vulputate.
  </p>
  <p>
    Etiam eget scelerisque tellus. Suspendisse vehicula sapien et felis dapibus varius. Orci varius natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Proin tincidunt suscipit nulla. Fusce ullamcorper malesuada lacus vel venenatis. Morbi venenatis mi id diam lacinia facilisis. In nec ipsum nec tortor fringilla sodales at in sapien. Nam accumsan turpis augue, in finibus ligula congue a. Nunc lobortis sem eget libero eleifend consectetur. In euismod maximus aliquam. Maecenas placerat scelerisque cursus. Fusce elementum malesuada sem in elementum. Phasellus congue mi et leo gravida tincidunt.
  </p>
  <p>
    Proin suscipit nulla tellus, ut lobortis ipsum porttitor eu. Sed pulvinar quam porttitor ligula ornare viverra. Nulla vitae metus lobortis, sagittis mauris ac, gravida diam. Fusce tincidunt massa a odio euismod maximus. Mauris eget dignissim justo. Proin quis nunc est. Duis non blandit ligula.
  </p>
  <sky-dropdown buttonType="context-menu">
    <sky-dropdown-menu>
      <sky-dropdown-item>
        <button type="button">
          Option 1
        </button>
      </sky-dropdown-item>
      <sky-dropdown-item>
        <button type="button">
          Option 2
        </button>
      </sky-dropdown-item>
      <sky-dropdown-item>
        <button type="button">
          Option 3
        </button>
      </sky-dropdown-item>
    </sky-dropdown-menu>
  </sky-dropdown>
</sky-popover>

<h3>
  Popovers may also be asynchronous
</h3>

<button
  type="button"
  class="sky-btn sky-btn-default"
  [skyPopover]="asyncPopoverRef">
  This trigger has an asynchronous popover
</button>

<sky-popover #asyncPopover>
  My asynchronous popover.
</sky-popover>
`
        },
        {
          name: 'popover-demo.component.scss',
          fileContents: `.popover-demo-container {
  display: flex;
  flex-wrap: wrap;
  margin: 10px 0 40px;
}

.popover-demo-item {
  padding: 10px;
}

.popover-demo-item ::ng-deep .sky-popover-container.sky-popover-hidden {
  position: relative !important;
  width: 100% !important;
  opacity: 1 !important;
  visibility: visible !important;
  z-index: 1;
  left: auto !important;
  top: auto !important;
}

.popover-demo-item ::ng-deep .sky-popover-container.sky-popover-hidden .sky-popover {
  margin: 0;
}
`
        },
        {
          name: 'popover-demo.component.ts',
          fileContents: `import {
  Component,
  ElementRef,
  ViewChild
} from '@angular/core';

import {
  SkyPopoverComponent
} from '../../core';

@Component({
  selector: 'sky-popover-demo',
  templateUrl: './popover-demo.component.html',
  styleUrls: ['./popover-demo.component.scss']
})
export class SkyPopoverDemoComponent {
  public asyncPopoverRef: SkyPopoverComponent;

  @ViewChild('remote')
  public remote: ElementRef;

  @ViewChild('asyncPopover')
  public asyncPopover: SkyPopoverComponent;

  constructor() {
    setTimeout(() => {
      this.asyncPopoverRef = this.asyncPopover;
    }, 1000);
  }

  public onPopoverOpened(popoverComponent: any) {
    alert('The popover was opened: ' + popoverComponent.popoverTitle);
  }

  public onPopoverClosed(popoverComponent: any) {
    alert('The popover was closed: ' + popoverComponent.popoverTitle);
  }
}
`,
          componentName: 'SkyPopoverDemoComponent',
          bootstrapSelector: 'sky-popover-demo'
        }
      ]
    },
    {
      name: 'Radio',
      component: SkyRadioDemoComponent,
      files: [
        {
          name: 'radio-demo.component.html',
          fileContents: `<form #checkForm="ngForm">
  <div>
    <fieldset>
      <legend>Radio button options</legend>
      <ul class="sky-list-unstyled">
        <li>
          <sky-radio name="radiotest" [(ngModel)]="selectedValue" #cb="ngModel" value="1">
            <sky-radio-label>Option 1</sky-radio-label>
          </sky-radio>
        </li>

        <li>
          <sky-radio
            name="radiotest"
            [ngModel]="selectedValue"
            (ngModelChange)="selectedValue = $event"
            disabled="true"
            [value]="valueGuy">
            <sky-radio-label>Option 2</sky-radio-label>
          </sky-radio>
        </li>

        <li>
          <sky-radio name="radiotest" [ngModel]="selectedValue" (ngModelChange)="selectedValue = $event" value="3">
            <sky-radio-label>Option 3</sky-radio-label>
          </sky-radio>
        </li>
      </ul>
    </fieldset>

    <br />
    Selected option: {{selectedValue}}
    Touched: {{cb.touched}}
    Pristine: {{cb.pristine}}
  </div>
</form>
`
        },
        {
          name: 'radio-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

@Component({
  selector: 'sky-radio-demo',
  templateUrl: './radio-demo.component.html'
})
export class SkyRadioDemoComponent {
  public selectedValue = '3';
  public valueGuy = '2';
}
`,
          componentName: 'SkyRadioDemoComponent',
          bootstrapSelector: 'sky-radio-demo'
        }
      ]
    },
    {
      name: 'Repeater',
      component: SkyRepeaterDemoComponent,
      files: [
        {
          name: 'repeater-demo.component.html',
          fileContents: `<sky-repeater
  [expandMode]="expandMode">

  <sky-repeater-item
    *ngFor="let item of items"
    [isExpanded]="item.expanded"
    [selectable]="selectable">

    <sky-repeater-item-title
      ng-if="repeaterCtrl.showTitle">
      <div class="pull-left">
        {{item.title}}
      </div>
      <div class="pull-right">
        <span
          class="label"
          ng-class="repeaterCtrl.getStatusCls(item)">
          {{item.status}}
        </span>
      </div>
    </sky-repeater-item-title>
    <sky-repeater-item-content>
      <div>
        {{item.note}}
      </div>
    </sky-repeater-item-content>
  </sky-repeater-item>
</sky-repeater>

<p>
  <label>
    Expand mode:
    <select [(ngModel)]="expandMode">
      <option value="none">
        None
      </option>
      <option value="single">
        Single
      </option>
      <option value="multiple">
        Multiple
      </option>
    </select>
  </label>
  <button class="sky-btn" (click)="addItem()">Add item</button>
  <button class="sky-btn" (click)="removeItem()">Remove last item</button>
  <sky-checkbox [(ngModel)]="selectable">
    <sky-checkbox-label>
      Show checkbox
    </sky-checkbox-label>
  </sky-checkbox>
</p>
`
        },
        {
          name: 'repeater-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

@Component({
  selector: 'sky-repeater-demo',
  templateUrl: './repeater-demo.component.html'
})
export class SkyRepeaterDemoComponent {
  public items: any[];
  public expandMode = 'single';
  public selectable = false;

  constructor() {
    this.items = [
      {
        title: 'Call Robert Hernandez',
        note: 'Robert recently gave a very generous gift.  We should call him to thank him.',
        status: 'Completed',
        statusType: 'success'
      },
      {
        title: 'Send invitation to Spring Ball',
        note: 'The Spring Ball is coming up soon.  Let\\\'s get those invitations out!',
        status: 'Past due',
        statusType: 'warning'
      }
    ];
  }

  public addItem() {
    let next = this.items.length + 1;
    this.items.push({
      title: 'New reminder ' + next,
      note: 'This is a new reminder',
      expanded: true,
      status: 'Active',
      statusType: 'info'
    });
  }

  public removeItem() {
    this.items.pop();
  }
}
`,
          componentName: 'SkyRepeaterDemoComponent',
          bootstrapSelector: 'sky-repeater-demo'
        }
      ]
    },
    {
      name: 'Search',
      component: SkySearchDemoComponent,
      files: [
        {
          name: 'search-demo.component.html',
          fileContents: `<sky-toolbar>
  <sky-toolbar-item>
    <button
      class="sky-btn sky-btn-default sky-demo-search-bar-item"
      (click)="searchApplied('Robert')">
      Predefined search text
    </button>
  </sky-toolbar-item>
  <sky-toolbar-item>
    <sky-search
      [searchText]="searchText"
      (searchApply)="searchApplied($event)">
    </sky-search>
  </sky-toolbar-item>
</sky-toolbar>
<div>
  <sky-repeater expandMode="none">
    <sky-repeater-item *ngFor="let item of displayedItems">
      <sky-repeater-item-title>
        {{item.title}}
      </sky-repeater-item-title>
      <sky-repeater-item-content>
        <div>
          {{item.note}}
        </div>
      </sky-repeater-item-content>
    </sky-repeater-item>
  </sky-repeater>
</div>
`
        },
        {
          name: 'search-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

@Component({
  selector: 'sky-search-demo',
  templateUrl: './search-demo.component.html'
})
export class SkySearchDemoComponent {
  public displayedItems: any;
  public searchText: string;

  private items: any[] = [
    {
      title: 'Call Robert Hernandez',
      note: 'Robert recently gave a very generous gift. We should call to thank him.'
    },
    {
      title: 'Send invitation to ball',
      note: 'The Spring Ball is coming up soon. Let\\\'s get those invitations out!'
    },
    {
      title: 'Clean up desk',
      note: 'File and organize papers.'
    },
    {
      title: 'Investigate leads',
      note: 'Check out leads for important charity event funding.'
    },
    {
      title: 'Send thank you note',
      note: 'Send a thank you note to Timothy for his donation.'
    }
  ];

  constructor() {
    this.displayedItems = this.items;
  }

  public searchApplied(searchText: string) {
    let filteredItems = this.items;
    this.searchText = searchText;

    if (searchText) {
      filteredItems = this.items.filter(function (item: any) {
        let property: any;

        for (property in item) {
          if (item.hasOwnProperty(property) && (property === 'title' || property === 'note')) {
            if (item[property].indexOf(searchText) > -1) {
              return true;
            }
          }
        }

        return false;
      });
    }
    this.displayedItems = filteredItems;
  }
}
`,
          componentName: 'SkySearchDemoComponent',
          bootstrapSelector: 'sky-search-demo'
        }
      ]
    },
    {
      name: 'Sectioned form',
      component: SkySectionedFormDemoComponent,
      files: [
        {
          name: 'sectioned-form-demo.component.html',
          fileContents: `<h3>Sectioned form — Index: {{ activeIndexDisplay }}</h3>
<div style="height: 150px;">
  <sky-sectioned-form (indexChanged)="updateIndex($event)" >
    <sky-sectioned-form-section heading="Basic information">
      <sky-demo-information-form>
      </sky-demo-information-form>
    </sky-sectioned-form-section>
    <sky-sectioned-form-section heading="Addresses" itemCount="2" active="true">
      <sky-demo-address-form></sky-demo-address-form>
    </sky-sectioned-form-section>
    <sky-sectioned-form-section heading="Phone numbers" itemCount="3">
      <sky-demo-phone-form></sky-demo-phone-form>
    </sky-sectioned-form-section>
  </sky-sectioned-form>
</div>

<h3>With a modal</h3>
<button type="button" class="sky-btn sky-btn-default" (click)="openModal()">
  Open sectioned form
</button>`
        },
        {
          name: 'sectioned-form-demo.component.ts',
          fileContents: `import {
  Component,
  AfterContentChecked
} from '@angular/core';

import {
  SkyModalService,
  SkyModalCloseArgs
} from '../../core';

import {
  SkySectionedModalFormDemoComponent
} from './sectioned-modal-form-demo.component';

@Component({
  selector: 'sky-sectioned-form-demo',
  templateUrl: './sectioned-form-demo.component.html'
})
export class SkySectionedFormDemoComponent implements AfterContentChecked {
  public activeIndexDisplay: number;
  private _activeIndex: number;

  constructor(private modal: SkyModalService) { }

  public openModal() {
    let modalInstance = this.modal.open(SkySectionedModalFormDemoComponent);

    modalInstance.closed.subscribe((result: SkyModalCloseArgs) => {
      if (result.reason === 'cancel') {
        console.log(\`Modal cancelled with data \${result.data}\`);
      } else if (result.reason === 'save') {
        console.log(\`Modal saved with data \${result.data}\`);
      }
    });
  }

  public ngAfterContentChecked() {
    this.activeIndexDisplay = this._activeIndex;
  }

  public updateIndex(newIndex: number) {
    this._activeIndex = newIndex;
  }
}
`,
          componentName: 'SkySectionedFormDemoComponent',
          bootstrapSelector: 'sky-sectioned-form-demo'
        },
        {
          name: 'sectioned-modal-form-demo.component.html',
          fileContents: `<sky-modal>
  <sky-modal-header>
      Sectioned form — Index: {{ activeIndexDisplay }}
  </sky-modal-header>
  <sky-modal-content>
    <sky-sectioned-form (indexChanged)="updateIndex($event)">
      <sky-sectioned-form-section heading="Basic information">
        <sky-demo-information-form>
        </sky-demo-information-form>
      </sky-sectioned-form-section>
      <sky-sectioned-form-section heading="Addresses" itemCount="2" [active]="activeTab">
        <sky-demo-address-form></sky-demo-address-form>
      </sky-sectioned-form-section>
      <sky-sectioned-form-section heading="Phone numbers" itemCount="3">
        <sky-demo-phone-form></sky-demo-phone-form>
      </sky-sectioned-form-section>
    </sky-sectioned-form>
  </sky-modal-content>
  <sky-modal-footer>
    <button
      type="button"
      *ngIf="tabsHidden()"
      class="sky-btn sky-btn-secondary"
      (click)="showTabs()"
    >
      Show sections
    </button>
    <button
      type="button"
      class="sky-btn sky-btn-primary"
      (click)="instance.save('Something cool')"
    >
      Save
    </button>
    <button
      type="button"
      class="sky-btn sky-btn-link"
      (click)="instance.cancel('Nevermind')"
    >
      Cancel
    </button>
  </sky-modal-footer>
  </sky-modal>
  `
        },
        {
          name: 'sectioned-modal-form-demo.component.ts',
          fileContents: `import {
  Component,
  ViewChild,
  AfterContentChecked
} from '@angular/core';

import {
  SkyModalInstance,
  SkySectionedFormComponent
} from '../../core';

@Component({
  selector: 'sky-sectioned-modal-form-demo',
  templateUrl: './sectioned-modal-form-demo.component.html'
})
export class SkySectionedModalFormDemoComponent implements AfterContentChecked {

  @ViewChild(SkySectionedFormComponent)
  public sectionedFormComponent: SkySectionedFormComponent;

  public activeIndexDisplay: number = undefined;
  public activeTab = true;

  private _activeIndex: number = undefined;

  constructor(public instance: SkyModalInstance) {}

  public ngAfterContentChecked() {
    this.activeIndexDisplay = this._activeIndex;
  }

  public updateIndex(newIndex: number) {
    this._activeIndex = newIndex;
  }

  public tabsHidden() {
    return !this.sectionedFormComponent.tabsVisible();
  }

  public showTabs() {
    this.sectionedFormComponent.showTabs();
  }
}
`
        },
        {
          name: 'demo-address-form.component.html',
          fileContents: `<div class="sky-emphasized" style="margin-bottom: 5px;">
  <label>Home</label>
</div>
<div>410 17th Street</div>
<div>Denver, CO 80202-4402</div>

<div class="sky-emphasized" style="margin: 5px 0 5px 0;">
  <label>Vacation</label>
</div>
<div>3345 Franciscan Way</div>
<div>Tampa, FL 10255-0098</div>
`
        },
        {
          name: 'demo-address-form.component.ts',
          fileContents: `import { Component } from '@angular/core';

@Component({
  selector: 'sky-demo-address-form',
  templateUrl: './demo-address-form.component.html'
})
export class SkyDemoAddressFormComponent { }
`,
          componentName: 'SkyDemoAddressFormComponent'
        },
        {
          name: 'demo-information-form.component.html',
          fileContents: `<div style="display: flex;">
  <div style="margin-right: 10px; flex: 1;">
    <label 
      for="inputName" 
      class="sky-control-label"
      [ngClass]="{'sky-control-label-required': nameRequired}"
    >
      Name
    </label>
    <input 
      class="sky-form-control"
      [(ngModel)]="name"
      id="inputName" 
      type="text"
      [required]="nameRequired"
    />
  </div>
  <div style="margin-right: 10px; flex: 1;">
    <label 
      for="inputId" 
      class="sky-control-label"
    >
      ID
    </label>
    <input 
      class="sky-form-control"
      [(ngModel)]="id"
      id="inputId" 
      type="text"
    />
  </div>
</div>

<div style="margin-top: 10px;">
  <sky-checkbox [(ngModel)]="nameRequired">
    <sky-checkbox-label>
      Make 'Name' required
    </sky-checkbox-label>
  </sky-checkbox>
</div>
  
`
        },
        {
          name: 'demo-information-form.component.ts',
          fileContents: `import { Component } from '@angular/core';

import { SkySectionedFormService } from '../../core';

@Component({
  selector: 'sky-demo-information-form',
  templateUrl: './demo-information-form.component.html'
})
export class SkyDemoInformationFormComponent {
  private _nameRequired: boolean;
  private _name: string = '';
  private _id: string = '5324901';

  public get nameRequired() {
    return this._nameRequired;
  }

  public set nameRequired(value: boolean) {
    this._nameRequired = value;
    this.sectionedFormService.requiredFieldChanged(value);
  }

  public get name() {
    return this._name;
  }

  public set name(value: string) {
    this._name = value;

    if (this._nameRequired) {
      this.sectionedFormService.requiredFieldChanged(!this._name);
    }
  }

  public get id() {
    return this._id;
  }

  public set id(value: string) {
    this._id = value;

    const valid = this.idValid(this._id);
    this.sectionedFormService.invalidFieldChanged(!valid);
  }

  constructor(
    private sectionedFormService: SkySectionedFormService
  ) { }

  private idValid(value: string) {
    if (value) {
      let regExp = new RegExp('^[0-9]+\$');
      return regExp.test(value);
    } else {
      return true;
    }
  }
}
`,
          componentName: 'SkyDemoInformationFormComponent'
        },
        {
          name: 'demo-phone-form.component.html',
          fileContents: `<div class="sky-emphasized" style="margin-bottom: 5px;">
  <label>Home</label>
</div>
<div>303.997.3301</div>

<div class="sky-emphasized" style="margin: 5px 0 5px 0;">
  <label>Vacation</label>
</div>
<div>888.387.1211</div>

<div class="sky-emphasized" style="margin: 5px 0 5px 0;">
  <label>Vacation</label>
</div>
<div>303.997.2000</div>
`
        },
        {
          name: 'demo-phone-form.component.ts',
          fileContents: `import { Component } from '@angular/core';

@Component({
  selector: 'sky-demo-phone-form',
  templateUrl: './demo-phone-form.component.html'
})
export class SkyDemoPhoneFormComponent { }
`,
          componentName: 'SkyDemoPhoneFormComponent'
        }
      ]
    },
    {
      name: 'Select field',
      component: SkySelectFieldDemoComponent,
      files: [
        {
          name: 'select-field-demo.component.html',
          fileContents: `<sky-row>
  <sky-column screenSmall="6">
    <h2 class="sky-section-heading">
      Reactive form setup
    </h2>

    <form
      [formGroup]="reactiveForm"
      (ngSubmit)="submitReactiveForm()"
      novalidate>

      <div class="sky-form-group">
        <label
          class="sky-control-label"
          id="my-select-field-1">
          What is your favorite color?
        </label>
        <sky-select-field
          [data]="colorStream"
          ariaLabelledBy="my-select-field-1"
          formControlName="favoriteColor"
          pickerHeading="Colors"
          selectMode="single"
          singleSelectClearButtonTitle="Clear selected color"
          singleSelectOpenButtonTitle="Select a color"
          singleSelectPlaceholderText="Select a color...">
        </sky-select-field>
      </div>

      <div class="sky-form-group">
        <label
          class="sky-control-label"
          id="my-select-field-2">
          What are your favorite fruits?
        </label>
        <sky-select-field
          [data]="fruitStream"
          ariaLabelledBy="my-select-field-2"
          formControlName="favoriteFruits"
          multipleSelectOpenButtonText="Select some fruits"
          pickerHeading="Fruits">
        </sky-select-field>
      </div>

      <div class="select-field-demo-alert">
        <strong>Form model:</strong>
        <pre>{{ reactiveForm.value | json }}</pre>
      </div>

      <button
        class="sky-btn sky-btn-primary"
        type="submit">
        Submit
      </button>
    </form>
  </sky-column>
  <sky-column screenSmall="6">
    <h2 class="sky-section-heading">
      Template-driven form setup
    </h2>

    <form
      #myForm="ngForm"
      (ngSubmit)="submitTemplateForm(myForm.value)"
      novalidate>

      <div class="sky-form-group">
        <label
          class="sky-control-label"
          id="my-select-field-template-driven-1">
          What is your favorite color?
        </label>
        <sky-select-field
          [data]="colorStream"
          ariaLabelledBy="my-select-field-template-driven-1"
          pickerHeading="Colors"
          selectMode="single"
          singleSelectClearButtonTitle="Clear selected color"
          singleSelectOpenButtonTitle="Select a color"
          singleSelectPlaceholderText="Select a color..."
          name="favoriteColor"
          [(ngModel)]="templateDrivenModel.favoriteColor">
        </sky-select-field>
      </div>

      <div class="sky-form-group">
        <label
          class="sky-control-label"
          id="my-select-field-template-driven-2">
          What are your favorite fruits?
        </label>
        <sky-select-field
          [data]="fruitStream"
          ariaLabelledBy="my-select-field-template-driven-2"
          multipleSelectOpenButtonText="Select some fruits"
          pickerHeading="Fruits"
          name="favoriteFruits"
          [(ngModel)]="templateDrivenModel.favoriteFruits">
        </sky-select-field>
      </div>

      <div class="select-field-demo-alert">
        <strong>Form model:</strong>
        <pre>{{ myForm.value | json }}</pre>
      </div>

      <button
        class="sky-btn sky-btn-primary"
        type="submit">
        Submit
      </button>
    </form>
  </sky-column>
</sky-row>
<sky-row>
  <sky-column screenSmall="12">
    <h2 class="sky-section-heading">
      Disabled select fields
    </h2>

    <div class="sky-form-group">
      <label
        class="sky-control-label">
        Make a single selection (disabled)
      </label>
      <sky-select-field
        [data]="colorStream"
        [disabled]="true"
        selectMode="single">
      </sky-select-field>
    </div>

    <div class="sky-form-group">
      <label
        class="sky-control-label">
        Make multiple selections (disabled)
      </label>
      <sky-select-field
        [data]="fruitStream"
        [disabled]="true">
      </sky-select-field>
    </div>
  </sky-column>
</sky-row>
`
        },
        {
          name: 'select-field-demo.component.scss',
          fileContents: `.select-field-demo-alert {
  padding: 10px;
  margin-bottom: 10px;
  border: 1px solid #cdcfd2;
  border-radius: 3px;
}

.select-field-demo-alert pre {
  margin: 0;
}
`
        },
        {
          name: 'select-field-demo.component.ts',
          fileContents: `import {
  Component,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';

import {
  FormBuilder,
  FormGroup,
  FormControl
} from '@angular/forms';

import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/take';

@Component({
  selector: 'sky-select-field-demo',
  templateUrl: 'select-field-demo.component.html',
  styleUrls: ['./select-field-demo.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SkySelectFieldDemoComponent implements OnInit {
  public reactiveForm: FormGroup;

  public colors = [
    { id: '1', label: 'Red' },
    { id: '2', label: 'Green' },
    { id: '3', label: 'Violet' }
  ];

  public fruits = [
    { id: '1', category: 'Pome', label: 'Apple', description: 'Anne eats apples' },
    { id: '2', category: 'Berry', label: 'Banana', description: 'Ben eats bananas' },
    { id: '3', category: 'Pome', label: 'Pear', description: 'Patty eats pears' },
    { id: '4', category: 'Berry', label: 'Grape', description: 'George eats grapes' },
    { id: '5', category: 'Berry', label: 'Banana', description: 'Becky eats bananas' },
    { id: '6', category: 'Citrus', label: 'Lemon', description: 'Larry eats lemons' },
    { id: '7', category: 'Aggregate fruit', label: 'Strawberry', description: 'Sally eats strawberries' }
  ];

  public fruitStream = new BehaviorSubject<any>([]);
  public colorStream = new BehaviorSubject<any>([]);

  public templateDrivenModel: any = {
    favoriteColor: this.colors[0],
    favoriteFruits: [this.fruits[0]]
  };

  constructor(
    private formBuilder: FormBuilder
  ) { }

  public ngOnInit(): void {
    this.createForm();
    this.populateData();
  }

  public submitReactiveForm(): void {
    alert('Form submitted with: \\n' + JSON.stringify(this.reactiveForm.value));
  }

  public submitTemplateForm(formData: any): void {
    alert('Form submitted with: \\n' + JSON.stringify(formData));
  }

  private createForm(): void {
    this.reactiveForm = this.formBuilder.group({
      favoriteColor: new FormControl(this.colors[0]),
      favoriteFruits: new FormControl([this.fruits[0]])
    });
  }

  private populateData() {
    this.fruitStream.next(this.fruits);
    this.colorStream.next(this.colors);
  }
}
`,
          componentName: 'SkySelectFieldDemoComponent',
          bootstrapSelector: 'sky-select-field-demo'
        }
      ]
    },
    {
      name: 'Sort',
      component: SkySortDemoComponent,
      files: [
        {
          name: 'sort-demo.component.html',
          fileContents: `<sky-toolbar>
  <sky-toolbar-item>
    <sky-sort>
      <sky-sort-item
        *ngFor="let item of sortOptions"
        [active]="initialState === item.id"
        (itemSelect)="sortItems(item)">
        {{item.label}}
      </sky-sort-item>
    </sky-sort>
  </sky-toolbar-item>
</sky-toolbar>
<div>
  <sky-repeater expandMode="none">
    <sky-repeater-item *ngFor="let item of sortedItems">
      <sky-repeater-item-title>
          {{item.title}}
      </sky-repeater-item-title>
      <sky-repeater-item-content>
          <div style="display: flex; justify-content: space-between">
            <div>
              Assigned to {{item.assignee}}
            </div>
            <div>
              Created {{item.date | date}}
            </div>
          </div>
          <div>
            {{item.note}}
          </div>
      </sky-repeater-item-content>
    </sky-repeater-item>
  </sky-repeater>
</div>
`
        },
        {
          name: 'sort-demo.component.ts',
          fileContents: `import {
  Component,
  OnInit
} from '@angular/core';

@Component({
  selector: 'sky-sort-demo',
  templateUrl: './sort-demo.component.html'
})
export class SkySortDemoComponent implements OnInit {
  public initialState: number;
  public sortOptions = [
    {
      id: 1,
      label: 'Assigned to (A - Z)',
      name: 'assignee',
      descending: false
    },
    {
      id: 2,
      label: 'Assigned to (Z - A)',
      name: 'assignee',
      descending: true
    },
    {
      id: 3,
      label: 'Date created (newest first)',
      name: 'date',
      descending: true
    },
    {
      id: 4,
      label: 'Date created (oldest first)',
      name: 'date',
      descending: false
    },
    {
      id: 5,
      label: 'Note title (A - Z)',
      name: 'title',
      descending: false
    },
    {
      id: 6,
      label: 'Note title (Z - A)',
      name: 'title',
      descending: true
    }
  ];

  public sortedItems: any[] = [
    {
      title: 'Call Robert Hernandez',
      note: 'Robert recently gave a very generous gift. We should call to thank him.',
      assignee: 'Debby Fowler',
      date: new Date('12/22/2015')
    },
    {
      title: 'Send invitation to ball',
      note: 'The Spring Ball is coming up soon. Let\\\'s get those invitations out!',
      assignee: 'Debby Fowler',
      date: new Date('1/1/2016')
    },
    {
      title: 'Clean up desk',
      note: 'File and organize papers.',
      assignee: 'Tim Howard',
      date: new Date('2/2/2016')
    },
    {
      title: 'Investigate leads',
      note: 'Check out leads for important charity event funding.',
      assignee: 'Larry Williams',
      date: new Date('4/5/2016')
    },
    {
      title: 'Send thank you note',
      note: 'Send a thank you note to Timothy for his donation.',
      assignee: 'Catherine Hooper',
      date: new Date('11/11/2015')
    }
  ];

  public sortItems(item: any) {
    let result = this.sortedItems.sort(function (a: any, b: any) {
      let descending = item.descending ? -1 : 1,
        sortProperty = item.name;

      if (a[sortProperty] > b[sortProperty]) {
        return (descending);
      } else if (a[sortProperty] < b[sortProperty]) {
        return (-1 * descending);
      } else {
        return 0;
      }
    });

    this.sortedItems = result;
  }

  public ngOnInit() {
    this.initialState = 3;
    this.sortItems(this.sortOptions[2]);
  }
}
`,
          componentName: 'SkySortDemoComponent',
          bootstrapSelector: 'sky-sort-demo'
        }
      ]
    },
    {
      name: 'Tabs',
      component: SkyTabsDemoComponent,
      files: [
        {
          name: 'tabs-demo.component.html',
          fileContents: `<sky-tabset
  (newTab)="newTabClick()"
  (openTab)="openTabClick()"
  [active]="activeTabIndex"
  (activeChange)="tabChanged($event)">
  <sky-tab
    *ngFor="let tab of tabs; let i = index"
    [tabHeading]="tab.heading"
    (close)="closeClick(i)">
    {{tab.content}}
  </sky-tab>
  <sky-tab
    tabHeading="Permanent tab"
    tabIndex="permanent">
    This tab cannot be closed.
  </sky-tab>
</sky-tabset>

<h3>
  Tabs example with counter
</h3>

<sky-tabset
  [active]="0">
  <sky-tab
    *ngFor="let tab of tabsWithCounts"
    [tabHeading]="tab.heading"
    [tabHeaderCount]="tab.headerCount">
    {{tab.content}}
  </sky-tab>
</sky-tabset>
`
        },
        {
          name: 'tabs-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

@Component({
  selector: 'sky-tabs-demo',
  templateUrl: './tabs-demo.component.html'
})
export class SkyTabsDemoComponent {
  public tabs: any[];
  public tabsWithCounts: any[];
  public activeTabIndex: any = 0;

  constructor() {
    this.tabs = [
      {
        heading: 'Tab 1',
        content: 'Content 1',
        active: true
      },
      {
        heading: 'Tab 2',
        content: 'Content 2'
      },
      {
        heading: 'Tab 3',
        content: 'Content 3'
      }
    ];
    this.tabsWithCounts = [
      {
        heading: 'Records',
        content: 'Placeholder content for records',
        headerCount: 10,
        active: true
      },
      {
        heading: 'Gifts',
        content: 'Placeholder content for gifts',
        headerCount: 14
      },
      {
        heading: 'Users',
        content: 'Placeholder content for users',
        headerCount: 144
      }
    ];
  }

  public closeClick(tabIndex: number) {
    this.tabs.splice(tabIndex, 1);
  }

  public newTabClick() {
    let nextTab = this.tabs && this.tabs.length + 1;

    this.tabs.push({
      heading: 'Tab ' + nextTab,
      content: 'Content ' + nextTab,
      active: true
    });
  }

  public openTabClick() {
    alert('You clicked the open tab button');
  }

  public tabChanged(newIndex: any) {
    console.log('new active', this.activeTabIndex);
  }
}
`,
          componentName: 'SkyTabsDemoComponent',
          bootstrapSelector: 'sky-tabs-demo'
        }
      ]
    },
    {
      name: 'Text expand',
      component: SkyTextExpandDemoComponent,
      files: [
        {
          name: 'text-expand-demo.component.html',
          fileContents: `<h3>
  Text expand
</h3>

<sky-text-expand
  [text]="shortText">
</sky-text-expand>

<h3>
  Text expand modal view
</h3>

<sky-text-expand
  [text]="longText">
</sky-text-expand>

<h3>
  Text expand modal view (with newlines)
</h3>

<sky-text-expand
  [text]="longText"
  [truncateNewlines]="false">
</sky-text-expand>

<h3>
  Text expand repeater
</h3>

<sky-text-expand-repeater
  [data]="repeaterData"
  [maxItems]="2">
</sky-text-expand-repeater>
`
        },
        {
          name: 'tabs-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

@Component({
  selector: 'sky-text-expand-demo',
  templateUrl: './text-expand-demo.component.html'
})
export class SkyTextExpandDemoComponent {
  // tslint:disable-next-line
  public shortText = 'Lorem ipsum dolor sit amet, consectetuer adipiscing elit. Aenean commodo ligula eget dolor. Aenean massa. Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Donec quam felis, ultricies nec, pellentesque eu, pretium quis, sem. Nulla consequat massa quis enim. Donec pede justo, fringilla vel, aliquet nec, vulputate eget, arcu. In enim justo, rhoncus ut, imperdiet a, venenatis vitae, justo. Nullam dictum felis eu pede mollis pretium. Integer tincidunt. Cras dapibu';

  // tslint:disable-next-line
  public longText = 'Lorem ipsum dolor sit amet, consectetuer adipiscing elit. Aenean commodo ligula eget dolor.\\nAenean massa.\\nCum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus.\\nDonec quam felis, ultricies nec, pellentesque eu, pretium quis, sem.\\nNulla consequat massa quis enim.\\nDonec pede justo, fringilla vel, aliquet nec, vulputate eget, arcu. In enim justo, rhoncus ut, imperdiet a, venenatis vitae, justo. Nullam dictum felis eu pede mollis pretium.\\nInteger tincidunt.\\nCras dapibu';

  public repeaterData: string[] = [
    'Repeater item 1',
    'Repeater item 2',
    'Repeater item 3',
    'Repeater item 4',
    'Repeater item 5'
  ];
}
`,
          componentName: 'SkyTextExpandDemoComponent',
          bootstrapSelector: 'sky-text-expand-demo'
        }
      ]
    },
    {
      name: 'Text highlight',
      component: SkyTextHighlightDemoComponent,
      files: [
        {
          name: 'text-highlight-demo.component.html',
          fileContents: `<div class="sky-form-group">
  <label for="sky-demo-search-term">Enter text to highlight:</label>
  <br>
  <input
    id="sky-demo-search-term"
    type="text"
    style="margin: 10px 0 10px 0; width: 180px;"
    class="sky-form-control"
    [(ngModel)]="searchTerm">
</div>

<div style="margin-bottom: 10px;">
  <sky-checkbox [(ngModel)]="showAdditionalContent">
    <sky-checkbox-label>Display additional content</sky-checkbox-label>
  </sky-checkbox>
</div>

<div [skyHighlight]="searchTerm">
  The text that you enter is highlighted here.
  <div *ngIf="showAdditionalContent">
    This additional content is highlighted too!
  </div>
</div>
`
        },
        {
          name: 'text-highlight-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

@Component({
  selector: 'sky-text-highlight-demo',
  templateUrl: './text-highlight-demo.component.html'
})
export class SkyTextHighlightDemoComponent {
  public searchTerm: string;
  public showAdditionalContent = false;
}
`,
          componentName: 'SkyTextHighlightDemoComponent',
          bootstrapSelector: 'sky-text-highlight-demo'
        }
      ]
    },
    {
      name: 'Tile',
      component: SkyTileDemoComponent,
      files: [
        {
          name: 'tile-demo.component.html',
          fileContents: `<sky-tile-dashboard [(config)]="dashboardConfig"></sky-tile-dashboard>
`
        },
        {
          name: 'tile-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';
import { SkyTileDashboardConfig } from '../../core';
import { SkyTileDemoTile1Component } from './tile-demo-tile1.component';
import { SkyTileDemoTile2Component } from './tile-demo-tile2.component';

@Component({
  selector: 'sky-tile-demo',
  templateUrl: './tile-demo.component.html'
})
export class SkyTileDemoComponent {
  public dashboardConfig: SkyTileDashboardConfig;

  constructor() {
    this.dashboardConfig = {
      tiles: [
        {
          id: 'tile1',
          componentType: SkyTileDemoTile1Component
        },
        {
          id: 'tile2',
          componentType: SkyTileDemoTile2Component
        }
      ],
      layout: {
        singleColumn: {
          tiles: [
            {
              id: 'tile2',
              isCollapsed: false
            },
            {
              id: 'tile1',
              isCollapsed: true
            }
          ]
        },
        multiColumn: [
          {
            tiles: [
              {
                id: 'tile1',
                isCollapsed: true
              }
            ]
          },
          {
            tiles: [
              {
                id: 'tile2',
                isCollapsed: false
              }
            ]
          }
        ]
      }
    };
  }
}
`,
          componentName: 'SkyTileDemoComponent',
          bootstrapSelector: 'sky-tile-demo'
        },
        {
          name: 'tile-demo-tile1.component.html',
          fileContents: `<sky-tile (settingsClick)="tileSettingsClick()">
  <sky-tile-title>
    Tile 1
  </sky-tile-title>
  <sky-tile-summary>
    $123.4m
  </sky-tile-summary>
  <sky-tile-content>
    <sky-tile-content-section>
      Section 1
    </sky-tile-content-section>
    <sky-tile-content-section>
      Section 2
    </sky-tile-content-section>
    <sky-tile-content-section>
      Section 3
    </sky-tile-content-section>
  </sky-tile-content>
</sky-tile>
`
        },
        {
          name: 'tile-demo-tile1.component.ts',
          fileContents: `import { Component } from '@angular/core';

@Component({
  // tslint:disable-next-line
  selector: 'div.tile1',
  templateUrl: './tile-demo-tile1.component.html'
})
export class SkyTileDemoTile1Component {
  public tileSettingsClick() {
    alert('tile settings clicked');
  }
}
`,
          componentName: 'SkyTileDemoTile1Component'
        },
        {
          name: 'tile-demo-tile2.component.html',
          fileContents: `<sky-tile>
  <sky-tile-title>
    Tile 2
  </sky-tile-title>
  <sky-tile-content>
    <sky-tile-content-section>
      Section 1
    </sky-tile-content-section>
    <sky-tile-content-section>
      Section 2
    </sky-tile-content-section>
    <sky-tile-content-section>
      Section 3
    </sky-tile-content-section>
  </sky-tile-content>
</sky-tile>
`
        },
        {
          name: 'tile-demo-tile2.component.ts',
          fileContents: `import { Component } from '@angular/core';

@Component({
  // tslint:disable-next-line
  selector: 'div.tile2',
  templateUrl: './tile-demo-tile2.component.html'
})
export class SkyTileDemoTile2Component { }
`,
          componentName: 'SkyTileDemoTile2Component'
        }
      ]
    },
    {
      name: 'Timepicker',
      component: SkyTimepickerDemoComponent,
      files: [
        {
          name: 'timepicker-demo.component.html',
          fileContents: `<h3>
  12-hour timepicker
</h3>

<div style="max-width: 400px">
  <sky-timepicker #timePickerExample1>
    <input
      type="text"
      [skyTimepickerInput]="timePickerExample1"
      [timeFormat]="format12"
      [(ngModel)]="selectedTime1"
      #time="ngModel">
  </sky-timepicker>

  <div
    class="sky-error-label"
    *ngIf="time.errors && time.errors.skyTime && (time.dirty || time.touched)">
    <span [hidden]="!time.errors.skyTime.invalid" >
      Please enter a valid time.
    </span>
  </div>
</div>

<div>
  Selected time is {{selectedTime1?.local}}
</div>

<br/>

<h3>
  24-hour timepicker.
</h3>

<div style="max-width: 400px">
  <sky-timepicker #timePickerExample2>
    <input
    type="text"
    [skyTimepickerInput]="timePickerExample2"
    [timeFormat]="format24"
    [(ngModel)]="selectedTime2"
    #time2="ngModel">
  </sky-timepicker>
  <div
    class="sky-error-label"
    *ngIf="time2.errors && time2.errors.skyTime && (time2.dirty || time2.touched)">
    <span [hidden]="!time2.errors.skyTime.invalid">
      Please enter a valid time.
    </span>
  </div>
</div>

<div>
  Selected time is {{selectedTime2?.hour}}:{{selectedTime2?.minute}}
</div>

<br/>

<h3>
  Custom timepicker
</h3>

<div style="max-width: 400px">
  <sky-timepicker #timePickerExample3>
    <input
      type="hidden"
      [skyTimepickerInput]="timePickerExample3"
      [timeFormat]="format24"
      [returnFormat]="returnFormat"
      [(ngModel)]="selectedTime3"
      #time3="ngModel">
  </sky-timepicker>
  <div
    class="sky-error-label"
    *ngIf="time3.errors && time3.errors.skyTime && (time3.dirty || time3.touched)">
    <span [hidden]="!time.errors.skyTime.invalid">
      Please enter a valid time.
    </span>
  </div>
  Selected time is {{selectedTime3?.local}}
</div>
`
        },
        {
          name: 'timepicker-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

@Component({
  selector: 'sky-timepicker-demo',
  templateUrl: './timepicker-demo.component.html'
})
export class SkyTimepickerDemoComponent {
  public format12 = 'hh';
  public format24 = 'HH';
  public returnFormat = 'HH:mm:ssZ';
  public selectedTime1: any = '8:30 PM';
  public selectedTime2: any = '20:30';
  public selectedTime3: any = '02:00:00-0400';
}
`,
          componentName: 'SkyTimepickerDemoComponent',
          bootstrapSelector: 'sky-timepicker-demo'
        }
      ]
    },
    {
      name: 'Tokens',
      component: SkyTokensDemoComponent,
      files: [
        {
          name: 'tokens-demo.component.html',
          fileContents: `<h3 class="sky-subsection-heading">
  Default setup
</h3>

<p>
  These tokens can be navigated with the arrow keys, and they can be dismissed with the close buttons.
</p>

<p>
  <sky-tokens
    [(tokens)]="colors">
  </sky-tokens>
</p>

<hr>

<h3 class="sky-subsection-heading">
  Non-dismissible tokens
</h3>

<p>
  These tokens cannot be dismissed, but they can still be navigated with the arrow keys.
</p>

<p>
  <sky-tokens
    [(tokens)]="colors"
    [dismissible]="false">
  </sky-tokens>
</p>

<hr>

<h3 class="sky-subsection-heading">
  Non-focusable tokens
</h3>

<p>
  These tokens cannot be focused using the tab key, but they can still be clicked and navigated with the arrow keys.
</p>

<p>
  <sky-tokens
    [(tokens)]="colors"
    [dismissible]="false"
    [focusable]="false">
  </sky-tokens>
</p>

<hr>

<h3 class="sky-subsection-heading">
  Disabled tokens
</h3>

<p>
  These tokens are disabled and cannot be dismissed or navigated with the arrow keys.
</p>

<p>
  <sky-tokens
    [(tokens)]="colors"
    [disabled]="true">
  </sky-tokens>
</p>

<hr>

<h3 class="sky-subsection-heading">
  Token value interactions
</h3>

<p>
  <button
    class="sky-btn sky-btn-default"
    type="button"
    (click)="changeColors()">
    Change tokens
  </button>

  <button
    class="sky-btn sky-btn-default"
    type="button"
    (click)="resetColors()">
    Reset tokens
  </button>

  <button
    class="sky-btn sky-btn-default"
    type="button"
    (click)="destroyColors()">
    Destroy tokens
  </button>
</p>

<hr>

<h3 class="sky-subsection-heading">
  Custom setup
</h3>

<p>
  These tokens define a custom property to display their values. When users select a token, it emits an event.
</p>

<p>
  <sky-tokens
    displayWith="label"
    [(tokens)]="filters"
    [messageStream]="tokensController"
    (focusIndexOverRange)="onFocusIndexOverRange()"
    (focusIndexUnderRange)="onFocusIndexUnderRange()"
    (tokenSelected)="onTokenSelected($event)">
    (You may also include content inside the tokens component.)
  </sky-tokens>
</p>

<p>
  <button
    class="sky-btn sky-btn-default"
    (click)="focusLastToken()">
    Focus last token
  </button>
</p>
`
        },
        {
          name: 'tokens-demo.component.ts',
          fileContents: `import {
  Component,
  OnDestroy
} from '@angular/core';

import { Subject } from 'rxjs/Subject';

import {
  SkyToken,
  SkyTokenSelectedEventArgs,
  SkyTokensMessageType,
  SkyTokensMessage
} from '../../core';

@Component({
  selector: 'sky-tokens-demo',
  templateUrl: './tokens-demo.component.html'
})
export class SkyTokensDemoComponent implements OnDestroy {
  public colors: SkyToken[];
  public filters: SkyToken[];
  public tokensController: Subject<SkyTokensMessage>;

  private defaultColors = [
    { name: 'Red' },
    { name: 'Black' },
    { name: 'Blue' },
    { name: 'Brown' },
    { name: 'Green' },
    { name: 'Orange' },
    { name: 'Pink' },
    { name: 'Purple' },
    { name: 'Turquoise' },
    { name: 'White' },
    { name: 'Yellow' }
  ];

  private selectedFilters = [
    { label: 'Canada' },
    { label: 'Older than 55' },
    { label: 'Employed' },
    { label: 'Added before 2018' }
  ];

  constructor() {
    this.createColors();
    this.filters = this.parseTokens(this.selectedFilters);
  }

  public ngOnDestroy() {
    if (this.tokensController) {
      this.tokensController.complete();
    }
  }

  public resetColors() {
    this.createColors();
  }

  public changeColors() {
    this.colors = this.parseTokens([
      { name: 'Red' },
      { name: 'White' },
      { name: 'Blue' }
    ]);
  }

  public destroyColors() {
    this.colors = undefined;
  }

  public createColors() {
    this.colors = this.parseTokens(this.defaultColors);
  }

  public onTokenSelected(args: SkyTokenSelectedEventArgs) {
    console.log('Token selected:', args);
  }

  public onFocusIndexUnderRange() {
    console.log('Focus index was less than zero.');
  }

  public onFocusIndexOverRange() {
    console.log('Focus index was greater than the number of tokens.');
  }

  public focusLastToken() {
    if (!this.tokensController) {
      this.tokensController = new Subject<SkyTokensMessage>();
    }

    this.tokensController.next({
      type: SkyTokensMessageType.FocusLastToken
    });
  }

  private parseTokens(data: any[]): SkyToken[] {
    return data.map((item: any) => {
      return {
        value: item
      } as SkyToken;
    });
  }
}
`,
          componentName: 'SkyTokensDemoComponent',
          bootstrapSelector: 'sky-tokens-demo'
        }
      ]
    },
    {
      name: 'Toolbar',
      component: SkyToolbarDemoComponent,
      files: [
        {
          name: 'toolbar-demo.component.html',
          fileContents: `<sky-toolbar>
  <sky-toolbar-item>
    <button class="sky-btn sky-btn-default">Button 1</button>
  </sky-toolbar-item>
  <sky-toolbar-item>
    <button class="sky-btn sky-btn-default">Button 2</button>
  </sky-toolbar-item>
</sky-toolbar>
`
        },
        {
          name: 'toolbar-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

@Component({
  selector: 'sky-toolbar-demo',
  templateUrl: './toolbar-demo.component.html'
})
export class SkyToolbarDemoComponent { }
`,
          componentName: 'SkyToolbarDemoComponent',
          bootstrapSelector: 'sky-toolbar-demo'
        }
      ]
    },
    {
      name: 'URL validation',
      component: SkyUrlValidationDemoComponent,
      files: [
        {
          name: 'url-validation-demo.component.html',
          fileContents: `<input
  skyUrlValidation
  class="sky-form-control"
  [(ngModel)]="url"
  #input="ngModel">

<div
  class="sky-error-label"
  *ngIf="input.errors && input.errors.skyUrl && (input.dirty && input.touched)">
  <span
    [hidden]="!input.errors.skyUrl.invalid">
    Please enter valid url with format http(s)://site.domain
  </span>
</div>
`
        },
        {
          name: 'url-validation.component.ts',
          fileContents: `import { Component } from '@angular/core';

@Component({
  selector: 'sky-url-validation-demo',
  templateUrl: './url-validation-demo.component.html'
})
export class SkyUrlValidationDemoComponent {
  public url: string;
}
`,
          componentName: 'SkyUrlValidationDemoComponent',
          bootstrapSelector: 'sky-url-validation-demo'
        }
      ]
    },
    {
      name: 'Vertical tabs',
      component: SkyVerticalTabsDemoComponent,
      files: [
        {
          name: 'vertical-tabs-demo.component.html',
          fileContents: `<div style="height: 40vh">
  <sky-vertical-tabset
    showTabsText="Tab list"
    (activeChange)="tabChanged($event)">

    <sky-vertical-tabset-group
      *ngFor="let group of groups"
      [groupHeading]="group.heading"
      [open]="group.isOpen"
      [disabled]="group.isDisabled">

      <sky-vertical-tab
        *ngFor="let tab of group.subTabs"
        [active]="tab.active"
        [tabHeading]="tab.tabHeading"
        [tabHeaderCount]="tab.tabHeaderCount"
        [disabled]="tab.disabled">
        {{ tab.content }}
      </sky-vertical-tab>
    </sky-vertical-tabset-group>
  </sky-vertical-tabset>
</div>

<h3>
  Without groups
</h3>

<sky-vertical-tabset (activeChange)="tabChanged($event)">
  <sky-vertical-tab tabHeading="tab 1">
    Tab 1 content
  </sky-vertical-tab>
  <sky-vertical-tab tabHeading="tab 2" active="true">
    Tab 2 content
  </sky-vertical-tab>
  <sky-vertical-tab tabHeading="tab 3">
    Tab 3 content
  </sky-vertical-tab>
</sky-vertical-tabset>
`
        },
        {
          name: 'vertical-tabs-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

@Component({
  selector: 'sky-vertical-tabs-demo',
  templateUrl: './vertical-tabs-demo.component.html'
})
export class SkyVerticalTabsDemoComponent {
  public groups: any[];

  constructor() {
    this.groups = [
      {
        heading: 'Group 1',
        isOpen: false,
        isDisabled: false,
        subTabs: [
          { tabHeading: 'Group 1 — Tab 1', content: 'Group 1 — Tab 1 Content'},
          { tabHeading: 'Group 1 — Tab 2', content: 'Group 1 — Tab 2 Content', tabHeaderCount: 7}]
      },
      {
        heading: 'Group 2',
        isOpen: true,
        isDisabled: false,
        subTabs: [
          { tabHeading: 'Group 2 — Tab 1', content: 'Group 2 — Tab 1 Content', active: true},
          { tabHeading: 'Group 2 — Tab 2 — Disabled',
            content: 'Group 2 — Tab 2 Content',
            disabled: true
          }]
      },
      {
        heading: 'Disabled',
        isOpen: false,
        isDisabled: true,
        subTabs: []
      }
    ];
  }

  public tabChanged(newIndex: any) {
    console.log(\`new active \${newIndex}\`);
  }
}
`,
          componentName: 'SkyVerticalTabsDemoComponent',
          bootstrapSelector: 'sky-vertical-tabs-demo'
        }
      ]
    },
    {
      name: 'Wait',
      component: SkyWaitDemoComponent,
      files: [
        {
          name: 'wait-demo.component.html',
          fileContents: `<button type="button" class="sky-btn sky-btn-default" (click)="isWaiting = !isWaiting">Toggle element wait</button>


<div style="height: 200px; width: 200px; margin-top: 10px;">
  A large area that can be waited with the <code>sky-wait</code> directive.
  <sky-wait [isWaiting]="isWaiting">
  </sky-wait>
</div>

<button type="button" class="sky-btn sky-btn-default" (click)="showPageWait(true)">Show page wait</button>

<button type="button" class="sky-btn sky-btn-default" (click)="showPageWait(false)">Show non-blocking page wait</button>
`
        },
        {
          name: 'wait-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

import {
  SkyWaitService
} from '../../core';

@Component({
  selector: 'sky-wait-demo',
  templateUrl: './wait-demo.component.html'
})
export class SkyWaitDemoComponent {
  public isWaiting = false;

  constructor(private waitSvc: SkyWaitService) { }

  public showPageWait(isBlocking: boolean) {
    if (isBlocking) {
      this.waitSvc.beginBlockingPageWait();
      setTimeout(() => {
        this.waitSvc.endBlockingPageWait();
      }, 2000);
    } else {
      this.waitSvc.beginNonBlockingPageWait();
      setTimeout(() => {
        this.waitSvc.endNonBlockingPageWait();
      }, 2000);
    }
  }
}
`,
          componentName: 'SkyWaitDemoComponent',
          bootstrapSelector: 'sky-wait-demo'
        }
      ]
    },
    {
      name: 'Wizard',
      component: SkyWizardDemoComponent,
      files: [
        {
          name: 'wizard-demo.component.html',
          fileContents: `<button
  type="button"
  class="sky-btn sky-btn-default"
  (click)="openWizard()">
  Open wizard
</button>
`
        },
        {
          name: 'wizard-demo.component.ts',
          fileContents: `import { Component } from '@angular/core';

import { SkyModalService } from '../../core';

import { SkyWizardDemoFormComponent } from './wizard-demo-form.component';

@Component({
  selector: 'sky-wizard-demo',
  templateUrl: './wizard-demo.component.html'
})
export class SkyWizardDemoComponent {
  constructor(private modal: SkyModalService) { }

  public openWizard() {
    this.modal.open(SkyWizardDemoFormComponent);
  }
}
`,
          componentName: 'SkyWizardDemoComponent',
          bootstrapSelector: 'sky-wizard-demo'
        },
        {
          name: 'wizard-demo-form.component.html',
          fileContents: `<sky-modal>
  <sky-modal-header>
    {{title}}
  </sky-modal-header>
  <sky-modal-content>
    <sky-tabset tabStyle="wizard" #wizardDemo>
      <sky-tab
        tabHeading="Step 1"
        active="true">
        <label>
          Enter text to continue:
          <input class="sky-form-control" type="text" [(ngModel)]="requiredValue1" />
        </label>
      </sky-tab>
      <sky-tab
        tabHeading="Step 2"
        [disabled]="step2Disabled">
        <sky-checkbox [(ngModel)]="requiredValue2">
          <sky-checkbox-label>
            Select this checkbox to enable step 3.
          </sky-checkbox-label>
        </sky-checkbox>
      </sky-tab>
      <sky-tab
        tabHeading="Step 3"
        [disabled]="step3Disabled">
        Some other content.
      </sky-tab>
    </sky-tabset>
  </sky-modal-content>
  <sky-modal-footer>
    <sky-tabset-nav-button buttonType="previous" [tabset]="wizardDemo"></sky-tabset-nav-button>
    <sky-tabset-nav-button buttonType="next" [tabset]="wizardDemo"></sky-tabset-nav-button>
    <button
      type="button"
      class="sky-btn sky-btn-default"
      (click)="instance.close()">
      Save and close
    </button>
    <button
      type="button"
      class="sky-btn sky-btn-link"
      (click)="instance.cancel()">
      Cancel
    </button>
  </sky-modal-footer>
</sky-modal>
`
        },
        {
          name: 'wizard-demo-form.component.ts',
          fileContents: `import {
  ChangeDetectionStrategy,
  Component
} from '@angular/core';

import { SkyModalInstance } from '../../core';

@Component({
  selector: 'sky-demo-wizard-form',
  templateUrl: './wizard-demo-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SkyWizardDemoFormComponent {
  public title = 'Wizard example';
  public requiredValue1: string;
  public requiredValue2: boolean;

  public get step2Disabled(): boolean {
    return !this.requiredValue1;
  }

  public get step3Disabled(): boolean {
    return this.step2Disabled || !this.requiredValue2;
  }

  constructor(
    public instance: SkyModalInstance
  ) { }

  public validateStep1() {
    return true;
  }
}
`,
          componentName: 'SkyWizardDemoFormComponent'
        }
      ]
    }
  ];

  public getComponent(name: string): any {
    const found = this.components.find((component: any) => {
      return component.component.name === name;
    });

    return found;
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
